<template>
<div class="ai-analysis-panel">
  <!-- Main Model Selection Tabs -->
  <div class="model-selection-tabs">
    <div class="model-tab-header">
      <button
        v-for="model in availableModels"
        :key="model.id"
        class="model-tab-button"
        :class="{ active: activeModel === model.id }"
        @click="switchModel(model.id)"
        :disabled="isLoadingProstateAnnotations || isLoadingAnnotations"
      >
        <i :class="model.icon"></i>
        {{ model.name }}
      </button>
    </div>
  </div>

  <!-- Blood Film Analysis Panel -->
  <div v-if="activeModel === 'blood-film'" class="blood-film-analysis-panel">
    <!-- Analysis Header -->
    <div class="analysis-header">
      <div class="header-top">
        <div class="header-left">
          <h3 class="panel-title">
            Peripheral Blood Film Analysis
          </h3>
          <div class="analysis-info">
            <div class="info-item">
              <span class="label" style="color: white;">Analysis Status</span>
              <span class="value status" style="color: white; " :class="analysisSession.status">
                {{ formatStatus(analysisSession.status) }}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Main Content Area -->
    <div class="analysis-content">
      <!-- Analysis Type Tabs -->
      <div class="analysis-tabs">
        <div class="tab-header">
          <button
            v-for="tab in analysisTabs"
            :key="tab.id"
            class="tab-button"
            :class="{ active: activeTab === tab.id }"
            @click="switchTab(tab.id)"
            :disabled="isLoadingAnnotations || tabLoadingStates.wbc || tabLoadingStates.rbc || tabLoadingStates.plt"
          >
            {{ tab.name }}
          </button>
        </div>

        <div class="tab-content">
          <!-- Annotation List Tab -->
          <div v-if="activeTab === 'annotations'" class="annotations-analysis-tab">
            <div class="annotation-filters">
              <div class="filter-group">
                <label>Filter by Term:</label>
                <select v-model="selectedAnnotationTerm" @change="filterAnnotations" class="term-filter-select">
                  <option value="">Select Annotation Terms</option>
                  <option v-for="term in availableAnnotationTerms" :key="term.id" :value="term.id">
                    {{ term.name }}
                  </option>
                </select>
              </div>
              <div class="filter-actions">
                <button
                  @click="loadAllAnnotations"
                  :disabled="isLoadingAnnotations"
                  class="load-all-btn"
                >
                  <i class="fas fa-download"></i>
                  {{ isLoadingAnnotations ? 'Loading...' : 'Load All Annotations' }}
                </button>
              </div>
            </div>

            <div class="annotation-filters">
              <div class="annotation-stats">
                <span>Total: {{ filteredAnnotations.length }} annotations</span>
              </div>
            </div>

            <div class="annotation-list">
              <div v-if="isLoadingAnnotations" class="loading-indicator">
                <div class="spinner"></div>
                <span>Loading annotations...</span>
              </div>

              <div v-else-if="filteredAnnotations.length === 0" class="no-annotations">
                <p>No annotations found.</p>
              </div>

              <div v-else class="annotation-items">
                <div
                  v-for="annotation in filteredAnnotations"
                  :key="annotation.id"
                  class="annotation-item clickable"
                  @click="goToAnnotation(annotation)"
                >
                  <div class="annotation-preview-container">
                    <!-- Thumbnail preview -->
                    <div
                      class="annotation-thumbnail"
                      :style="getAnnotationThumbnailStyle(annotation)"
                    >
                      <button class="preview-button" @click.stop="toggleAnnotationDetails(annotation.id)">
                        <i :class="['fas', expandedAnnotations[annotation.id] ? 'fa-minus' : 'fa-plus']"></i>
                      </button>
                    </div>

                    <!-- Annotation info -->
                    <div class="annotation-info">
                      <div class="annotation-header">
                        <span class="annotation-id">#{{ annotation.id }}</span>
                        <span class="annotation-term">{{ getAnnotationTermName(annotation) }}</span>
                      </div>

                      <!-- Expandable details -->
                      <div v-if="expandedAnnotations[annotation.id]" class="annotation-details">
                        <div class="annotation-location">
                          {{ getFormattedLocation(annotation) }}
                        </div>
                        <div class="annotation-meta">
                          <span class="annotation-user">{{ annotation.user?.username || 'Unknown' }}</span>
                          <span class="annotation-date">{{ formatAnnotationDate(annotation.created) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- WBC Analysis Tab -->
          <div v-if="activeTab === 'wbc'" class="wbc-analysis-tab">
            <!-- Loading indicator for WBC tab -->
            <div v-if="tabLoadingStates.wbc" class="tab-loading-indicator">
              <div class="loading-spinner"></div>
              <span>Loading WBC data...</span>
            </div>

            <div v-else class="wbc-simple-layout">
              <!-- Cell Type Counts Display -->
              <div class="wbc-counts-container">
                <div class="wbc-header">
                  <div class="wbc-header-top">
                    <h3>White Blood Cell Differential Count</h3>
                    <button
                      class="refresh-btn-professional"
                      :class="{ loading: tabLoadingStates.wbc }"
                      @click="refreshWBCData"
                      :disabled="tabLoadingStates.wbc"
                      title="Refresh WBC count data"
                    >
                      <svg class="refresh-icon" viewBox="0 0 24 24" width="16" height="16">
                        <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                      </svg>
                      Refresh Data
                    </button>
                  </div>
                </div>

                <div class="total-count-display">
                  <span class="total-label">Total WBC Counted:</span>
                  <span class="total-value">{{ totalWBCCounted }}</span>
                </div>

                <!-- Cell Type List - Table Format -->
                <div class="wbc-table-container">
                  <div class="wbc-table">
                    <div class="table-header">
                      <div class="cell-type-col">Cell Type</div>
                      <div class="percentage-col">%</div>
                    </div>

                    <div class="table-body">
                      <!-- No data state -->
                      <div v-if="sortedWBCTerms.length === 0" class="no-data-message">
                        <p>No WBC data available yet. Click "Refresh Data" to load annotations.</p>
                      </div>

                      <!-- Sorted WBC Types (highest percentage first, platelet-thrombocyte hidden) -->
                      <div
                        v-for="termItem in sortedWBCTerms"
                        :key="termItem.name"
                        class="wbc-term-group"
                      >
                        <!-- Term row -->
                        <div class="wbc-row">
                          <div class="cell-type">
                            {{ termItem.name }}
                            <span class="count">({{ termItem.count }})</span>
                            <span v-if="!termItem.isSupported" class="asterisk">*</span>
                          </div>
                          <div class="percentage">
                            {{ termItem.percentageText }}%
                          </div>
                        </div>
                      </div>

                      <!-- Show message if no results -->
                      <div v-if="sortedWBCTerms.length === 0" class="no-wbc-data">
                        <div class="wbc-row">
                          <div class="cell-type">No cell types detected. Click Refresh to load annotation data.</div>
                          <div class="percentage">-</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Analysis Summary -->
                <div class="analysis-summary">
                  <div class="summary-stats">
                    <div class="stat-item">
                      <span class="stat-label">Different Cell Types Found:</span>
                      <span class="stat-value">{{ uniqueCellTypesCount }}</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">Classified Cells:</span>
                      <span class="stat-value">{{ classifiedCellsCount }}/{{ totalWBCCounted }}</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">Classification Completeness:</span>
                      <span class="stat-value">{{ classificationCompleteness }}%</span>
                    </div>
                  </div>

                  <!-- Reference Ranges (if needed) -->

                </div>
              </div>
            </div>
          </div>

          <!-- RBC Analysis Tab -->
          <div v-if="activeTab === 'rbc'" class="rbc-analysis-tab">
            <!-- Loading indicator for RBC tab -->
            <div v-if="tabLoadingStates.rbc" class="tab-loading-indicator">
              <div class="loading-spinner"></div>
              <span>Loading RBC data...</span>
            </div>

            <div v-else class="rbc-simple-layout">
              <!-- RBC Morphology Analysis -->
              <div class="rbc-morphology-container">
                <div class="rbc-header">
                  <div class="header-top">
                    <h3>Red Blood Cell Morphology Analysis</h3>
                    <button
                      class="auto-calc-btn"
                      @click="autoCalculateMorphologyScores"
                      :disabled="isAutoCalculating"
                      :title="isAutoCalculating ? 'Auto-calculation in progress...' : 'Auto-calculate scores based on RBC term annotations'"
                    >
                      <span v-if="isAutoCalculating" class="loading-indicator">
                        <span class="spinner"></span>
                        Auto-Calculating...
                      </span>
                      <span v-else>Auto-Calculate Scores</span>
                    </button>
                  </div>
                  <div class="analysis-description">
                    <p>Assess RBC morphology abnormalities using the standardized grading system. Select appropriate score (1+/2+/3+) for each morphology type observed.</p>
                    <button
                      class="refresh-btn-professional"
                      :class="{ loading: tabLoadingStates.rbc }"
                      @click="refreshRBCData"
                      :disabled="tabLoadingStates.rbc"
                      title="Refresh RBC annotation data"
                      style="margin-top: 10px;"
                    >
                      <svg class="refresh-icon" viewBox="0 0 24 24" width="16" height="16">
                        <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                      </svg>
                      Refresh RBC Data
                    </button>
                  </div>
                </div>

                <!-- Morphology Scoring Table -->
                <div class="morphology-table-container">
                  <div v-for="category in rbcMorphologyCategories" :key="category.name" class="morphology-category-table">
                    <h4 class="table-category-header">{{ category.name }}</h4>

                    <div class="morphology-table">
                      <div class="table-header">
                        <div class="header-cell morphology-name">Cell Type</div>
                        <div class="header-cell score-column">0/1+2+3+</div>
                        <div class="header-cell score-column">1+</div>
                        <div class="header-cell score-column">2+</div>
                        <div class="header-cell score-column">3+</div>
                      </div>

                      <div
                        v-for="item in category.items"
                        :key="item.name"
                        class="table-row"
                        :class="{ 'scored-row': item.score }"
                      >
                        <!-- Cell Type Name -->
                        <div class="table-cell morphology-name">
                          <span class="morphology-title">{{ item.displayName }}</span>
                          <!-- Show count for normal RBCs -->
                          <span v-if="item.isNormal" class="normal-count">
                            ({{ getRBCCellCount(item.name) }} cells)
                          </span>
                        </div>

                        <!-- For Normal RBCs: Show count instead of scoring -->
                        <div v-if="item.isNormal" class="table-cell normal-info" colspan="4">
                          <span class="normal-status">✓ Normal - No scoring required</span>
                        </div>

                        <!-- For Abnormal RBCs: Show scoring radio buttons -->
                        <div v-else class="table-cell score-column radio-group">
                          <label class="score-radio-container">
                            <input
                              type="radio"
                              :name="`morphology-${item.name}`"
                              :value="1"
                              v-model="item.score"
                              @change="updateRBCMorphologyScore(item.name, 1)"
                            />
                            <span class="radio-label">1+</span>
                          </label>
                          <label class="score-radio-container">
                            <input
                              type="radio"
                              :name="`morphology-${item.name}`"
                              :value="2"
                              v-model="item.score"
                              @change="updateRBCMorphologyScore(item.name, 2)"
                            />
                            <span class="radio-label">2+</span>
                          </label>
                          <label class="score-radio-container">
                            <input
                              type="radio"
                              :name="`morphology-${item.name}`"
                              :value="3"
                              v-model="item.score"
                              @change="updateRBCMorphologyScore(item.name, 3)"
                            />
                            <span class="radio-label">3+</span>
                          </label>
                        </div>

                        <!-- Threshold columns only for abnormal RBCs -->
                        <template v-if="!item.isNormal">
                          <!-- 1+ Threshold Range Display -->
                          <div class="table-cell threshold-column">
                            <div class="threshold-range">{{ getReferenceRange(item, 1) }}</div>
                          </div>

                          <!-- 2+ Threshold Range Display -->
                          <div class="table-cell threshold-column">
                            <div class="threshold-range">{{ getReferenceRange(item, 2) }}</div>
                          </div>

                          <!-- 3+ Threshold Range Display -->
                          <div class="table-cell threshold-column">
                            <div class="threshold-range">{{ getReferenceRange(item, 3) }}</div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- RBC Count Display -->
                <div class="rbc-count-display">
                  <div class="count-header">
                    <h4>RBC Count</h4>
                  </div>
                  <div class="count-value">
                    <span class="count-number">{{ totalRBCCount }}</span>
                    <span class="count-label">RBC in current region</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- PLT Analysis Tab -->
          <div v-if="activeTab === 'plt'" class="plt-analysis-tab">
            <!-- Loading indicator for PLT tab -->
            <div v-if="tabLoadingStates.plt" class="tab-loading-indicator">
              <div class="loading-spinner"></div>
              <span>Loading PLT data...</span>
            </div>

            <div v-else class="plt-content">
              <div class="plt-header">
                <h3 class="section-title">PLT Estimate</h3>
                <button
                  class="refresh-btn-professional"
                  :class="{ loading: tabLoadingStates.plt }"
                  @click="refreshPLTData"
                  :disabled="tabLoadingStates.plt"
                  title="Refresh PLT count data"
                >
                  <svg class="refresh-icon" viewBox="0 0 24 24" width="16" height="16">
                    <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                  </svg>
                  Refresh Data
                </button>
              </div>

              <!-- Simple PLT Calculation Display -->
              <div class="plt-calculation">
                <div class="calc-section">
                  <label class="calc-label">
                    Average PLT<br>Counted/Field
                    <span class="data-source-indicator" :class="pltData.source">
                      {{ pltData.source === 'auto' ? '(Auto)' : '(Manual)' }}
                    </span>
                  </label>
                  <input
                    type="number"
                    :value="currentPLTAverageCount"
                    @input="updatePLTCount($event.target.value)"
                    class="plt-input"
                    min="0"
                    step="1"
                    :placeholder="`Auto: ${autoCalculatedPLTCount}`"
                  />
                </div>

                <div class="calc-section operator">
                  <span class="operator-symbol">×</span>
                </div>

                <div class="calc-section">
                  <label class="calc-label">Estimation<br>Factor</label>
                  <div class="factor-display">10</div>
                </div>

                <div class="calc-section operator">
                  <span class="operator-symbol">=</span>
                </div>

                <div class="calc-section result">
                  <label class="calc-label">PLT Estimate</label>
                  <div class="result-value">{{ currentPLTAverageCount * 10 }}×10⁹/L</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Report Tab -->
          <div v-if="activeTab === 'report'" class="report-analysis-tab">
            <div class="report-content">
              <h3 class="report-title">Report</h3>

              <!-- Image Information -->
              <div class="image-info">
                <div class="image-details">
                  <span class="image-label">Image ID:</span>
                  <span class="image-value">{{ image?.id || 'N/A' }}</span>
                </div>
                <div class="image-details">
                  <span class="image-label">Filename:</span>
                  <span class="image-value">{{ image?.originalFilename || image?.filename || 'N/A' }}</span>
                </div>
                <div class="image-details">
                  <span class="image-label">User:</span>
                  <span class="image-value">{{ currentUser?.name || currentUser?.username || 'N/A' }}</span>
                </div>
              </div>

              <!-- WBC Section -->
              <div class="report-section">
                <div class="report-table">
                  <div class="table-header">
                    <div class="cell-type-col">Cell Type</div>
                    <div class="percentage-col">%</div>
                  </div>

                  <div class="table-body">
                    <!-- Sorted WBC Types (highest percentage first, platelet-thrombocyte hidden) -->
                    <div
                      v-for="termItem in sortedWBCTerms"
                      :key="termItem.name"
                      class="wbc-term-group"
                    >
                      <!-- Term row -->
                      <div class="wbc-row">
                        <div class="cell-type">
                          {{ termItem.name }}
                          <span class="count">({{ termItem.count }})</span>
                          <span v-if="!termItem.isSupported" class="asterisk">*</span>
                        </div>
                        <div class="percentage">
                          {{ termItem.percentageText }}%
                        </div>
                      </div>
                    </div>

                    <!-- Show message if no results -->
                    <div v-if="sortedWBCTerms.length === 0" class="no-wbc-data">
                      <div class="wbc-row">
                        <div class="cell-type">No WBC differential data available</div>
                        <div class="percentage">-</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- WBC Summary -->
                <div class="report-summary">
                  <div class="summary-item">
                    <strong>Total WBC counted: {{ totalWBCCounted || 0 }}</strong>
                  </div>
                  <div class="summary-note">
                    Cell types that are not currently supported by AI preclassification are denoted with asterisks (*)
                  </div>
                </div>
              </div>

              <!-- RBC Section -->
              <div class="report-section rbc-section">
                <h3 class="section-title">RBC Morphology</h3>

                <!-- RBC Morphology Results -->
                <div v-if="hasRBCMorphologyData" class="rbc-morphology-results">
                  <div
                    v-for="(scoreData, morphologyName) in rbcMorphologyScores"
                    :key="morphologyName"
                    v-if="scoreData && scoreData.score && scoreData.score !== '0'"
                    class="rbc-morphology-item"
                  >
                    <div class="morphology-name">
                      {{ getRBCDisplayName(morphologyName) }}
                    </div>
                    <div class="morphology-score">
                      {{ scoreData.score }}
                    </div>
                    <div class="morphology-percentage" v-if="scoreData.percentage">
                      ({{ scoreData.percentage }}%)
                    </div>
                  </div>
                </div>

                <!-- No RBC data message -->
                <div v-if="!hasRBCMorphologyData" class="no-rbc-data">
                  <div class="morphology-item">
                    <div class="morphology-name">No significant RBC morphological abnormalities detected</div>
                  </div>
                </div>

                <!-- RBC Summary -->
                <div class="rbc-summary" v-if="hasRBCMorphologyData">
                  <div class="summary-note">
                    RBC morphology grading: 1+ (mild), 2+ (moderate), 3+ (severe)
                  </div>
                </div>
              </div>

              <!-- PLT Section -->
              <div class="report-section plt-section">
                <h3 class="section-title">PLT</h3>
                <div class="plt-estimate">
                  PLT Estimate: {{ currentPLTAverageCount * 10 }}×10⁹/L
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Navigation -->
    <div class="bottom-navigation">
      <div class="nav-buttons">
        <button class="nav-btn secondary" :disabled="isFirstStep">
          Previous
        </button>
        <button class="nav-btn primary" @click="nextStep">
          {{ isLastStep ? 'Complete Full Analysis' : 'Next Analysis Step' }}
        </button>
      </div>
    </div>

    <!-- AI-Assisted Note Section (Report Tab Only) -->
    <div v-if="activeTab === 'report'" class="ai-assisted-section">
      <div class="ai-note-cards">
        <!-- Error/Rule Comments Card -->
        <div class="ai-note-card">
          <div class="ai-card-header" style="display: flex; justify-content: space-between; align-items: center;">
            <span class="ai-card-label" style="font-weight: bold; color: #000">Error/Rule Comments</span>
            <button
              v-if="!editingAIComments.errorRule"
              class="add-comment-btn"
              @click="startEditingAIComment('errorRule')"
            >
              {{ aiComments.errorRule ? 'Edit Note' : 'Add Note' }}
            </button>
          </div>

          <!-- Display existing comment if present and not editing -->
          <div v-if="aiComments.errorRule && !editingAIComments.errorRule" class="existing-comment">
            <div class="comment-content">{{ aiComments.errorRule }}</div>
            <div class="comment-meta">
              <span class="comment-date">{{ getAICommentDate('errorRule') }}</span>
            </div>
          </div>

          <!-- Inline comment editor -->
          <div v-if="editingAIComments.errorRule" class="comment-editor">
            <textarea
              v-model="currentAICommentText"
              class="comment-textarea-inline"
              rows="4"
              placeholder="Enter AI-assisted error/rule comments..."
            ></textarea>

            <div class="comment-meta-info-inline">
              <div class="user-info">
                <span class="user-name">{{ currentUser?.name || 'Current User' }}</span>
                <span class="time-value">{{ new Date().toLocaleString() }}</span>
              </div>

              <div class="comment-actions">
                <button class="btn-secondary-small" @click="cancelEditAIComment('errorRule')">Cancel</button>
                <button class="btn-primary-small" @click="saveAICommentInline('errorRule')">Save</button>
                <button
                  v-if="aiComments.errorRule"
                  class="btn-danger-small"
                  @click="deleteAICommentInline('errorRule')"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>

          <div class="ai-diagnosis">
            <span class="ai-label">AI-Assisted Error/Rule Comments:</span>
            <div class="ai-content">
              <!-- AI suggestions will be displayed here -->
            </div>
          </div>
        </div>

        <!-- Action Messages Card -->
        <div class="ai-note-card">
          <div class="ai-card-header" style="display: flex; justify-content: space-between; align-items: center;">
            <span class="ai-card-label" style="font-weight: bold; color: #000">Action Messages:</span>
            <button
              v-if="!editingAIComments.actionMessages"
              class="add-comment-btn"
              @click="startEditingAIComment('actionMessages')"
            >
              {{ aiComments.actionMessages ? 'Edit Message' : 'Add Message' }}
            </button>
          </div>

          <!-- Display existing comment if present and not editing -->
          <div v-if="aiComments.actionMessages && !editingAIComments.actionMessages" class="existing-comment">
            <div class="comment-content">{{ aiComments.actionMessages }}</div>
            <div class="comment-meta">
              <span class="comment-date">{{ getAICommentDate('actionMessages') }}</span>
            </div>
          </div>

          <!-- Inline comment editor -->
          <div v-if="editingAIComments.actionMessages" class="comment-editor">
            <textarea
              v-model="currentAICommentText"
              class="comment-textarea-inline"
              rows="4"
              placeholder="Enter AI-assisted action messages..."
            ></textarea>

            <div class="comment-meta-info-inline">
              <div class="user-info">
                <span class="user-name">{{ currentUser?.name || 'Current User' }}</span>
                <span class="time-value">{{ new Date().toLocaleString() }}</span>
              </div>

              <div class="comment-actions">
                <button class="btn-secondary-small" @click="cancelEditAIComment('actionMessages')">Cancel</button>
                <button class="btn-primary-small" @click="saveAICommentInline('actionMessages')">Save</button>
                <button
                  v-if="aiComments.actionMessages"
                  class="btn-danger-small"
                  @click="deleteAICommentInline('actionMessages')"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>

          <div class="ai-diagnosis">
            <span class="ai-label">AI-Assisted Action Messages:</span>
            <div class="ai-content">
              <!-- AI suggestions will be displayed here -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Comments Section -->
    <div class="comments-section">
      <h3>Notes</h3>
      <div class="comment-groups">
        <!-- Show only current tab comment for WBC, RBC, PLT tabs (exclude annotations tab) -->
        <div v-if="activeTab !== 'report' && activeTab !== 'annotations'" class="comment-group" :key="`comment-${activeTab}`">
          <div class="comment-header">
            <span class="comment-label">{{ getTabDisplayName(activeTab) }}</span>
            <button
              v-if="!editingComments[activeTab]"
              class="add-comment-btn"
              @click="startEditingComment(activeTab)"
            >
              {{ tabComments[activeTab] ? 'Edit Note' : 'Add Note' }}
            </button>
          </div>

          <!-- Display existing comment if present and not editing -->
          <div v-if="tabComments[activeTab] && !editingComments[activeTab]" class="existing-comment">
            <div class="comment-content">{{ tabComments[activeTab] }}</div>
            <div class="comment-meta">
              <span class="comment-date">{{ getCommentDate(activeTab) }}</span>
            </div>
          </div>

          <!-- Inline comment editor -->
          <div v-if="editingComments[activeTab]" class="comment-editor">
            <textarea
              v-model="currentCommentText"
              class="comment-textarea-inline"
              rows="4"
              :placeholder="`Enter your observations and comments for ${getTabDisplayName(activeTab)}...`"
            ></textarea>

            <div class="comment-meta-info-inline">
              <div class="user-info">
                <span class="user-name">{{ currentUser?.name || 'Current User' }}</span>
                <span class="time-value">{{ new Date().toLocaleString() }}</span>
              </div>

              <div class="comment-actions">
                <button class="btn-secondary-small" @click="cancelEditComment(activeTab)">Cancel</button>
                <button class="btn-primary-small" @click="saveCommentInline(activeTab)">Save</button>
                <button
                  v-if="tabComments[activeTab]"
                  class="btn-danger-small"
                  @click="deleteCommentInline(activeTab)"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
          <div class="ai-diagnosis">
            <span class="ai-label">AI-Assisted Note:</span>
            <div class="ai-content">
              <!-- AI suggestions will be displayed here -->
            </div>
          </div>
        </div>

        <!-- Show all comments for Report tab -->
        <div v-if="activeTab === 'report'" class="comment-group" v-for="tab in analysisTabs" :key="`comment-${tab.id}`">
          <div class="comment-header">
            <span class="comment-label">{{ tab.name }}</span>
            <button
              v-if="!editingComments[tab.id]"
              class="add-comment-btn"
              @click="startEditingComment(tab.id)"
            >
              {{ tabComments[tab.id] ? 'Edit Note' : 'Add Note' }}
            </button>
          </div>

          <!-- Display existing comment if present and not editing -->
          <div v-if="tabComments[tab.id] && !editingComments[tab.id]" class="existing-comment">
            <div class="comment-content">{{ tabComments[tab.id] }}</div>
            <div class="comment-meta">
              <span class="comment-date">{{ getCommentDate(tab.id) }}</span>
            </div>
          </div>

          <!-- Inline comment editor -->
          <div v-if="editingComments[tab.id]" class="comment-editor">
            <textarea
              v-model="currentCommentText"
              class="comment-textarea-inline"
              rows="4"
              :placeholder="`Enter your observations and comments for ${tab.name}...`"
            ></textarea>

            <div class="comment-meta-info-inline">
              <div class="user-info">
                <span class="user-name">{{ currentUser?.name || 'Current User' }}</span>
                <span class="time-value">{{ new Date().toLocaleString() }}</span>
              </div>

              <div class="comment-actions">
                <button class="btn-secondary-small" @click="cancelEditComment(tab.id)">Cancel</button>
                <button class="btn-primary-small" @click="saveCommentInline(tab.id)">Save</button>
                <button
                  v-if="tabComments[tab.id]"
                  class="btn-danger-small"
                  @click="deleteCommentInline(tab.id)"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
          <div class="ai-diagnosis">
            <span class="ai-label">AI-Assisted Note:</span>
            <div class="ai-content">
              <!-- AI suggestions will be displayed here -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cell Classification Modal -->
    <CellClassificationModal
      :is-open="classificationModal.isOpen"
      :selected-cell="classificationModal.selectedCell"
      :classification-options="classificationOptions"
      @close="closeClassificationModal"
      @classify-cell="handleCellClassification"
    />

    <!-- Blood Film Annotation Details Playground -->
    <div class="blood-film-annotation-details-playground" v-if="currentSelectedAnnotation">
      <vue-draggable-resizable
        class="draggable blood-film-draggable"
        :parent="true"
        :resizable="false"
        drag-handle=".drag"
        :w="400" h='auto' :x="50" :y="50"
      >
        <div class="actions">
          <!-- <h1>Current Selection - Blood Film</h1> -->
          <button class="drag button is-small close">
            <i class="fas fa-arrows-alt"></i>
          </button>
          <button class="button is-small close" @click="clearSelection">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="blood-film-annotation-details-container">
          <div class="selected-preview">
            <div
              class="selected-thumbnail"
              :style="getAnnotationThumbnailStyle(currentSelectedAnnotation)"
            ></div>
            <div class="selected-info">
              <div class="selected-id">#{{ currentSelectedAnnotation.id }}</div>
              <div class="selected-term">{{ getAnnotationTermName(currentSelectedAnnotation) }}</div>
              <div class="selected-location">
                {{ getFormattedLocation(currentSelectedAnnotation) }}
              </div>
            </div>
          </div>

          <!-- Terms Section -->
          <div class="terms-section">
            <h5>Classification Terms</h5>

            <!-- Current Terms Display -->
            <div v-if="currentSelectedAnnotation.terms && currentSelectedAnnotation.terms.length" class="current-terms">
              <div class="current-terms-label">Current:</div>
              <div class="terms-list">
                <div
                  v-for="term in currentSelectedAnnotation.terms"
                  :key="term.id"
                  class="term-item"
                >
                  <span class="term-name">{{ getTermDisplayName(term.id) }}</span>
                  <span class="term-confidence" v-if="term.rate">{{ Math.round(term.rate * 100) }}%</span>
                </div>
              </div>
            </div>

            <!-- Term Reclassification Select -->
            <div class="term-reclassification">
              <label class="reclassification-label">Change Classification:</label>
              <div class="select-container">
                <select
                  v-model="selectedNewTerm"
                  @change="onTermSelectionChange"
                  class="term-reclassification-select"
                  :disabled="isUpdatingAnnotation"
                >
                  <option value="">-- Select New Term --</option>
                  <optgroup
                    v-for="category in termCategories"
                    :key="category.name"
                    :label="category.name"
                  >
                    <option
                      v-for="term in category.terms"
                      :key="term.id"
                      :value="term.id"
                      :disabled="isCurrentTerm(term.id)"
                    >
                      {{ term.name }}{{ isCurrentTerm(term.id) ? ' (current)' : '' }}
                    </option>
                  </optgroup>
                </select>
                <div v-if="isUpdatingAnnotation" class="updating-indicator">
                  <i class="fas fa-spinner fa-spin"></i>
                  Updating...
                </div>
              </div>
            </div>
          </div>
        </div>
      </vue-draggable-resizable>
    </div>

  </div>

  <!-- Prostate Gleason Analysis Panel -->
  <div v-if="activeModel === 'prostate-gleason'" class="prostate-gleason-analysis-panel">
    <div class="analysis-header">
      <div class="header-top">
        <div class="header-left">
          <h3 class="panel-title">
            Prostate Gleason Scoring Analysis
          </h3>
          <div class="analysis-info">
            <div class="info-item">
              <span class="label" style="color: white;">Analysis Status</span>
              <span class="value status" style="color: white;" :class="analysisSession.status">
                {{ formatStatus(analysisSession.status) }}
              </span>
            </div>
            <div class="info-item" v-if="prostateData.gleasonScore">
              <span class="label" style="color: white;">Gleason Score</span>
              <span class="value" style="color: white; font-weight: bold;">
                {{ prostateData.gleasonScore }}
                <span v-if="prostateData.gradeGroup" class="grade-group-badge">
                  ({{ prostateData.gradeGroup }})
                </span>
              </span>
            </div>
          </div>
        </div>
        <div class="header-actions">
          <!-- <button class="btn-primary" @click="initializeProstateAnalysis">
            <i class="fas fa-sync-alt"></i>
            Initialise Analysis
          </button> -->
        </div>
      </div>
    </div>

    <!-- Prostate Analysis Content -->
    <div class="prostate-content">
      <!-- Analysis Type Tabs -->
      <div class="analysis-tabs">
        <div class="tab-header">
          <button
            v-for="tab in prostateTabs"
            :key="tab.id"
            class="tab-button"
            :class="{ active: activeProstateTab === tab.id }"
            @click="switchProstateTab(tab.id)"
            :disabled="isLoadingProstateAnnotations"
          >
            {{ tab.name }}
          </button>
        </div>

        <div class="tab-content">
          <!-- Annotation List Tab -->
          <div v-if="activeProstateTab === 'annotations'" class="annotations-analysis-tab">
            <div class="annotation-filters">
              <div class="filter-group">
                <label>Filter by Pattern:</label>
                <select v-model="selectedProstateAnnotationTerm" @change="filterProstateAnnotations" class="term-filter-select">
                  <option value="">All Patterns</option>
                  <option value="gleason3">Gleason 3</option>
                  <option value="gleason4">Gleason 4</option>
                  <option value="gleason5">Gleason 5</option>
                  <option value="stroma">Stroma</option>
                  <option value="benign">Benign</option>
                </select>
              </div>
              <div class="filter-actions">
                <button
                  @click="loadAllProstateAnnotations"
                  :disabled="isLoadingProstateAnnotations"
                  class="load-all-btn"
                  :class="{ 'loading': isLoadingProstateAnnotations }"
                >
                  <i class="fas fa-download"></i>
                  {{ isLoadingProstateAnnotations ? 'Loading...' : 'Load All Annotations' }}
                </button>
              </div>
            </div>

            <div class="annotation-filters">
              <div class="annotation-stats">
                <span>Total: {{ allProstateAnnotations.length }} annotations</span>
                <span class="pattern-breakdown" v-if="prostateData.annotationCounts">
                  (G3: {{ prostateData.annotationCounts.gleason3 || 0 }},
                  G4: {{ prostateData.annotationCounts.gleason4 || 0 }},
                  G5: {{ prostateData.annotationCounts.gleason5 || 0 }})
                </span>
              </div>
            </div>

            <div class="annotation-list">
              <div v-if="isLoadingProstateAnnotations" class="loading-indicator">
                <div class="spinner"></div>
                <span>Loading prostate annotations...</span>
              </div>

              <div v-else-if="allProstateAnnotations.length === 0" class="no-annotations">
                <p>No prostate annotations found.</p>
                <p class="help-text">Ensure annotations are created with Gleason 3, 4, 5, stroma, or benign terms.</p>
              </div>

              <div v-else class="annotation-items">
                <div
                  v-for="annotation in filteredProstateAnnotations"
                  :key="annotation.id"
                  class="annotation-item clickable"
                  @click="goToProstateAnnotation(annotation)"
                >
                  <div class="annotation-preview-container">
                    <!-- Thumbnail preview -->
                    <div
                      class="annotation-thumbnail"
                      :style="getProstateAnnotationThumbnailStyle(annotation)"
                    >
                      <div class="thumbnail-fallback" v-if="!annotation.hasThumbnail">
                      </div>
                      <button class="preview-button" @click.stop="toggleProstateAnnotationDetails(annotation.id)">
                        <i :class="['fas', expandedProstateAnnotations[annotation.id] ? 'fa-minus' : 'fa-plus']"></i>
                      </button>
                    </div>

                    <!-- Annotation info -->
                    <div class="annotation-info">
                      <div class="annotation-header">
                        <span class="annotation-id">#{{ annotation.id }}</span>
                        <span class="annotation-term" :class="getGleasonPatternClass(annotation)">
                          {{ getProstateAnnotationTermName(annotation) }}
                        </span>
                      </div>

                      <!-- Expandable details -->
                      <div v-if="expandedProstateAnnotations[annotation.id]" class="annotation-details">
                        <div class="annotation-location">
                          {{ getFormattedProstateLocation(annotation) }}
                        </div>
                        <div class="annotation-area">
                          Area: {{ getAnnotationArea(annotation) }} mm²
                        </div>
                        <div class="annotation-meta">
                          <span class="annotation-user">{{ annotation.user?.username || 'Unknown' }}</span>
                          <span class="annotation-date">{{ formatAnnotationDate(annotation.created) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Area Calculation Tab -->
          <div v-if="activeProstateTab === 'areas'" class="areas-analysis-tab">
            <div class="areas-content">
              <div class="areas-header">
                <h3>Geometric Area Calculations</h3>
                <div class="header-actions">
                  <button
                    class="refresh-btn-professional"
                    @click="calculateProstateAreas"
                    :disabled="isCalculatingAreas || allProstateAnnotations.length === 0"
                  >
                    {{ isCalculatingAreas ? 'Calculating...' : 'Calculate Areas' }}
                  </button>
                </div>
                <div class="header-actions">
                  <button
                    class="btn-secondary"
                    @click="autoCalculateGleasonScore"
                    :disabled="!prostateData.totalMalignantArea || prostateData.totalMalignantArea === 0"
                  >
                    Auto-Score
                  </button>
                </div>
              </div>

              <!-- Pixel Size Configuration -->
              <div class="pixel-config-section">
                <h4>Configuration</h4>
                <div class="pixel-config">
                  <div class="config-input">
                    <label>Pixel Size (μm):</label>
                    <input
                      type="number"
                      v-model="prostateData.pixelSizeUm"
                      @change="updatePixelSize"
                      step="0.01"
                      min="0.01"
                      class="pixel-input"
                    />
                  </div>
                  <div class="config-info">
                    <span>Pixel Size: {{ prostateData.pixelSizeMm?.toFixed(4) }} mm</span>
                    <span class="help-text">({{ prostateData.pixelSizeUm }} μm per pixel)</span>
                  </div>
                </div>
              </div>

              <!-- Area Summary -->
              <div class="area-summary" v-if="prostateData.totalMalignantArea > 0">
                <div class="area-cards">
                  <!-- Malignant Patterns -->
                  <div class="area-card pattern-3" v-if="prostateData.areas.gleason3 > 0">
                    <div class="area-card-header">
                      <h5>Gleason 3</h5>
                      <span class="pattern-role" v-if="prostateData.primaryPattern === 3">Primary</span>
                      <span class="pattern-role" v-else-if="prostateData.secondaryPattern === 3">Secondary</span>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.areas.gleason3?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-count">
                        {{ prostateData.annotationCounts.gleason3 || 0 }} annotations
                      </div>
                      <div class="area-percentage" v-if="prostateData.percentages.gleason3">
                        {{ prostateData.percentages.gleason3.toFixed(1) }}%
                      </div>
                    </div>
                  </div>

                  <div class="area-card pattern-4" v-if="prostateData.areas.gleason4 > 0">
                    <div class="area-card-header">
                      <h5>Gleason 4</h5>
                      <span class="pattern-role" v-if="prostateData.primaryPattern === 4">Primary</span>
                      <span class="pattern-role" v-else-if="prostateData.secondaryPattern === 4">Secondary</span>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.areas.gleason4?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-count">
                        {{ prostateData.annotationCounts.gleason4 || 0 }} annotations
                      </div>
                      <div class="area-percentage" v-if="prostateData.percentages.gleason4">
                        {{ prostateData.percentages.gleason4.toFixed(1) }}%
                      </div>
                    </div>
                  </div>

                  <div class="area-card pattern-5" v-if="prostateData.areas.gleason5 > 0">
                    <div class="area-card-header">
                      <h5>Gleason 5</h5>
                      <span class="pattern-role" v-if="prostateData.primaryPattern === 5">Primary</span>
                      <span class="pattern-role" v-else-if="prostateData.secondaryPattern === 5">Secondary</span>
                      <span class="pattern-role tertiary" v-else-if="prostateData.tertiaryPattern === 5">Tertiary</span>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.areas.gleason5?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-count">
                        {{ prostateData.annotationCounts.gleason5 || 0 }} annotations
                      </div>
                      <div class="area-percentage" v-if="prostateData.percentages.gleason5">
                        {{ prostateData.percentages.gleason5.toFixed(1) }}%
                      </div>
                    </div>
                  </div>

                  <!-- Non-malignant Areas -->
                  <div class="area-card stroma-card" v-if="prostateData.areas.stroma > 0">
                    <div class="area-card-header">
                      <h5>Stroma</h5>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.areas.stroma?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-count">
                        {{ prostateData.annotationCounts.stroma || 0 }} annotations
                      </div>
                    </div>
                  </div>

                  <div class="area-card benign-card" v-if="prostateData.areas.benign > 0">
                    <div class="area-card-header">
                      <h5>Benign</h5>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.areas.benign?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-count">
                        {{ prostateData.annotationCounts.benign || 0 }} annotations
                      </div>
                    </div>
                  </div>

                  <!-- Total Areas -->
                  <div class="area-card total-card malignant-total">
                    <div class="area-card-header">
                      <h5>Total Malignant</h5>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.totalMalignantArea?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-percentage" v-if="prostateData.totalTumorArea">
                        {{ ((prostateData.totalMalignantArea / prostateData.totalTumorArea) * 100).toFixed(1) }}% of tumor area
                      </div>
                    </div>
                  </div>

                  <div class="area-card total-card classified-total">
                    <div class="area-card-header">
                      <h5>Total Classified</h5>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.totalClassifiedArea?.toFixed(3) || '0.000' }} mm²
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="no-areas-message">
                <p>No area data calculated yet.</p>
                <p>Load annotations and click "Calculate Areas" to begin analysis.</p>
              </div>
            </div>
          </div>

          <!-- Scoring Tab -->
          <div v-if="activeProstateTab === 'scoring'" class="scoring-analysis-tab">
            <div class="scoring-content">
              <div class="scoring-header">
                <h3>Scoring and Grouping Calculation</h3>
                <div class="header-actions">
                  <!-- <button
                    class="refresh-btn-professional"
                    @click="calculateGleasonScore"
                    :disabled="!prostateData.totalMalignantArea || prostateData.totalMalignantArea === 0"
                  >
                    Calculate Gleason Score
                  </button> -->
                  <button class="refresh-btn-professional centralized-analysis-btn" @click="runCompleteProstateAnalysis">
                    <i class="fas fa-play-circle"></i>
                    Start/Restart Calculation
                  </button>
                </div>
              </div>

              <!-- Pattern Distribution Visualization -->
              <div class="pattern-distribution" v-if="prostateData.totalMalignantArea > 0">
                <div class="distribution-chart">
                  <div class="chart-bars">
                    <div
                      v-for="pattern in gleasonPatterns"
                      :key="pattern.id"
                      class="pattern-bar"
                      :class="[
                        `pattern-${pattern.id}`,
                        {
                          'primary-pattern': prostateData.primaryPattern === pattern.id,
                          'secondary-pattern': prostateData.secondaryPattern === pattern.id,
                          'tertiary-pattern': prostateData.tertiaryPattern === pattern.id
                        }
                      ]"
                      :style="{ width: `${getPatternPercentage(pattern.id)}%` }"
                    >
                      <div class="bar-label">
                        <span class="pattern-name">{{ pattern.name }}</span>
                        <span class="pattern-percentage">
                          {{ getPatternPercentageText(pattern.id) }}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Gleason Score Results -->
              <div class="gleason-results" v-if="prostateData.gleasonScore">
                <div class="results-card">
                  <div class="results-content">
                    <div class="score-display">
                      <div class="gleason-score-large">
                        {{ prostateData.gleasonScore }}
                      </div>
                      <div class="grade-group">
                        Grade Group: {{ prostateData.gradeGroup }}
                      </div>
                      <div class="score-description">
                        {{ getGleasonScoreDescription(prostateData.gleasonScore) }}
                      </div>
                    </div>

                    <div class="pattern-breakdown">
                      <div class="breakdown-item primary">
                        <span class="breakdown-label">Primary Pattern:</span>
                        <span class="breakdown-value pattern-3" v-if="prostateData.primaryPattern === 3">Gleason 3</span>
                        <span class="breakdown-value pattern-4" v-else-if="prostateData.primaryPattern === 4">Gleason 4</span>
                        <span class="breakdown-value pattern-5" v-else-if="prostateData.primaryPattern === 5">Gleason 5</span>
                        <span class="breakdown-percentage">({{ prostateData.percentages[prostateData.primaryPattern]?.toFixed(1) || '0.0' }}%)</span>
                      </div>
                      <div class="breakdown-item secondary">
                        <span class="breakdown-label">Secondary Pattern:</span>
                        <span class="breakdown-value pattern-3" v-if="prostateData.secondaryPattern === 3">Gleason 3</span>
                        <span class="breakdown-value pattern-4" v-else-if="prostateData.secondaryPattern === 4">Gleason 4</span>
                        <span class="breakdown-value pattern-5" v-else-if="prostateData.secondaryPattern === 5">Gleason 5</span>
                        <span class="breakdown-percentage">({{ prostateData.percentages[prostateData.secondaryPattern]?.toFixed(1) || '0.0' }}%)</span>
                      </div>
                      <div class="breakdown-item tertiary" v-if="prostateData.tertiaryPattern">
                        <span class="breakdown-label">Tertiary Pattern:</span>
                        <span class="breakdown-value pattern-3" v-if="prostateData.tertiaryPattern === 3">Gleason 3</span>
                        <span class="breakdown-value pattern-4" v-else-if="prostateData.tertiaryPattern === 4">Gleason 4</span>
                        <span class="breakdown-value pattern-5" v-else-if="prostateData.tertiaryPattern === 5">Gleason 5</span>
                        <span class="breakdown-percentage">({{ prostateData.percentages[prostateData.tertiaryPattern]?.toFixed(1) || '0.0' }}%)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Area Summary -->
              <div class="area-summary" v-if="prostateData.totalMalignantArea > 0">
                <div class="area-cards">
                  <!-- Malignant Patterns -->
                  <div class="area-card pattern-3" v-if="prostateData.areas.gleason3 > 0">
                    <div class="area-card-header">
                      <h5>Gleason 3</h5>
                      <span class="pattern-role" v-if="prostateData.primaryPattern === 3">Primary</span>
                      <span class="pattern-role" v-else-if="prostateData.secondaryPattern === 3">Secondary</span>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.areas.gleason3?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-count">
                        {{ prostateData.annotationCounts.gleason3 || 0 }} annotations
                      </div>
                      <div class="area-percentage" v-if="prostateData.percentages.gleason3">
                        {{ prostateData.percentages.gleason3.toFixed(1) }}%
                      </div>
                    </div>
                  </div>

                  <div class="area-card pattern-4" v-if="prostateData.areas.gleason4 > 0">
                    <div class="area-card-header">
                      <h5>Gleason 4</h5>
                      <span class="pattern-role" v-if="prostateData.primaryPattern === 4">Primary</span>
                      <span class="pattern-role" v-else-if="prostateData.secondaryPattern === 4">Secondary</span>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.areas.gleason4?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-count">
                        {{ prostateData.annotationCounts.gleason4 || 0 }} annotations
                      </div>
                      <div class="area-percentage" v-if="prostateData.percentages.gleason4">
                        {{ prostateData.percentages.gleason4.toFixed(1) }}%
                      </div>
                    </div>
                  </div>

                  <div class="area-card pattern-5" v-if="prostateData.areas.gleason5 > 0">
                    <div class="area-card-header">
                      <h5>Gleason 5</h5>
                      <span class="pattern-role" v-if="prostateData.primaryPattern === 5">Primary</span>
                      <span class="pattern-role" v-else-if="prostateData.secondaryPattern === 5">Secondary</span>
                      <span class="pattern-role tertiary" v-else-if="prostateData.tertiaryPattern === 5">Tertiary</span>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.areas.gleason5?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-count">
                        {{ prostateData.annotationCounts.gleason5 || 0 }} annotations
                      </div>
                      <div class="area-percentage" v-if="prostateData.percentages.gleason5">
                        {{ prostateData.percentages.gleason5.toFixed(1) }}%
                      </div>
                    </div>
                  </div>

                  <!-- Non-malignant Areas -->
                  <div class="area-card stroma-card" v-if="prostateData.areas.stroma > 0">
                    <div class="area-card-header">
                      <h5>Stroma</h5>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.areas.stroma?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-count">
                        {{ prostateData.annotationCounts.stroma || 0 }} annotations
                      </div>
                    </div>
                  </div>

                  <div class="area-card benign-card" v-if="prostateData.areas.benign > 0">
                    <div class="area-card-header">
                      <h5>Benign</h5>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.areas.benign?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-count">
                        {{ prostateData.annotationCounts.benign || 0 }} annotations
                      </div>
                    </div>
                  </div>

                  <!-- Total Areas -->
                  <div class="area-card total-card malignant-total">
                    <div class="area-card-header">
                      <h5>Total Malignant</h5>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.totalMalignantArea?.toFixed(3) || '0.000' }} mm²
                      </div>
                      <div class="area-percentage" v-if="prostateData.totalTumorArea">
                        {{ ((prostateData.totalMalignantArea / prostateData.totalTumorArea) * 100).toFixed(1) }}% of tumor area
                      </div>
                    </div>
                  </div>

                  <div class="area-card total-card classified-total">
                    <div class="area-card-header">
                      <h5>Total Classified</h5>
                    </div>
                    <div class="area-card-content">
                      <div class="area-value">
                        {{ prostateData.totalClassifiedArea?.toFixed(3) || '0.000' }} mm²
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="no-score-message">
                <p>No Gleason score or group calculated yet.</p>
                <p>Click "Start/Restart Calculation" to proceed.</p>
              </div>
            </div>
          </div>

          <!-- Report Tab -->
          <div v-if="activeProstateTab === 'report'" class="prostate-report-tab">
            <div class="report-content">
              <h3 class="report-title">Full Analysis</h3>

              <!-- Image Information -->
              <div class="image-info">
                <div class="image-details">
                  <span class="image-label">Image ID:</span>
                  <span class="image-value">{{ image?.id || 'N/A' }}</span>
                </div>
                <div class="image-details">
                  <span class="image-label">Filename:</span>
                  <span class="image-value">{{ image?.originalFilename || image?.filename || 'N/A' }}</span>
                </div>
                <div class="image-details">
                  <span class="image-label">Analysis Date:</span>
                  <span class="image-value">{{ new Date().toLocaleDateString() }}</span>
                </div>
                <div class="image-details">
                  <span class="image-label">Pixel Size:</span>
                  <span class="image-value">{{ prostateData.pixelSizeUm }} μm ({{ prostateData.pixelSizeMm?.toFixed(4) }} mm)</span>
                </div>
              </div>

              <!-- Gleason Score Summary - Editable -->
              <div class="gleason-summary-section" v-if="prostateData.gleasonScore || prostateData.totalMalignantArea > 0">
                <div class="section-header-with-actions">
                  <h4>Summary</h4>
                  <div class="edit-actions">
                    <button
                      v-if="!editingProstateValues.gleasonScore && !editingProstateValues.gradeGroup"
                      class="btn-edit-small"
                      @click="startEditingAllSummaryValues"
                      title="Edit all summary values"
                    >
                      <i class="fas fa-edit"></i> Edit All
                    </button>
                  </div>
                </div>

                <div class="summary-cards">
                  <!-- Gleason Score Card -->
                  <div class="summary-card primary editable-card">
                    <div class="summary-label">
                      Gleason Score
                      <span v-if="manualOverrides.gleasonScore" class="manual-override-badge" title="Manually overridden">
                        <i class="fas fa-user-edit"></i>
                      </span>
                    </div>

                    <div v-if="!editingProstateValues.gleasonScore" class="summary-value score editable-value">
                      {{ prostateData.gleasonScore || 'N/A' }}
                      <div class="value-actions">
                        <button class="btn-edit-tiny" @click="startEditingProstateValue('gleasonScore')">
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          v-if="manualOverrides.gleasonScore"
                          class="btn-reset-tiny"
                          @click="resetManualOverride('gleasonScore')"
                          title="Reset to auto-calculated"
                        >
                          <i class="fas fa-undo"></i>
                        </button>
                      </div>
                    </div>

                    <div v-else class="summary-edit">
                      <input
                        v-model="tempProstateValues.gleasonScore"
                        class="edit-input"
                        placeholder="e.g., 3+4, 4+3"
                        @keyup.enter="saveProstateValue('gleasonScore')"
                        @keyup.esc="cancelEditProstateValue('gleasonScore')"
                      />
                      <div class="edit-actions">
                        <button class="btn-save-tiny" @click="saveProstateValue('gleasonScore')">
                          <i class="fas fa-check"></i>
                        </button>
                        <button class="btn-cancel-tiny" @click="cancelEditProstateValue('gleasonScore')">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Grade Group Card -->
                  <div class="summary-card secondary editable-card">
                    <div class="summary-label">
                      Grade Group
                      <span v-if="manualOverrides.gradeGroup" class="manual-override-badge" title="Manually overridden">
                        <i class="fas fa-user-edit"></i>
                      </span>
                    </div>

                    <div v-if="!editingProstateValues.gradeGroup" class="summary-value group editable-value">
                      {{ prostateData.gradeGroup || 'N/A' }}
                      <div class="value-actions">
                        <button class="btn-edit-tiny" @click="startEditingProstateValue('gradeGroup')">
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          v-if="manualOverrides.gradeGroup"
                          class="btn-reset-tiny"
                          @click="resetManualOverride('gradeGroup')"
                          title="Reset to auto-calculated"
                        >
                          <i class="fas fa-undo"></i>
                        </button>
                      </div>
                    </div>

                    <div v-else class="summary-edit">
                      <input
                        v-model="tempProstateValues.gradeGroup"
                        class="edit-input"
                        placeholder="e.g., Grade Group 2"
                        @keyup.enter="saveProstateValue('gradeGroup')"
                        @keyup.esc="cancelEditProstateValue('gradeGroup')"
                      />
                      <div class="edit-actions">
                        <button class="btn-save-tiny" @click="saveProstateValue('gradeGroup')">
                          <i class="fas fa-check"></i>
                        </button>
                        <button class="btn-cancel-tiny" @click="cancelEditProstateValue('gradeGroup')">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Total Malignant Area Card -->
                  <div class="summary-card tertiary">
                    <div class="summary-label">Total Malignant Area</div>
                    <div class="summary-value area">{{ prostateData.totalMalignantArea?.toFixed(3) || '0.000' }} mm²</div>
                  </div>

                  <!-- Tumor Percentage Card - Editable -->
                  <div class="summary-card quaternary editable-card">
                    <div class="summary-label">
                      Tumor Percentage
                      <span v-if="manualOverrides.tumorPercentage" class="manual-override-badge" title="Manually overridden">
                        <i class="fas fa-user-edit"></i>
                      </span>
                    </div>

                    <div v-if="!editingProstateValues.tumorPercentage" class="summary-value percentage editable-value">
                      {{ prostateData.totalTumorArea ? ((prostateData.totalMalignantArea / prostateData.totalTumorArea) * 100).toFixed(1) : '0.0' }}%
                      <div class="value-actions">
                        <button class="btn-edit-tiny" @click="startEditingProstateValue('tumorPercentage')">
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          v-if="manualOverrides.tumorPercentage"
                          class="btn-reset-tiny"
                          @click="resetManualOverride('tumorPercentage')"
                          title="Reset to auto-calculated"
                        >
                          <i class="fas fa-undo"></i>
                        </button>
                      </div>
                    </div>

                    <div v-else class="summary-edit">
                      <input
                        v-model="tempProstateValues.tumorPercentage"
                        type="number"
                        class="edit-input"
                        min="0"
                        max="100"
                        step="0.1"
                        placeholder="0.0 - 100.0"
                        @keyup.enter="saveProstateValue('tumorPercentage')"
                        @keyup.esc="cancelEditProstateValue('tumorPercentage')"
                      />
                      <span class="percentage-suffix">%</span>
                      <div class="edit-actions">
                        <button class="btn-save-tiny" @click="saveProstateValue('tumorPercentage')">
                          <i class="fas fa-check"></i>
                        </button>
                        <button class="btn-cancel-tiny" @click="cancelEditProstateValue('tumorPercentage')">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pattern Distribution - Editable -->
              <div class="pattern-distribution-section" v-if="prostateData.totalMalignantArea > 0">
                <div class="section-header-with-actions">
                  <h4>Pattern Distribution</h4>
                  <div class="edit-actions">
                    <button
                      v-if="!Object.values(editingProstateValues.percentages).some(v => v)"
                      class="btn-edit-small"
                      @click="startEditingAllPercentages"
                      title="Edit all percentages"
                    >
                      <i class="fas fa-edit"></i> Edit All
                    </button>
                  </div>
                </div>

                <div class="distribution-table">
                  <div class="table-header">
                    <div class="table-cell">Pattern</div>
                    <div class="table-cell">Area (mm²)</div>
                    <div class="table-cell">Percentage</div>
                    <!-- <div class="table-cell">Annotations</div>-->
                    <!-- <div class="table-cell">Role</div> -->
                    <div class="table-cell"></div>
                  </div>
                  <div class="table-body">
                    <!-- Gleason 3 -->
                    <div class="table-row" :class="{'primary-row': prostateData.primaryPattern === 3, 'secondary-row': prostateData.secondaryPattern === 3}">
                      <div class="table-cell pattern-name">Gleason 3</div>
                      <div class="table-cell pattern-area">{{ (prostateData.areas.gleason3 || 0).toFixed(3) }}</div>

                      <!-- Percentage Cell - Editable -->
                      <div class="table-cell pattern-percentage editable-cell">
                        <div v-if="!editingProstateValues.percentages.gleason3" class="">
                          <span class="">{{ (prostateData.percentages.gleason3 || 0).toFixed(1) }}%</span>
                          <span v-if="manualOverrides.percentages.gleason3" class="manual-override-badge" title="Manually overridden">
                            <i class="fas fa-user-edit"></i>
                          </span>
                        </div>

                        <div v-else class="percentage-edit">
                          <input
                            v-model="tempProstateValues.percentages.gleason3"
                            type="number"
                            class="percentage-input"
                            min="0"
                            max="100"
                            step="0.1"
                            @keyup.enter="saveProstateValue('percentages', 'gleason3')"
                            @keyup.esc="cancelEditProstateValue('percentages', 'gleason3')"
                          />
                          <span class="percentage-suffix">%</span>
                          <div class="edit-actions">
                            <button class="btn-save-tiny" @click="saveProstateValue('percentages', 'gleason3')">
                              <i class="fas fa-check"></i>
                            </button>
                            <button class="btn-cancel-tiny" @click="cancelEditProstateValue('percentages', 'gleason3')">
                              <i class="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- <div class="table-cell pattern-annotations">{{ prostateData.annotationCounts.gleason3 || 0 }}</div> -->
                      <!-- <div class="table-cell pattern-role">
                        <span v-if="prostateData.primaryPattern === 3" class="role-badge primary">Primary</span>
                        <span v-else-if="prostateData.secondaryPattern === 3" class="role-badge secondary">Secondary</span>
                      </div> -->
                      <div class="table-cell pattern-actions">
                        <button
                          v-if="!editingProstateValues.percentages.gleason3"
                          class="btn-edit-tiny"
                          @click="startEditingProstateValue('percentages', 'gleason3')"
                          title="Edit percentage"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          v-if="manualOverrides.percentages.gleason3 && !editingProstateValues.percentages.gleason3"
                          class="btn-reset-tiny"
                          @click="resetManualOverride('percentages', 'gleason3')"
                          title="Reset to auto-calculated"
                        >
                          <i class="fas fa-undo"></i>
                        </button>
                      </div>
                    </div>

                    <!-- Repeat similar structure for Gleason 4 and Gleason 5 -->
                    <!-- Gleason 4 -->
                    <div class="table-row" :class="{'primary-row': prostateData.primaryPattern === 4, 'secondary-row': prostateData.secondaryPattern === 4}">
                      <div class="table-cell pattern-name">Gleason 4</div>
                      <div class="table-cell pattern-area">{{ (prostateData.areas.gleason4 || 0).toFixed(3) }}</div>

                      <div class="table-cell pattern-percentage editable-cell">
                        <div v-if="!editingProstateValues.percentages.gleason4" class="">
                          <span class="">{{ (prostateData.percentages.gleason4 || 0).toFixed(1) }}%</span>
                          <span v-if="manualOverrides.percentages.gleason4" class="manual-override-badge" title="Manually overridden">
                            <i class="fas fa-user-edit"></i>
                          </span>
                        </div>

                        <div v-else class="percentage-edit">
                          <input
                            v-model="tempProstateValues.percentages.gleason4"
                            type="number"
                            class="percentage-input"
                            min="0"
                            max="100"
                            step="0.1"
                            @keyup.enter="saveProstateValue('percentages', 'gleason4')"
                            @keyup.esc="cancelEditProstateValue('percentages', 'gleason4')"
                          />
                          <span class="percentage-suffix">%</span>
                          <div class="edit-actions">
                            <button class="btn-save-tiny" @click="saveProstateValue('percentages', 'gleason4')">
                              <i class="fas fa-check"></i>
                            </button>
                            <button class="btn-cancel-tiny" @click="cancelEditProstateValue('percentages', 'gleason4')">
                              <i class="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- <div class="table-cell pattern-annotations">{{ prostateData.annotationCounts.gleason4 || 0 }}</div> -->
                      <!-- <div class="table-cell pattern-role">
                        <span v-if="prostateData.primaryPattern === 4" class="role-badge primary">Primary</span>
                        <span v-else-if="prostateData.secondaryPattern === 4" class="role-badge secondary">Secondary</span>
                      </div> -->
                      <div class="table-cell pattern-actions">
                        <button
                          v-if="!editingProstateValues.percentages.gleason4"
                          class="btn-edit-tiny"
                          @click="startEditingProstateValue('percentages', 'gleason4')"
                          title="Edit percentage"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          v-if="manualOverrides.percentages.gleason4 && !editingProstateValues.percentages.gleason4"
                          class="btn-reset-tiny"
                          @click="resetManualOverride('percentages', 'gleason4')"
                          title="Reset to auto-calculated"
                        >
                          <i class="fas fa-undo"></i>
                        </button>
                      </div>
                    </div>

                    <!-- Gleason 5 -->
                    <div class="table-row" :class="{'primary-row': prostateData.primaryPattern === 5, 'secondary-row': prostateData.secondaryPattern === 5, 'tertiary-row': prostateData.tertiaryPattern === 5}">
                      <div class="table-cell pattern-name">Gleason 5</div>
                      <div class="table-cell pattern-area">{{ (prostateData.areas.gleason5 || 0).toFixed(3) }}</div>

                      <div class="table-cell pattern-percentage editable-cell">
                        <div v-if="!editingProstateValues.percentages.gleason5" class="">
                          <span class="">{{ (prostateData.percentages.gleason5 || 0).toFixed(1) }}%</span>
                          <span v-if="manualOverrides.percentages.gleason5" class="manual-override-badge" title="Manually overridden">
                            <i class="fas fa-user-edit"></i>
                          </span>
                        </div>

                        <div v-else class="percentage-edit">
                          <input
                            v-model="tempProstateValues.percentages.gleason5"
                            type="number"
                            class="percentage-input"
                            min="0"
                            max="100"
                            step="0.1"
                            @keyup.enter="saveProstateValue('percentages', 'gleason5')"
                            @keyup.esc="cancelEditProstateValue('percentages', 'gleason5')"
                          />
                          <span class="percentage-suffix">%</span>
                          <div class="edit-actions">
                            <button class="btn-save-tiny" @click="saveProstateValue('percentages', 'gleason5')">
                              <i class="fas fa-check"></i>
                            </button>
                            <button class="btn-cancel-tiny" @click="cancelEditProstateValue('percentages', 'gleason5')">
                              <i class="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- <div class="table-cell pattern-annotations">{{ prostateData.annotationCounts.gleason5 || 0 }}</div> -->
                      <!-- <div class="table-cell pattern-role">
                        <span v-if="prostateData.primaryPattern === 5" class="role-badge primary">Primary</span>
                        <span v-else-if="prostateData.secondaryPattern === 5" class="role-badge secondary">Secondary</span>
                        <span v-else-if="prostateData.tertiaryPattern === 5" class="role-badge tertiary">Tertiary</span>
                      </div> -->
                      <div class="table-cell pattern-actions">
                        <button
                          v-if="!editingProstateValues.percentages.gleason5"
                          class="btn-edit-tiny"
                          @click="startEditingProstateValue('percentages', 'gleason5')"
                          title="Edit percentage"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          v-if="manualOverrides.percentages.gleason5 && !editingProstateValues.percentages.gleason5"
                          class="btn-reset-tiny"
                          @click="resetManualOverride('percentages', 'gleason5')"
                          title="Reset to auto-calculated"
                        >
                          <i class="fas fa-undo"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Total Percentage Validation -->
                <div class="percentage-total-validation" :class="{'invalid-total': Math.abs(patternPercentageSum - 100) > 1}">
                  <span class="total-label">Total Percentage: {{ patternPercentageSum.toFixed(1) }}%</span>
                  <span v-if="Math.abs(patternPercentageSum - 100) > 1" class="validation-warning">
                    <i class="fas fa-exclamation-triangle"></i>
                    Total should be 100% (currently {{ patternPercentageSum.toFixed(1) }}%)
                  </span>
                </div>
              </div>

              <div v-else class="no-data-message">
                <p>No prostate analysis data available.</p>
                <p>Complete the analysis steps to generate a report.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Navigation -->
    <div class="bottom-navigation">
      <div class="nav-buttons">
        <button class="nav-btn secondary" :disabled="isFirstProstateStep" @click="previousProstateStep">
          Previous
        </button>
        <button class="nav-btn primary" @click="nextProstateStep">
          {{ isLastProstateStep ? 'Complete Full Analysis' : 'Next' }}
        </button>
      </div>
    </div>

    <!-- Comments Section -->
    <!-- <div class="comments-section">
      <h3>Prostate Analysis Notes</h3>
      <div class="comment-groups">
        <div class="comment-group">
          <div class="comment-header">
            <span class="comment-label">Clinical Interpretation</span>
            <button
              v-if="!editingProstateComments.clinical"
              class="add-comment-btn"
              @click="startEditingProstateComment('clinical')"
            >
              {{ prostateComments.clinical ? 'Edit Note' : 'Add Note' }}
            </button>
          </div>

          <div v-if="prostateComments.clinical && !editingProstateComments.clinical" class="existing-comment">
            <div class="comment-content">{{ prostateComments.clinical }}</div>
            <div class="comment-meta">
              <span class="comment-date">{{ getProstateCommentDate('clinical') }}</span>
            </div>
          </div>

          <div v-if="editingProstateComments.clinical" class="comment-editor">
            <textarea
              v-model="currentProstateCommentText"
              class="comment-textarea-inline"
              rows="4"
              placeholder="Enter clinical interpretation notes..."
            ></textarea>

            <div class="comment-meta-info-inline">
              <div class="user-info">
                <span class="user-name">{{ currentUser?.name || 'Current User' }}</span>
                <span class="time-value">{{ new Date().toLocaleString() }}</span>
              </div>

              <div class="comment-actions">
                <button class="btn-secondary-small" @click="cancelEditProstateComment('clinical')">Cancel</button>
                <button class="btn-primary-small" @click="saveProstateCommentInline('clinical')">Save</button>
                <button
                  v-if="prostateComments.clinical"
                  class="btn-danger-small"
                  @click="deleteProstateCommentInline('clinical')"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div> -->
  </div>
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';
import {getAllTerms} from '@/utils/ontology-utils';
import {appendShortTermToken} from '@/utils/token-utils';
import CellClassificationModal from './blood-film/wbc/CellClassificationModal.vue';
import ImageThumbnail from '@/components/image/ImageThumbnail.vue';
import VueDraggableResizable from 'vue-draggable-resizable';
import {Annotation, AnnotationTerm, AnnotationCollection, TagDomainAssociationCollection, Property, PropertyCollection} from 'cytomine-client';

export default {
  name: 'ai-analysis-panel',
  components: {
    CellClassificationModal,
    ImageThumbnail,
    VueDraggableResizable
  },
  props: {
    index: String
  },
  data() {
    return {
      activeModel: 'prostate-gleason',
      availableModels: [
        {
          id: 'prostate-gleason',
          name: 'Prostate Gleason Scoring Analysis',
          description: 'Prostate cancer Gleason scoring'
        },
        {
          id: 'blood-film',
          name: 'Peripheral Blood Film Analysis',
          description: 'Peripheral Blood Film analysis'
        }
      ],

      // Tab and view state
      activeTab: 'annotations',
      currentView: 'cells',

      // Analysis session data
      analysisSession: {
        runId: 'demo123',
        imageId: 'demo123',
        status: 'pending_review', // pending_review, in_progress, completed
        currentStep: 'wbc'
      },

      // Analysis tabs configuration
      analysisTabs: [
        { id: 'annotations', name: 'Annotation List' },
        { id: 'wbc', name: 'White Blood Cell (WBC)' },
        { id: 'rbc', name: 'Red Blood Cell (RBC)' },
        { id: 'plt', name: 'Platelet (PLT)' },
        { id: 'report', name: 'Report' }
      ],

      // Ontology data (populated from real Cytomine ontology)
      ontologyCategories: [],

      // Mock cell data for grid display
      currentCells: [
        { id: 140, index: 140, aiClassification: 'Lymphocyte', thumbnailUrl: null },
        { id: 141, index: 141, aiClassification: 'Lymphocyte', thumbnailUrl: null },
        { id: 142, index: 142, aiClassification: 'Lymphocyte', thumbnailUrl: null },
        { id: 143, index: 143, aiClassification: 'Lymphocyte', thumbnailUrl: null },
        { id: 144, index: 144, aiClassification: 'Lymphocyte', thumbnailUrl: null },
        { id: 145, index: 145, aiClassification: 'Lymphocyte', thumbnailUrl: null },
        { id: 146, index: 146, aiClassification: 'Lymphocyte', thumbnailUrl: null },
        { id: 147, index: 147, aiClassification: 'Lymphocyte', thumbnailUrl: null },
        { id: 148, index: 148, aiClassification: 'Lymphocyte', thumbnailUrl: null },
        { id: 149, index: 149, aiClassification: 'Lymphocyte', thumbnailUrl: null },
        { id: 150, index: 150, aiClassification: 'Lymphocyte', thumbnailUrl: null },
        { id: 151, index: 151, aiClassification: 'Lymphocyte', thumbnailUrl: null }
      ],

      // RBC cell data with term-based detection
      rbcCells: [],

      // PLT cell data with term-based detection
      pltCells: [],

      // Loading progress tracking for large annotation sets
      annotationLoadingProgress: null, // { loaded: number, total: number, message: string }

      // Overall loading states for each tab
      tabLoadingStates: {
        wbc: false,
        rbc: false,
        plt: false
      },

      // Loading optimization configuration
      loadingConfig: {
        useSingleRequest: true, // Try single request first
        largeBatchSize: 50000, // Fallback batch size (much larger than original 5k)
        skipDelays: true // Remove delays between batches for faster loading
      },

      // RBC regions
      rbcRegions: [
        { id: 1, name: 'Region 1', active: true, status: 'pending', analyzedCount: 0, abnormalCount: 0 },
        { id: 2, name: 'Region 2', active: false, status: 'pending', analyzedCount: 0, abnormalCount: 0 },
        { id: 3, name: 'Region 3', active: false, status: 'pending', analyzedCount: 0, abnormalCount: 0 },
        { id: 4, name: 'Region 4', active: false, status: 'pending', analyzedCount: 0, abnormalCount: 0 }
      ],

      // RBC analysis data
      rbcCurrentView: 'grid',
      currentRBCCells: [
        { id: 'rbc1', index: 1, thumbnailUrl: null, morphologyScore: null },
        { id: 'rbc2', index: 2, thumbnailUrl: null, morphologyScore: null },
        { id: 'rbc3', index: 3, thumbnailUrl: null, morphologyScore: null },
        { id: 'rbc4', index: 4, thumbnailUrl: null, morphologyScore: null },
        { id: 'rbc5', index: 5, thumbnailUrl: null, morphologyScore: null },
        { id: 'rbc6', index: 6, thumbnailUrl: null, morphologyScore: null },
        { id: 'rbc7', index: 7, thumbnailUrl: null, morphologyScore: null },
        { id: 'rbc8', index: 8, thumbnailUrl: null, morphologyScore: null }
      ],

      // RBC morphology categories and scoring - Updated based on PDF specifications
      rbcMorphologyCategories: [
        {
          name: 'Normal',
          items: [
            {
              name: 'rbc0-normal',
              displayName: 'Normal RBC',
              score: null,
              referenceRanges: { 1: 'N/A', 2: 'N/A', 3: 'N/A' },
              isNormal: true
            }
          ]
        },
        {
          name: 'Shape',
          items: [
            {
              name: 'rbc1-helmet-schistocyte',
              displayName: 'Schistocyte',
              score: null,
              referenceRanges: { 1: '1-2', 2: '3-6', 3: '>6' }
            },
            {
              name: 'rbc2-ovalocyte',
              displayName: 'Ovalocyte',
              score: null,
              referenceRanges: { 1: '15-61', 2: '>61', 3: null }
            },
            {
              name: 'rbc3-spur-acanthocyte',
              displayName: 'Acanthocyte',
              score: null,
              referenceRanges: { 1: '15-61', 2: '>61', 3: null }
            },
            {
              name: 'rbc4-teardrop',
              displayName: 'Dacrocyte',
              score: null,
              referenceRanges: { 1: '15-61', 2: '>61', 3: null }
            },
            {
              name: 'rbc5-burr-echinocyte',
              displayName: 'Echinocyte',
              score: null,
              referenceRanges: { 1: '15-61', 2: '>61', 3: null }
            },
            {
              name: 'rbc6-sickle',
              displayName: 'Drepanocyte',
              score: null,
              referenceRanges: { 1: '3-6', 2: '>6', 3: null }
            },
            {
              name: 'rbc8-pencil-elliptocyte',
              displayName: 'Elliptocyte',
              score: null,
              referenceRanges: { 1: '15-61', 2: '>61', 3: null }
            },
            {
              name: 'rbc9-spherocyte',
              displayName: 'Spherocyte',
              score: null,
              referenceRanges: { 1: '15-61', 2: '>61', 3: null }
            },
            {
              name: 'rbc10-stomatocyte',
              displayName: 'Stomatocyte',
              score: null,
              referenceRanges: { 1: '15-61', 2: '>61', 3: null }
            },
            {
              name: 'rbc11-target',
              displayName: 'Codocyte',
              score: null,
              referenceRanges: { 1: '15-61', 2: '>61', 3: null }
            }
          ]
        },
        {
          name: 'Size & Colour',
          items: [
            {
              name: 'rbc12-hypochromia',
              displayName: 'Hypochromia',
              score: null,
              referenceRanges: { 1: '33-61', 2: '>61', 3: null }
            },
            {
              name: 'rbc13-macrocyte',
              displayName: 'Macrocyte',
              score: null,
              referenceRanges: { 1: '33-61', 2: '>61', 3: null }
            },
            {
              name: 'rbc14-microcyte',
              displayName: 'Microcyte',
              score: null,
              referenceRanges: { 1: '33-61', 2: '>61', 3: null }
            },
            {
              name: 'rbc15-erythroblast',
              displayName: 'Nucleated RBC',
              score: null,
              referenceRanges: { 1: 'Any', 2: '>5%', 3: '>10%' }
            }
          ]
        }
      ],

      // PLT analysis data removed - now using computed properties and database storage

      // General state
      totalWBCCounted: 200,

      // Classification modal state
      classificationModal: {
        isOpen: false,
        selectedCell: null
      },

      // Comments editing state
      editingComments: {
        annotations: false,
        wbc: false,
        rbc: false,
        plt: false,
        report: false
      },

      // Comments storage (per project and tab)
      tabComments: {
        annotations: '',
        wbc: '',
        rbc: '',
        plt: '',
        report: ''
      },

      // Comment metadata storage
      commentMetadata: {
        annotations: null,
        wbc: null,
        rbc: null,
        plt: null,
        report: null
      },

      // Current comment text for editing
      currentCommentText: '',

      // Annotation list data
      selectedAnnotationTerm: '',
      isLoadingAnnotations: false,
      allAnnotations: [],
      filteredAnnotations: [],
      expandedAnnotations: {},
      currentSelectedAnnotation: null,

      // Term reclassification state
      selectedNewTerm: '',
      isUpdatingAnnotation: false,

      // Thumbnail configuration
      shouldUseImageCropFallback: false, // Can be enabled if userannotation endpoint fails

      // Server configuration
      cytomineServerUrl: process.env.BACKEND_URL || 'https://selayangkkm2.viewer.vidanex.com',

      // AI Comments editing state (for Report tab)
      editingAIComments: {
        errorRule: false,
        actionMessages: false
      },

      // AI Comments storage (for Report tab)
      aiComments: {
        errorRule: '',
        actionMessages: ''
      },

      // AI Comment metadata storage
      aiCommentMetadata: {
        errorRule: null,
        actionMessages: null
      },

      // Current AI comment text for editing
      currentAICommentText: '',

      // RBC Morphology scores storage
      rbcMorphologyScores: {
        // Structure: { morphologyName: { score: number, source: 'auto'|'manual', timestamp: string, userId: string } }
      },

      // Loading states
      isAutoCalculating: false,

      // PLT data storage
      pltData: {
        averageCount: null, // null means use auto-calculated value
        source: 'auto', // 'auto' or 'manual'
        lastModified: null,
        userId: null,
        userName: null
      },

      // Slide viewer state
      slideImageLoading: false,
      slideImageError: false,
      annotationsVisible: true,
      slideViewerZoom: 1,
      slideViewerCenter: { x: 0, y: 0 },

      // Prostate-specific data
      activeProstateTab: 'scoring',
      prostateTabs: [
        /*{ id: 'annotations', name: 'Annotation List' },
        { id: 'areas', name: 'Area Calculation' },*/
        { id: 'scoring', name: 'Scoring and Grouping Calculation' },
        { id: 'report', name: 'Full Analysis' }
      ],

      // Prostate annotation data
      selectedProstateAnnotationTerm: '',
      isLoadingProstateAnnotations: false,
      allProstateAnnotations: [],
      filteredProstateAnnotations: [],
      expandedProstateAnnotations: {},

      // Prostate analysis data
      prostateData: {
        pixelSizeUm: 0.25, // Default pixel size in micrometers
        pixelSizeMm: 0.00025, // Calculated pixel size in millimeters
        areas: {
          gleason3: 0,
          gleason4: 0,
          gleason5: 0,
          stroma: 0,
          benign: 0
        },
        annotationCounts: {
          gleason3: 0,
          gleason4: 0,
          gleason5: 0,
          stroma: 0,
          benign: 0
        },
        percentages: {
          gleason3: 0,
          gleason4: 0,
          gleason5: 0
        },
        totalMalignantArea: 0,
        totalClassifiedArea: 0,
        totalTumorArea: 0,
        tumorPercentage: 0,
        primaryPattern: null,
        secondaryPattern: null,
        tertiaryPattern: null,
        gleasonScore: null,
        gradeGroup: null
      },

      // Prostate comments
      editingProstateComments: {
        clinical: false
      },
      prostateComments: {
        clinical: ''
      },
      currentProstateCommentText: '',

      // Loading states
      isCalculatingAreas: false,

      // Gleason pattern definitions
      gleasonPatterns: [
        { id: 3, name: 'Gleason 3', color: '#4CAF50' },
        { id: 4, name: 'Gleason 4', color: '#FF9800' },
        { id: 5, name: 'Gleason 5', color: '#F44336' }
      ],

      // Prostate editable state
      editingProstateValues: {
        gleasonScore: false,
        gradeGroup: false,
        percentages: {
          gleason3: false,
          gleason4: false,
          gleason5: false
        },
        tumorPercentage: false
      },

      // Temporary storage for edited values
      tempProstateValues: {
        gleasonScore: '',
        gradeGroup: '',
        percentages: {
          gleason3: '',
          gleason4: '',
          gleason5: ''
        },
        tumorPercentage: ''
      },

      // Track which values are manually overridden
      manualOverrides: {
        gleasonScore: false,
        gradeGroup: false,
        percentages: {
          gleason3: false,
          gleason4: false,
          gleason5: false
        },
        tumorPercentage: false
      }
    };
  },
  computed: {
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    currentUser: get('currentUser/user'),
    slices() {
      return this.imageWrapper.activeSlices;
    },
    ontology: get('currentProject/ontology'),
    allTerms() {
      return this.ontology ? getAllTerms(this.ontology) : [];
    },
    pltEstimate() {
      return `${this.currentPLTAverageCount * 10}×10⁹/L`;
    },
    isFirstStep() {
      return this.activeTab === 'annotations';
    },
    isLastStep() {
      return this.activeTab === 'report';
    },

    // Sorted and filtered WBC data for inline display
    sortedWBCTerms() {
      const allTerms = [];

      // Add safety check and loading state handling
      if (!this.ontologyCategories || this.ontologyCategories.length === 0) {
        console.log('sortedWBCTerms: No ontology categories available yet');
        return [];
      }

      try {
        this.ontologyCategories.forEach(category => {
          if (!category.terms || !Array.isArray(category.terms)) {
            console.warn(`Category ${category.name} has no valid terms array`);
            return;
          }

          category.terms.forEach(term => {
            // Skip platelet-thrombocyte terms and RBC terms
            if (term.name && (term.name.toLowerCase().includes('platelet') ||
                term.name.toLowerCase().includes('thrombocyte') ||
                term.name.toLowerCase().startsWith('rbc'))) {
              return;
            }

            // Include terms with counts >= 0 (show all terms, even with 0 count for better UX)
            const count = parseInt(term.count) || 0;
            const percentage = parseFloat(term.percentage) || 0;

            allTerms.push({
              name: this.cleanWBCCellTypeName(term.name),
              originalName: term.name, // Keep original for debugging
              count: count,
              percentage: percentage,
              percentageText: percentage.toFixed(1),
              isSupported: term.isSupported !== false // Default to true
            });

            // Add child terms if they exist
            if (term.children && Array.isArray(term.children)) {
              term.children.forEach(child => {
                // Skip RBC child terms
                if (child.name && child.name.toLowerCase().startsWith('rbc')) {
                  return;
                }

                const childCount = parseInt(child.count) || 0;
                const childPercentage = parseFloat(child.percentage) || 0;

                allTerms.push({
                  name: this.cleanWBCCellTypeName(child.name),
                  originalName: child.name, // Keep original for debugging
                  count: childCount,
                  percentage: childPercentage,
                  percentageText: childPercentage.toFixed(1),
                  isSupported: child.isSupported !== false
                });
              });
            }
          });
        });
      } catch (error) {
        console.error('Error in sortedWBCTerms:', error);
        return [];
      }

      // Sort by count first (highest first), then by percentage
      return allTerms.sort((a, b) => {
        if (a.count !== b.count) {
          return b.count - a.count;
        }
        return b.percentage - a.percentage;
      });
    },

    // Classification options for modal dropdown
    classificationOptions() {
      // Add safety check
      if (!this.ontologyCategories || !Array.isArray(this.ontologyCategories)) {
        console.log('classificationOptions: No ontology categories available');
        return [];
      }

      try {
        return this.ontologyCategories.map(category => ({
          name: category.name,
          terms: (category.terms || []).map(term => ({
            id: term.id,
            name: term.name,
            isSupported: term.isSupported !== false // Default to true if not specified
          }))
        }));
      } catch (error) {
        console.error('Error in classificationOptions:', error);
        return [];
      }
    },

    // Cytomine integration computed properties
    project() {
      try {
        return this.$store.state.currentProject?.project ||
               this.$store.getters['currentProject/project'] ||
               null;
      } catch (error) {
        console.warn('Error accessing project from store:', error);
        return null;
      }
    },

    currentUser() {
      try {
        return this.$store.state.currentUser?.user ||
               this.$store.getters['currentProject/user'] ||
               { name: 'Unknown User', id: 'unknown' };
      } catch (error) {
        console.warn('Error accessing currentUser from store:', error);
        return { name: 'Unknown User', id: 'unknown' };
      }
    },

    // Slide viewer computed properties
    showSlideIntegration() {
      // Enable slide integration when in Slide view mode
      return this.currentView === 'slide' && this.image;
    },

    aiProcessedCellCount() {
      return this.currentCells.filter(cell => cell.aiClassification).length;
    },

    // Slide viewer computed properties
    shortTermToken: get('currentUser/shortTermToken'),

    slideImageReady() {
      // Simple check if we have image and token for ImageThumbnail component
      const ready = !!(this.image && this.shortTermToken);
      console.log('Slide image ready:', ready);
      if (ready) {
        console.log('Image ID:', this.image.id);
        console.log('Has macro:', !!this.image.macroURL);
      }
      return ready;
    },

    visibleAnnotations() {
      return this.annotationsVisible ? this.currentCells.filter(cell => cell.position) : [];
    },

    // RBC Analysis computed properties
    activeRBCRegion() {
      return this.rbcRegions.find(region => region.active) || this.rbcRegions[0];
    },

    totalRBCAbnormalities() {
      let total = 0;
      this.rbcMorphologyCategories.forEach(category => {
        category.items.forEach(item => {
          total += parseInt(item.score) || 0;
        });
      });
      return total;
    },

    rbcSeverityLevel() {
      const total = this.totalRBCAbnormalities;
      if (total === 0) return 'Normal';
      if (total <= 5) return 'Mild';
      if (total <= 10) return 'Moderate';
      return 'Severe';
    },

    rbcSeverityClass() {
      const level = this.rbcSeverityLevel.toLowerCase();
      return {
        'severity-normal': level === 'normal',
        'severity-mild': level === 'mild',
        'severity-moderate': level === 'moderate',
        'severity-severe': level === 'severe'
      };
    },

    canCompleteRegion() {
      return this.activeRBCRegion.analyzedCount > 0;
    },


    // WBC Analysis computed properties
    uniqueCellTypesCount() {
      if (!this.ontologyCategories || !Array.isArray(this.ontologyCategories)) {
        return 0;
      }

      let uniqueTypes = 0;
      try {
        this.ontologyCategories.forEach(category => {
          if (category.terms && Array.isArray(category.terms)) {
            category.terms.forEach(term => {
              if (term.count > 0) uniqueTypes++;
              if (term.children && Array.isArray(term.children)) {
                term.children.forEach(child => {
                  if (child.count > 0) uniqueTypes++;
                });
              }
            });
          }
        });
      } catch (error) {
        console.error('Error in uniqueCellTypesCount:', error);
      }
      return uniqueTypes;
    },

    // Server URL configuration
    cytomineApiBaseUrl() {
      // Priority order: environment variable, URL parameter, default hardcoded
      const urlParams = new URLSearchParams(window.location.search);
      const serverFromUrl = urlParams.get('server') || urlParams.get('cytomineUrl');

      return serverFromUrl ||
             process.env.VUE_APP_CYTOMINE_URL ||
             process.env.BACKEND_URL ||
             this.cytomineServerUrl ||
             'https://selayangkkm2.viewer.vidanex.com';
    },

    // Term Categories for Reclassification Select
    termCategories() {
      const categories = [];
      if (this.ontologyCategories && Array.isArray(this.ontologyCategories)) {
        this.ontologyCategories.forEach(category => {
          if (category.terms && Array.isArray(category.terms)) {
            const categoryTerms = [];
            category.terms.forEach(term => {
              // Clean term name by removing prefixes like "wbc0-", "rbc0-", "plt0-"
              const cleanName = this.cleanTermName(term.name);
              categoryTerms.push({ id: term.id, name: cleanName, originalName: term.name });

              // Add children if they exist
              if (term.children && Array.isArray(term.children)) {
                term.children.forEach(child => {
                  const cleanChildName = this.cleanTermName(child.name);
                  categoryTerms.push({ id: child.id, name: cleanChildName, originalName: child.name });
                });
              }
            });

            if (categoryTerms.length > 0) {
              categories.push({
                name: category.name,
                terms: categoryTerms
              });
            }
          }
        });
      }
      return categories;
    },

    // Annotation List computed properties
    availableAnnotationTerms() {
      const terms = [];
      if (this.ontologyCategories && Array.isArray(this.ontologyCategories)) {
        this.ontologyCategories.forEach(category => {
          if (category.terms && Array.isArray(category.terms)) {
            category.terms.forEach(term => {
              // Include ALL terms, not just cleaned names for the filter dropdown
              terms.push({
                id: term.id,
                name: term.name, // Use original name for filtering
                originalName: term.name
              });

              // Include child terms
              if (term.children && Array.isArray(term.children)) {
                term.children.forEach(child => {
                  terms.push({
                    id: child.id,
                    name: child.name, // Use original name
                    originalName: child.name
                  });
                });
              }
            });
          }
        });
      }

      // Remove duplicates by ID
      const uniqueTerms = [];
      const seenIds = new Set();

      terms.forEach(term => {
        if (!seenIds.has(term.id)) {
          seenIds.add(term.id);
          uniqueTerms.push(term);
        }
      });

      uniqueTerms.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });

      console.log('Available annotation terms for filter:', uniqueTerms.map(t => ({id: t.id, name: t.name})));
      return uniqueTerms;
    },

    classifiedCellsCount() {
      if (!this.ontologyCategories || !Array.isArray(this.ontologyCategories)) {
        return 0;
      }

      let classifiedCount = 0;
      try {
        this.ontologyCategories.forEach(category => {
          if (category.terms && Array.isArray(category.terms)) {
            category.terms.forEach(term => {
              classifiedCount += parseInt(term.count) || 0;
              if (term.children && Array.isArray(term.children)) {
                term.children.forEach(child => {
                  classifiedCount += parseInt(child.count) || 0;
                });
              }
            });
          }
        });
      } catch (error) {
        console.error('Error in classifiedCellsCount:', error);
      }
      return classifiedCount;
    },

    classificationCompleteness() {
      if (this.totalWBCCounted === 0) return 0;
      return Math.round((this.classifiedCellsCount / this.totalWBCCounted) * 100);
    },

    showReferenceRanges() {
      // Show reference ranges if at least some cells are classified
      return this.classifiedCellsCount > 0;
    },

    categoriesWithScores() {
      let count = 0;
      this.rbcMorphologyCategories.forEach(category => {
        category.items.forEach(item => {
          if (item.score && item.score > 0) count++;
        });
      });
      return count;
    },

    highestSeverityLevel() {
      let maxScore = 0;
      this.rbcMorphologyCategories.forEach(category => {
        category.items.forEach(item => {
          if (item.score && item.score > maxScore) {
            maxScore = item.score;
          }
        });
      });

      if (maxScore === 0) return 'None';
      if (maxScore === 1) return '1+ (Mild)';
      if (maxScore === 2) return '2+ (Moderate)';
      if (maxScore === 3) return '3+ (Marked)';
      return 'None';
    },

    morphologyCompletionPercentage() {
      let totalItems = 0;
      let scoredItems = 0;

      this.rbcMorphologyCategories.forEach(category => {
        category.items.forEach(item => {
          totalItems++;
          if (item.score) scoredItems++;
        });
      });

      return totalItems > 0 ? Math.round((scoredItems / totalItems) * 100) : 0;
    },

    // Count total RBC annotations (terms starting with 'rbc')
    totalRBCCount() {
      console.log('=== RBC Count Debug ===');
      console.log('rbcCells length:', this.rbcCells ? this.rbcCells.length : 'null');

      if (!this.rbcCells || this.rbcCells.length === 0) {
        console.log('No rbcCells found');
        return 0;
      }

      // Count all RBC annotations with terms starting with 'rbc'
      let rbcCount = this.rbcCells.length;

      console.log('Total RBC annotations with RBC terms:', rbcCount);

      this.rbcCells.forEach((cell, index) => {
        console.log(`RBC Cell ${index}:`, {
          id: cell.id,
          terms: cell.terms
        });
      });

      console.log('Final RBC count:', rbcCount);
      console.log('=== End RBC Count Debug ===');
      return rbcCount;
    },

    // Auto-count PLT from annotations with "platelet-thrombocyte" terms
    autoCalculatedPLTCount() {
      console.log('=== PLT Count Debug ===');
      console.log('pltCells length:', this.pltCells ? this.pltCells.length : 'null');

      if (!this.pltCells || this.pltCells.length === 0) {
        console.log('No pltCells found for PLT count');
        return 0;
      }

      // Count all PLT annotations with platelet-thrombocyte terms
      let pltCount = this.pltCells.length;

      console.log('Total PLT annotations with platelet-thrombocyte terms:', pltCount);

      // For now, return the actual count
      // In a real scenario, this would be the average per field if we knew which field each annotation belonged to
      // The field division should happen when we have field information for each annotation
      console.log(`PLT count: ${pltCount}`);
      console.log('=== End PLT Count Debug ===');

      return pltCount;
    },

    // Current PLT average count (manual override or auto-calculated)
    currentPLTAverageCount() {
      // If user manually set a value, use that
      if (this.pltData.source === 'manual' && this.pltData.averageCount !== null) {
        return this.pltData.averageCount;
      }

      // Otherwise use auto-calculated value
      return this.autoCalculatedPLTCount;
    },

    // RBC Report computed properties
    hasRBCMorphologyData() {
      return Object.values(this.rbcMorphologyScores).some(scoreData =>
        scoreData && scoreData.score && scoreData.score !== '0'
      );
    },

    // Prostate-specific computed properties
    isFirstProstateStep() {
      return this.activeProstateTab === 'scoring';
    },

    isLastProstateStep() {
      return this.activeProstateTab === 'report';
    },

    patternPercentageSum() {
      if (!this.prostateData.percentages) return 0;
      return (this.prostateData.percentages.gleason3 || 0) +
            (this.prostateData.percentages.gleason4 || 0) +
            (this.prostateData.percentages.gleason5 || 0);
    },

    hasRuleViolation() {
      return Math.abs(this.patternPercentageSum - 100) > 1; // Allow 1% tolerance
    },

    hasMinimumMalignantArea() {
      return this.prostateData.totalMalignantArea >= 0.1; // Minimum 0.1 mm²
    }
  },
  watch: {
    // Watch for changes in currentCells data
    currentCells: {
      handler() {
        // Auto-update WBC counts when currentCells data changes
        if (this.activeTab === 'wbc' && this.currentCells) {
          this.$nextTick(() => {
            this.updateCellTypeCounts();
          });
        }

        // Auto-update PLT count when currentCells data changes
        if (this.activeTab === 'plt' && this.currentCells) {
          this.$nextTick(() => {
            console.log('Current cells changed - PLT count updated:', this.autoCalculatedPLTCount);
          });
        }
      },
      deep: true
    },

    // Watch for active tab changes to refresh data
    activeTab: {
      handler(newTab, oldTab) {
        if (newTab === 'annotations') {
          // Load annotations when switching to annotations tab
          console.log('Switching to annotations tab - loading annotations...');
          this.$nextTick(() => {
            this.loadAnnotationsForList();
          });
        } else if (newTab === 'wbc') {
          // Refresh WBC data when switching to WBC tab
          this.$nextTick(() => {
            this.refreshWBCData();
          });
        }
      }
    }
  },
  methods: {
    switchModel(modelId) {
      console.log('Switching model to:', modelId);

      // Reset all critical states
      this.resetComponentState();

      // Set the new model
      this.activeModel = modelId;

      // Reset to default tabs for each model
      if (modelId === 'blood-film') {
        this.activeTab = 'annotations';
        this.initializeBloodFilmData();
      } else if (modelId === 'prostate-gleason') {
        this.activeProstateTab = 'scoring';
        this.initializeProstateData();
      }

      // Load model data
      this.$nextTick(() => {
        this.loadModelData(modelId);
      });
    },

    // Reset all component states
    resetComponentState() {
      console.log('Resetting component state...');

      // Reset loading states for both models
      this.tabLoadingStates = {
        wbc: false,
        rbc: false,
        plt: false
      };

      this.prostateLoadingStates = {
        annotations: false,
        areas: false,
        scoring: false,
        report: false
      };

      this.isLoadingAnnotations = false;
      this.isLoadingProstateAnnotations = false;
      this.isCalculatingAreas = false;
      this.isAutoCalculating = false;

      // Reset UI states
      this.currentSelectedAnnotation = null;
      this.selectedNewTerm = '';
      this.expandedAnnotations = {};
      this.expandedProstateAnnotations = {};

      // Clear any pending operations
      this.annotationLoadingProgress = null;

      // Reset editing states
      Object.keys(this.editingComments).forEach(key => {
        this.editingComments[key] = false;
      });
      Object.keys(this.editingAIComments).forEach(key => {
        this.editingAIComments[key] = false;
      });
      Object.keys(this.editingProstateComments).forEach(key => {
        this.editingProstateComments[key] = false;
      });

      this.currentCommentText = '';
      this.currentAICommentText = '';
      this.currentProstateCommentText = '';
    },

    // Initialize prostate data - ENHANCED
    initializeProstateData() {
      console.log('Initializing prostate data...');

      this.prostateData = {
        pixelSizeUm: 0.25,
        pixelSizeMm: 0.00025,
        areas: {
          gleason3: 0,
          gleason4: 0,
          gleason5: 0,
          stroma: 0,
          benign: 0
        },
        annotationCounts: {
          gleason3: 0,
          gleason4: 0,
          gleason5: 0,
          stroma: 0,
          benign: 0
        },
        percentages: {
          gleason3: 0,
          gleason4: 0,
          gleason5: 0
        },
        totalMalignantArea: 0,
        totalClassifiedArea: 0,
        totalTumorArea: 0,
        tumorPercentage: 0,
        primaryPattern: null,
        secondaryPattern: null,
        tertiaryPattern: null,
        gleasonScore: null,
        gradeGroup: null
      };

      // Reset prostate UI state
      this.selectedProstateAnnotationTerm = '';
      this.filteredProstateAnnotations = [];
      this.allProstateAnnotations = [];
      this.expandedProstateAnnotations = {};
    },

    // Initialize blood film data
    initializeBloodFilmData() {
      console.log('Initializing blood film data...');

      // Reset blood film specific states
      this.selectedAnnotationTerm = '';
      this.filteredAnnotations = [];
      this.allAnnotations = [];

      // Reset RBC morphology scores
      this.rbcMorphologyScores = {};

      // Reset PLT data
      this.pltData = {
        averageCount: null,
        source: 'auto',
        lastModified: null,
        userId: null,
        userName: null
      };
    },

    // Load data for specific model
    async loadModelData(modelId) {
      try {
        switch(modelId) {
          case 'blood-film':
            await this.loadBloodFilmData();
            break;
          case 'prostate-gleason':
            await this.loadProstateGleasonData();
            break;
          default:
            console.warn('Unknown model:', modelId);
        }
      } catch (error) {
        console.error('Error loading model data:', error);
        this.$notify({
          type: 'error',
          text: `Failed to load ${modelId} data`
        });
      }
    },

    // Blood film data loading (your existing initialization)
    async loadBloodFilmData() {
      try {
        await this.loadOntologyData();
        await this.loadCellAnnotations();
        this.updateCellTypeCounts();
        this.setupEventListeners();
      } catch (error) {
        console.error('Error loading blood film data:', error);
        throw error;
      }
    },

    // Prostate Gleason data loading
    async loadProstateGleasonData() {
      try {
        console.log('Loading prostate Gleason data...');

        // Reset prostate data first
        this.initializeProstateData();

        // Load annotations if we're on the annotations tab
        if (this.activeProstateTab === 'scoring') {
          await this.loadProstateAnnotations();
        }

      } catch (error) {
        console.error('Error loading prostate data:', error);
        throw error;
      }
    },

    // Breast cancer data loading
    async loadBreastCancerData() {
      // Initialize breast cancer-specific data
      console.log('Loading breast cancer data...');
      // Add breast cancer-specific initialization here
    },

    // Configuration methods for loading optimization
    setLoadingStrategy(strategy) {
      switch (strategy) {
        case 'single':
          this.loadingConfig.useSingleRequest = true;
          this.loadingConfig.largeBatchSize = 50000;
          this.loadingConfig.skipDelays = true;
          console.log('🚀 Loading strategy: Single request (fastest)');
          break;
        case 'large-batch':
          this.loadingConfig.useSingleRequest = false;
          this.loadingConfig.largeBatchSize = 50000;
          this.loadingConfig.skipDelays = true;
          console.log('📦 Loading strategy: Large batches (50k per batch)');
          break;
        case 'safe-batch':
          this.loadingConfig.useSingleRequest = false;
          this.loadingConfig.largeBatchSize = 10000;
          this.loadingConfig.skipDelays = false;
          console.log('🛡️ Loading strategy: Safe batches (10k per batch with delays)');
          break;
        default:
          console.warn('Unknown loading strategy:', strategy);
      }
    },

    // Clean up WBC cell type names by removing "wbc" prefix and formatting
    cleanWBCCellTypeName(cellTypeName) {
      if (!cellTypeName) return '';

      let cleanName = cellTypeName;

      // Remove "wbc" prefix (e.g., "wbc0-neutrophil" -> "neutrophil")
      if (cleanName.toLowerCase().startsWith('wbc')) {
        // Remove "wbc" and any following digits and hyphen (e.g., "wbc0-", "wbc12-")
        cleanName = cleanName.replace(/^wbc\d*-?/i, '');
      }

      // Capitalize first letter
      if (cleanName.length > 0) {
        cleanName = cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
      }

      return cleanName;
    },

    // Get count of RBC cells by type name (for display purposes)
    getRBCCellCount(morphologyName) {
      if (!this.rbcCells || this.rbcCells.length === 0) {
        return 0;
      }

      let count = 0;
      this.rbcCells.forEach(cell => {
        if (cell.terms && Array.isArray(cell.terms)) {
          const hasMatchingTerm = cell.terms.some(term => {
            const termName = typeof term === 'object' ? term.name : term;
            return termName && termName.toLowerCase() === morphologyName.toLowerCase();
          });
          if (hasMatchingTerm) {
            count++;
          }
        }
      });

      return count;
    },

    // Get reference range for RBC morphology scoring
    getReferenceRange(item, score) {
      if (!item.referenceRanges || !item.referenceRanges[score]) {
        return 'NA';
      }
      return item.referenceRanges[score] || 'NA';
    },

    // Tab navigation
    switchTab(tabId) {
      console.log('Switching to tab:', tabId);

      // Reset problematic states
      this.currentSelectedAnnotation = null;
      this.selectedNewTerm = '';

      this.$nextTick(() => {
        this.activeTab = tabId;
        this.analysisSession.currentStep = tabId;

        // Load data for specific tabs with error handling
        try {
          if (tabId === 'annotations') {
            this.loadAnnotationsForList();
          } else if (tabId === 'wbc') {
            this.refreshWBCData();
          } else if (tabId === 'rbc') {
            this.refreshRBCData();
          } else if (tabId === 'plt') {
            this.refreshPLTData();
          }
          // Report tab doesn't need auto-load
        } catch (error) {
          console.error('Error in tab switch:', error);
          this.$notify({
            type: 'error',
            text: 'Error loading tab data'
          });
        }
      });
    },

    // Refresh WBC differential count data
    async refreshWBCData() {
      console.log('=== Refreshing WBC Data ===');

      this.tabLoadingStates.wbc = true;

      try {
        // Reload annotations to get latest data
        await this.loadCellAnnotations();

        // Update cell type counts
        this.updateCellTypeCounts();

        console.log('WBC data refresh completed');

        // Show success notification
        this.$notify({
          type: 'success',
          text: 'WBC data refreshed successfully'
        });

      } catch (error) {
        console.error('Failed to refresh WBC data:', error);

        this.$notify({
          type: 'error',
          text: 'Failed to refresh WBC data'
        });
      } finally {
        this.tabLoadingStates.wbc = false;
      }
    },

    // Refresh RBC morphology data
    async refreshRBCData() {
      console.log('=== Refreshing RBC Data ===');

      this.tabLoadingStates.rbc = true;

      try {
        // Reload annotations to get latest data
        await this.loadCellAnnotations();

        console.log('RBC data after refresh:', {
          count: this.rbcCells ? this.rbcCells.length : 0,
          sampleCells: this.rbcCells ? this.rbcCells.slice(0, 3) : []
        });

        // Auto-calculate scores if enabled - FIXED: call the correct method
        if (this.rbcCells && this.rbcCells.length > 0) {
          await this.autoCalculateMorphologyScores(false); // false = not manual trigger
        }

        console.log('RBC data refresh completed');

        this.$notify({
          type: 'success',
          text: 'RBC data refreshed successfully'
        });

      } catch (error) {
        console.error('Failed to refresh RBC data:', error);

        this.$notify({
          type: 'error',
          text: 'Failed to refresh RBC data: ' + error.message
        });
      } finally {
        this.tabLoadingStates.rbc = false;
      }
    },

    // Refresh PLT data
    async refreshPLTData() {
      console.log('=== Refreshing PLT Data ===');

      this.tabLoadingStates.plt = true;

      try {
        // Reload annotations to get latest data
        await this.loadCellAnnotations();

        console.log('PLT data after refresh:', {
          count: this.pltCells ? this.pltCells.length : 0,
          autoCalculatedCount: this.autoCalculatedPLTCount,
          sampleCells: this.pltCells ? this.pltCells.slice(0, 3) : []
        });

        console.log('PLT data refresh completed');

        this.$notify({
          type: 'success',
          text: 'PLT data refreshed successfully'
        });

      } catch (error) {
        console.error('Failed to refresh PLT data:', error);

        this.$notify({
          type: 'error',
          text: 'Failed to refresh PLT data'
        });
      } finally {
        this.tabLoadingStates.plt = false;
      }
    },

    // Close panel
    closePanel() {
      this.$emit('close');
    },

    // Navigate to next step
    nextStep() {
      const tabOrder = ['annotations', 'wbc', 'rbc', 'plt', 'report'];
      const currentIndex = tabOrder.indexOf(this.activeTab);

      if (currentIndex < tabOrder.length - 1) {
        const nextTab = tabOrder[currentIndex + 1];
        this.switchTab(nextTab);
      } else {
        // Complete analysis
        console.log('Completing Full Analysis...');
        this.generatePDFReport();
      }
    },

    // Generate PDF Report
    generatePDFReport() {
      try {
        console.log('Generating PDF report...');

        // Update status to 'completed' when user completes full analysis
        this.updateAnalysisStatus('completed');

        // Create a new window/tab for the PDF report
        const reportWindow = window.open('', '_blank', 'width=690,height=302,resizable=no,scrollbars=no');

        if (!reportWindow) {
          this.$notify({
            type: 'error',
            text: 'Unable to open new window. Please allow pop-ups and try again.'
          });
          return;
        }

        // Generate the HTML content for the PDF report
        const reportHTML = this.generateReportHTML();

        // Write the HTML to the new window
        reportWindow.document.write(reportHTML);
        reportWindow.document.close();

        // Focus the new window
        reportWindow.focus();

        // Trigger print dialog after a short delay
        setTimeout(() => {
          reportWindow.print();
        }, 500);

        this.$notify({
          type: 'success',
          text: 'PDF report generated successfully. Print dialog should appear.'
        });

      } catch (error) {
        console.error('Error generating PDF report:', error);
        this.$notify({
          type: 'error',
          text: 'Failed to generate PDF report. Please try again.'
        });
      }
    },

    // Generate HTML content for the PDF report
    generateReportHTML() {
      const currentDate = new Date().toLocaleDateString();
      const currentTime = new Date().toLocaleTimeString();

      // Get WBC data for report
      const wbcData = this.getWBCReportData();

      // Get RBC data for report
      const rbcData = this.getRBCReportData();

      // Get PLT data for report
      const pltData = this.getPLTReportData();

      // Get AI comments for report
      const aiComments = this.getAICommentsForReport();

      return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Peripheral Blood Film Analysis Report</title>
  <style>
    @page {
      size: A4 landscape;
      margin: 20px;
    }
    body {
      font-family: Arial, sans-serif;
      font-size: 9pt;
      line-height: 1.3;
      margin: 0;
      padding: 0;
      width: 100%;
      min-height: 100vh;
      overflow: visible;
    }
    .report-container {
      width: 100%;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      padding: 10px;
      box-sizing: border-box;
    }
    .ip-message-section {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 15px;
      margin-bottom: 20px;
      min-height: 200px;
    }
    .ip-message-box {
      padding: 8px;
      border: 1px solid #ddd;
      border-radius: 4px;
      background-color: #f9f9f9;
      overflow: visible;
    }
    .ip-message-title {
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 10pt;
      color: #333;
      border-bottom: 1px solid #ddd;
      padding-bottom: 4px;
    }
    .ip-message-content {
      font-size: 8pt;
      line-height: 1.4;
      overflow: visible;
      word-wrap: break-word;
    }
    .footer-section {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 15px;
      margin-top: 20px;
      min-height: 100px;
    }
    .footer-box {
      padding: 8px;
      border: 1px solid #ddd;
      border-radius: 4px;
      background-color: #f9f9f9;
      overflow: visible;
    }
    .footer-title {
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 10pt;
      color: #333;
      border-bottom: 1px solid #ddd;
      padding-bottom: 4px;
    }
    .footer-content {
      font-size: 8pt;
      line-height: 1.4;
      overflow: visible;
      word-wrap: break-word;
    }
    /* Table styling for RBC morphology grading */
    .morphology-table {
      font-size: 8pt;
      line-height: 1.4;
      margin: 5px 0;
    }
    @media print {
      body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: auto;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        font-size: 8pt;
      }
      .report-container {
        width: 100%;
        height: auto;
        padding: 10px;
      }
      .ip-message-section {
        grid-template-columns: 1fr 1fr 1fr;
        gap: 10px;
        page-break-inside: avoid;
      }
      .footer-section {
        grid-template-columns: 1fr 1fr;
        gap: 10px;
        page-break-inside: avoid;
      }
      .ip-message-box, .footer-box {
        border: 1px solid #999;
        background-color: #f9f9f9 !important;
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <div class="report-container">
    <!-- Report Header -->
    <div style="text-align: center; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #333;">
      <h1 style="margin: 0; font-size: 16pt; color: #333;">Peripheral Blood Film Analysis Report</h1>
      <p style="margin: 5px 0 0 0; font-size: 10pt; color: #666;">Generated on ${currentDate} at ${currentTime}</p>
      <div style="margin-top: 10px; font-size: 9pt; color: #555;">
        <div style="margin-bottom: 3px;"><strong>Image ID:</strong> ${this.image?.id || 'N/A'}</div>
        <div><strong>Filename:</strong> ${this.image?.originalFilename || this.image?.filename || 'N/A'}</div>
        <div><strong>User:</strong> ${this.currentUser?.name || this.currentUser?.username || 'N/A'}</div>
      </div>
    </div>

    <!-- IP Messages Section -->
    <div class="ip-message-section">
      <div class="ip-message-box">
        <div class="ip-message-title">WBC IP Message</div>
        <div class="ip-message-content">
          ${wbcData.content}
        </div>
      </div>
      <div class="ip-message-box">
        <div class="ip-message-title">RBC IP Message</div>
        <div class="ip-message-content">
          ${rbcData.content}
        </div>
      </div>
      <div class="ip-message-box">
        <div class="ip-message-title">PLT IP Message</div>
        <div class="ip-message-content">
          ${pltData.content}
        </div>
      </div>
    </div>

    <!-- Footer Section -->
    <div class="footer-section">
      <div class="footer-box">
        <div class="footer-title">Error/Rule Comments:</div>
        <div class="footer-content">${aiComments.errorRuleComments || 'No error/rule comments'}</div>
      </div>
      <div class="footer-box">
        <div class="footer-title">Action Messages:</div>
        <div class="footer-content">${aiComments.actionMessages || 'No action messages'}</div>
      </div>
    </div>
  </div>
</body>
</html>`;
    },

    // Get WBC data formatted for report (NEW LOGIC)
    getWBCReportData() {
      let content = '';
      let ipMessages = [];

      // Use the same sorting and filtering logic as the WBC tab
      const sortedTerms = [];

      if (this.ontologyCategories && this.ontologyCategories.length > 0) {
        this.ontologyCategories.forEach(category => {
          category.terms.forEach(term => {
            // Skip platelet-thrombocyte terms
            if (term.name && (term.name.toLowerCase().includes('platelet') ||
                term.name.toLowerCase().includes('thrombocyte'))) {
              return;
            }

            // Include terms with counts > 0
            if (term.count > 0) {
              const percentage = parseFloat(term.percentage) || 0;
              sortedTerms.push({
                name: term.name,
                count: term.count,
                percentage: percentage,
                percentageText: term.percentage
              });

              // Check for IP Message conditions
              if (term.name.toLowerCase().includes('monocyte') && percentage > 10) {
                ipMessages.push(`Monocytosis (Detected: ${percentage.toFixed(1)}%)`);
              }
              if (term.name.toLowerCase().includes('basophil') && percentage > 3) {
                ipMessages.push(`Basophilia (Detected: ${percentage.toFixed(1)}%)`);
              }
              if (term.name.toLowerCase().includes('neutrophil') && percentage > 75) {
                ipMessages.push('Neutrophilia');
              }
              if (term.name.toLowerCase().includes('lymphocyte') && percentage > 45) {
                ipMessages.push('Lymphocytosis');
              }

              // Add child terms if they exist and have counts
              if (term.children && term.children.length > 0) {
                term.children.forEach(child => {
                  if (child.count > 0) {
                    const childPercentage = parseFloat(child.percentage) || 0;
                    sortedTerms.push({
                      name: child.name,
                      count: child.count,
                      percentage: childPercentage,
                      percentageText: child.percentage
                    });
                  }
                });
              }
            }
          });
        });
      }

      // Build content according to new format
      if (ipMessages.length > 0) {
        ipMessages.forEach(msg => {
          content += `${msg}<br>`;
        });
        content += '<br>';
      }

      content += 'WBC Differential:<br>';

      // Sort by percentage (highest first)
      sortedTerms.sort((a, b) => b.percentage - a.percentage);

      // Generate differential content
      sortedTerms.forEach(term => {
        content += `- ${term.name}: ${term.percentageText}%<br>`;
      });

      // Add WBC Note and AI-Assisted Note
      const wbcComment = this.tabComments.wbc || '';
      if (wbcComment.trim()) {
        content += '<br>WBC Note:<br>';
        content += `${wbcComment.trim()}<br>`;
      }

      const wbcAIComment = this.getWBCAIComment();
      if (wbcAIComment.trim()) {
        content += '<br>WBC AI-Assisted Note:<br>';
        content += `${wbcAIComment.trim()}`;
      }

      return {
        content: content || 'No WBC data available'
      };
    },

    // Get RBC data formatted for report (NEW LOGIC)
    getRBCReportData() {
      let content = '';
      let ipMessages = [];
      let morphologyGrading = [];

      // Use the actual RBC morphology scores from the RBC tab
      if (this.rbcMorphologyScores && Object.keys(this.rbcMorphologyScores).length > 0) {
        // Process each morphology type that has a score
        Object.keys(this.rbcMorphologyScores).forEach(morphologyName => {
          const scoreData = this.rbcMorphologyScores[morphologyName];

          if (scoreData && scoreData.score && scoreData.score !== '0') {
            // Find the display name from the morphology categories
            let displayName = this.getRBCDisplayName(morphologyName);
            const percentage = scoreData.percentage || 0;
            const grade = scoreData.score;
            const count = scoreData.count || this.getRBCCellCount(morphologyName);

            // Check for IP Message conditions based on item type and score
            const itemKey = morphologyName.toLowerCase();

            // Schistocytes - any presence is significant
            if (itemKey.includes('schistocyte') && grade !== '0') {
              ipMessages.push(`Schistocytes present (${count} cells, ${percentage.toFixed(1)}%)`);
            }

            // Spherocytes - moderate to severe
            if (itemKey.includes('spherocyte') && (grade === '2' || grade === '3')) {
              ipMessages.push(`Significant spherocytosis (${count} cells, ${percentage.toFixed(1)}%)`);
            }

            // Nucleated RBCs - any presence is significant
            if ((itemKey.includes('erythroblast') || itemKey.includes('nucleated')) && grade !== '0') {
              ipMessages.push(`Nucleated RBCs present (${count} cells, ${percentage.toFixed(1)}%)`);
            }

            // Other abnormalities (≥1+)
            if (!itemKey.includes('schistocyte') && !itemKey.includes('spherocyte') &&
                !itemKey.includes('erythroblast') && !itemKey.includes('nucleated') &&
                grade !== '0') {
              ipMessages.push(`${displayName} ${grade} (${count} cells, ${percentage.toFixed(1)}%)`);
            }

            // Add to morphology grading table for report
            morphologyGrading.push({
              abnormality: displayName,
              count: count,
              percentage: percentage,
              grade: grade,
              threshold: this.getRBCThreshold(morphologyName),
              clinicalNote: this.getRBCClinicalNote(morphologyName, grade)
            });
          }
        });
      }

      // Build content according to new format
      if (ipMessages.length > 0) {
        ipMessages.forEach(msg => {
          content += `${msg}<br>`;
        });
        content += '<br>';
      }

      if (morphologyGrading.length > 0) {
        content += 'RBC Morphology Grading:<br>';
        content += '<div class="morphology-table">';
        morphologyGrading.forEach(item => {
          content += `<div class="morphology-table">${item.abnormality}: ${item.grade} (${item.count} cells, ${item.percentage.toFixed(1)}%)</div>`;
        });
        content += '</div><br>';
      }

      // Add total RBC count
      const totalRBCs = this.getTotalRBCCount();
      content += `Total RBCs analysed: ${totalRBCs}<br><br>`;

      // Add RBC Note and AI-Assisted Note
      const rbcComment = this.tabComments.rbc || '';
      if (rbcComment.trim()) {
        content += 'RBC Note:<br>';
        content += `${rbcComment.trim()}<br>`;
      }

      const rbcAIComment = this.getRBCAIComment();
      if (rbcAIComment.trim()) {
        content += '<br>RBC AI-Assisted Note:<br>';
        content += `${rbcAIComment.trim()}`;
      }

      return {
        content: content || 'No significant RBC abnormalities detected'
      };
    },

    // Get PLT data formatted for report (NEW LOGIC)
    getPLTReportData() {
      const averageCount = this.currentPLTAverageCount || 0;
      const estimateValue = averageCount * 10;
      let content = '';
      let ipMessages = [];

      // Check for IP Message conditions
      if (estimateValue > 450) {
        ipMessages.push(`Thrombocytosis (${estimateValue}×10³/μL)`);
      }
      if (estimateValue < 150) {
        ipMessages.push(`Thrombocytopenia (${estimateValue}×10³/μL)`);
      }

      // Check for giant platelets
      const giantPlatelets = this.getGiantPlateletCount();
      if (giantPlatelets > 0) {
        ipMessages.push(`Giant platelets present (${giantPlatelets})`);
      }

      // Build content according to new format
      if (ipMessages.length > 0) {

        ipMessages.forEach(msg => {
          content += `${msg}<br>`;
        });
        content += '<br>';
      }

      /*content += 'Platelet Estimate:<br>';
      content += `- Average per field: ${averageCount}<br>`;
      content += `- Conversion factor: ×10<br>`;
      content += `- Final estimate: ${estimateValue}×10³/μL<br>`;*/

      // Add PLT Note and AI-Assisted Note
      const pltComment = this.tabComments.plt || '';
      if (pltComment.trim()) {
        content += '<br>Platelet Note:<br>';
        content += `${pltComment.trim()}<br>`;
      }

      const pltAIComment = this.getPLTAIComment();
      if (pltAIComment.trim()) {
        content += '<br>Platelet AI-Assisted Note:<br>';
        content += `${pltAIComment.trim()}`;
      }

      return {
        content
      };
    },

    // Get comments formatted for report
    getCommentsForReport() {
      const comments = [];

      this.analysisTabs.forEach(tab => {
        if (this.tabComments[tab.id] && this.tabComments[tab.id].trim()) {
          comments.push({
            tabName: tab.name,
            text: this.tabComments[tab.id]
          });
        }
      });

      return comments;
    },

    // Get AI Comments for PDF Report
    getAICommentsForReport() {
      return {
        errorRuleComments: this.aiComments.errorRule || '',
        actionMessages: this.aiComments.actionMessages || ''
      };
    },

    // Get WBC AI Comment
    getWBCAIComment() {
      // Return WBC AI-assisted note from WBC comment metadata
      const wbcMetadata = this.commentMetadata.wbc || {};
      return wbcMetadata.aiAssistedNote || '';
    },

    // Get RBC AI Comment
    getRBCAIComment() {
      // Return RBC AI-assisted note from RBC comment metadata
      const rbcMetadata = this.commentMetadata.rbc || {};
      return rbcMetadata.aiAssistedNote || '';
    },

    // Get PLT AI Comment
    getPLTAIComment() {
      // Return PLT AI-assisted note from PLT comment metadata
      const pltMetadata = this.commentMetadata.plt || {};
      return pltMetadata.aiAssistedNote || '';
    },

    // Get RBC Threshold for grading table
    getRBCThreshold(itemName) {
      const thresholds = {
        'rbc1-helmet-schistocyte': '1-2% = 1+, 3-6% = 2+, >6% = 3+',
        'rbc2-ovalocyte': '15-61% = 1+, >61% = 2+',
        'rbc3-spur-acanthocyte': '15-61% = 1+, >61% = 2+',
        'rbc4-teardrop': '15-61% = 1+, >61% = 2+',
        'rbc5-burr-echinocyte': '15-61% = 1+, >61% = 2+',
        'rbc6-sickle': '3-6% = 1+, >6% = 2+',
        'rbc8-pencil-elliptocyte': '15-61% = 1+, >61% = 2+',
        'rbc9-spherocyte': '15-61% = 1+, >61% = 2+',
        'rbc10-stomatocyte': '15-61% = 1+, >61% = 2+',
        'rbc11-target': '15-61% = 1+, >61% = 2+',
        'rbc12-hypochromia': '33-61% = 1+, >61% = 2+',
        'rbc13-macrocyte': '33-61% = 1+, >61% = 2+',
        'rbc14-microcyte': '33-61% = 1+, >61% = 2+',
        'rbc15-erythroblast': 'Any = 1+, >5/100 RBCs = 2+, >10/100 RBCs = 3+'
      };

      return thresholds[itemName] || 'Variable thresholds';
    },

    // Get RBC Clinical Note for grading table
    getRBCClinicalNote(itemName, grade) {
      const clinicalNotes = {
        'rbc1-helmet-schistocyte': {
          '1+': 'Mild fragmentation',
          '2+': 'DIC/TMA risk',
          '3+': 'Severe (urgent action)'
        },
        'rbc2-ovalocyte': {
          '1+': 'Nutritional deficiency',
          '2+': 'Significant (hereditary ovalocytosis)'
        },
        'rbc3-spur-acanthocyte': {
          '1+': 'Liver disease',
          '2+': 'Neuroacanthocytosis'
        },
        'rbc4-teardrop': {
          '1+': 'Early myelofibrosis',
          '2+': 'Advanced marrow fibrosis'
        },
        'rbc5-burr-echinocyte': {
          '1+': 'Artifact/uremia',
          '2+': 'Pathologic burr cells'
        },
        'rbc6-sickle': {
          '1+': 'Sickle cell trait',
          '2+': 'Sickle cell disease'
        },
        'rbc8-pencil-elliptocyte': {
          '1+': 'Iron deficiency',
          '2+': 'Hereditary elliptocytosis'
        },
        'rbc9-spherocyte': {
          '1+': 'Immune hemolysis',
          '2+': 'HS or severe AIHA'
        },
        'rbc10-stomatocyte': {
          '1+': 'Alcohol effect',
          '2+': 'Rh deficiency'
        },
        'rbc11-target': {
          '1+': 'Hemoglobinopathy',
          '2+': 'Thalassemia major'
        },
        'rbc12-hypochromia': {
          '1+': 'Mild iron deficiency',
          '2+': 'Severe deficiency/thalassemia'
        },
        'rbc13-macrocyte': {
          '1+': 'B12/folate deficiency',
          '2+': 'MDS/erythroleukemia'
        },
        'rbc14-microcyte': {
          '1+': 'Early iron deficiency',
          '2+': 'Thalassemia trait'
        },
        'rbc15-erythroblast': {
          '1+': 'Stress erythropoiesis',
          '2+': 'Marrow infiltration',
          '3+': 'Extreme demand (e.g., hemorrhage)'
        }
      };

      const itemNotes = clinicalNotes[itemName];
      return itemNotes ? (itemNotes[grade] || 'Clinical correlation recommended') : 'Clinical correlation recommended';
    },

    // Get Giant Platelet Count (simplified for now)
    getGiantPlateletCount() {
      // This would need to be implemented based on annotation data
      // For now, return 0 as placeholder
      return 0;
    },

    // Get RBC Display Name for Report
    getRBCDisplayName(morphologyName) {
      // Convert RBC annotation types to medical terms based on official document
      const displayNames = {
        // Official RBC annotation types from PDF document
        'rbc0-normal': 'Normal RBC',
        'rbc1-helmet-schistocyte': 'Schistocyte',
        'rbc2-ovalocyte': 'Ovalocyte',
        'rbc3-spur-acanthocyte': 'Acanthocyte',
        'rbc4-teardrop': 'Dacrocyte',
        'rbc5-burr-echinocyte': 'Echinocyte',
        'rbc6-sickle': 'Drepanocyte',
        'rbc8-pencil-elliptocyte': 'Elliptocyte',
        'rbc9-spherocyte': 'Spherocyte',
        'rbc10-stomatocyte': 'Stomatocyte',
        'rbc11-target': 'Codocyte',
        'rbc12-hypochromia': 'Hypochromia',
        'rbc13-macrocyte': 'Macrocyte',
        'rbc14-microcyte': 'Microcyte',
        'rbc15-erythroblast': 'Nucleated RBC',

        // Current morphology category names (alternative format)
        'rbc_elliptocyte': 'Elliptocyte',
        'rbc_ovalocyte': 'Ovalocyte',
        'rbc_teardrop_cell': 'Dacrocyte',
        'rbc_schistocyte': 'Schistocyte',
        'rbc_sickle_cell': 'Drepanocyte',
        'rbc_spherocyte': 'Spherocyte',
        'rbc_bite_cell': 'Bite Cell',
        'rbc_blister_cell': 'Blister Cell',
        'rbc_irregularly_contracted': 'Irregularly Contracted Cell',
        'rbc_acanthocyte': 'Acanthocyte',
        'rbc_echinocyte': 'Echinocyte',
        'rbc_stomatocyte': 'Stomatocyte',
        'rbc_target_cell': 'Codocyte',
        'rbc_hypochromia': 'Hypochromia',
        'rbc_hyperchromia': 'Hyperchromia',
        'rbc_polychromasia': 'Polychromasia',
        'rbc_anisocytosis': 'Anisocytosis',
        'rbc_microcytosis': 'Microcyte',
        'rbc_macrocytosis': 'Macrocyte',
        'rbc_poikilocytosis': 'Poikilocytosis',
        'rbc_rouleaux': 'Rouleaux',
        'rbc_agglutination': 'Agglutination',
        'rbc_dimorphic_population': 'Dimorphic Population',
        'rbc_nucleated_rbc': 'Nucleated RBC',
        'rbc_basophilic_stippling': 'Basophilic Stippling',
        'rbc_pappenheimer_bodies': 'Pappenheimer Bodies',
        'rbc_howell_jolly_bodies': 'Howell-Jolly Bodies',
        'rbc_heinz_bodies': 'Heinz Bodies',
        'rbc_malaria_parasites': 'Malaria Parasites'
      };

      return displayNames[morphologyName] || morphologyName;
    },

    // Generate WBC IP Message
    generateWBCIPMessage() {
      // Basic IP message based on WBC differential results
      const abnormalFindings = [];

      if (this.ontologyCategories) {
        this.ontologyCategories.forEach(category => {
          category.terms.forEach(term => {
            if (term.count > 0) {
              const percentage = parseFloat(term.percentage);

              // Check for abnormal ranges (simplified logic)
              if (term.name.toLowerCase().includes('monocyte') && percentage > 10) {
                abnormalFindings.push('Monocytosis');
              }
              if (term.name.toLowerCase().includes('basophil') && percentage > 2) {
                abnormalFindings.push('Basophilia');
              }
              if (term.name.toLowerCase().includes('neutrophil') && percentage > 80) {
                abnormalFindings.push('Neutrophilia');
              }
              if (term.name.toLowerCase().includes('lymphocyte') && percentage > 40) {
                abnormalFindings.push('Lymphocytosis');
              }
            }
          });
        });
      }

      return abnormalFindings.length > 0 ? abnormalFindings.join('<br>') : 'No significant abnormalities detected';
    },

    // Generate RBC IP Message
    generateRBCIPMessage() {
      const abnormalFindings = [];

      if (this.rbcMorphologyScores) {
        Object.keys(this.rbcMorphologyScores).forEach(itemName => {
          const score = this.rbcMorphologyScores[itemName];
          if (score && score.score && ['2+', '3+'].includes(score.score)) {
            abnormalFindings.push(`${itemName}: ${score.score}`);
          }
        });
      }

      return abnormalFindings.length > 0 ? abnormalFindings.join('<br>') : 'No significant morphological abnormalities';
    },

    // Generate PLT IP Message
    generatePLTIPMessage() {
      const estimate = this.currentPLTAverageCount * 10;

      if (estimate < 100) {
        return `Thrombocytopenia (${estimate}×10³/μL)`;
      } else if (estimate > 450) {
        return `Thrombocytosis (${estimate}×10³/μL)`;
      } else {
        return 'Platelet count within normal range';
      }
    },

    // Generate error/rule comments
    generateErrorRuleComments() {
      const comments = [];

      // Add tab-specific comments
      if (this.tabComments.wbc && this.tabComments.wbc.trim()) {
        comments.push(`(WBC) - ${this.tabComments.wbc.trim()}`);
      }

      if (this.tabComments.rbc && this.tabComments.rbc.trim()) {
        comments.push(`(RBC) - ${this.tabComments.rbc.trim()}`);
      }

      if (this.tabComments.plt && this.tabComments.plt.trim()) {
        comments.push(`(PLT) - ${this.tabComments.plt.trim()}`);
      }

      return comments.length > 0 ? comments.join('<br>') : '';
    },

    // Generate action messages
    generateActionMessages() {
      const actions = [];

      // Add report tab comments as action messages
      if (this.tabComments.report && this.tabComments.report.trim()) {
        actions.push(this.tabComments.report.trim());
      }

      // Suggest actions based on findings
      if (this.currentPLTAverageCount * 10 < 100) {
        actions.push('Recommend platelet count confirmation');
      }

      // Check for significant morphology scores
      if (this.rbcMorphologyScores) {
        Object.values(this.rbcMorphologyScores).forEach(score => {
          if (score && score.score === '3+') {
            actions.push('Further hematological evaluation recommended');
          }
        });
      }

      return actions.length > 0 ? actions.join('<br>') : '';
    },

    // View switching (Cells/Slide)
    switchView(view) {
      this.currentView = view;
    },

    // Cell classification
    classifyCell(cell) {
      this.classificationModal.selectedCell = cell;
      this.classificationModal.isOpen = true;
    },

    // Close classification modal
    closeClassificationModal() {
      this.classificationModal.isOpen = false;
      this.classificationModal.selectedCell = null;
    },

    // Handle cell classification from modal
    async handleCellClassification({cellId, oldClassification, newClassification}) {
      try {
        // Update status to 'in_progress' when user starts editing
        if (this.analysisSession.status === 'pending_review') {
          this.updateAnalysisStatus('in_progress');
        }

        // Update local cell data
        const cell = this.currentCells.find(c => c.id === cellId);
        if (cell) {
          cell.userClassification = newClassification;
        }

        // Update annotation in Cytomine
        await this.updateCellAnnotation(cellId, oldClassification, newClassification);

        // Update counts
        this.updateCellTypeCounts();

        this.$notify({
          type: 'success',
          text: `Cell #${cellId} reclassified as ${newClassification}`
        });

      } catch (error) {
        console.error('Failed to classify cell:', error);
        this.$notify({
          type: 'error',
          text: 'Failed to update cell classification'
        });
        throw error;
      }
    },

    // Update cell type counts after classification changes
    updateCellTypeCounts() {
      try {
        const totalCells = this.currentCells ? this.currentCells.length : 0;
        const counts = {};

        console.log('=== Updating cell counts ===');
        console.log('Total cells:', totalCells);
        console.log('Current cells exists:', !!this.currentCells);
        console.log('Ontology categories exists:', !!this.ontologyCategories);
        console.log('Ontology categories length:', this.ontologyCategories?.length || 0);

        // Initialize ontology categories if they don't exist yet
        if (!this.ontologyCategories || !Array.isArray(this.ontologyCategories)) {
          console.warn('Missing ontology categories for updateCellTypeCounts, loading mock data');
          this.loadMockOntologyData();
        }

        if (!this.currentCells) {
          console.warn('No currentCells available yet');
          return;
        }

      // Count cells by type
      this.currentCells.forEach((cell, index) => {
        const type = cell.userClassification || cell.aiClassification || 'Unclassified';
        counts[type] = (counts[type] || 0) + 1;
        console.log(`Cell ${index}: ID=${cell.id}, aiClassification=${cell.aiClassification}, userClassification=${cell.userClassification}, final type=${type}`);
      });

      console.log('Final cell type counts:', counts);

      // Update total WBC count
      this.totalWBCCounted = totalCells;

      // Update ontology categories with new counts
      this.ontologyCategories.forEach(category => {
        console.log(`Processing category: ${category.name}`);
        category.terms.forEach(term => {
          const count = counts[term.name] || 0;
          const oldCount = term.count;
          term.count = count;
          term.percentage = totalCells > 0 ? ((count / totalCells) * 100).toFixed(1) : '0.0';

          console.log(`  Term: ${term.name}, old count: ${oldCount}, new count: ${count}, percentage: ${term.percentage}%`);

          // Update hierarchical children if present
          if (term.children) {
            let totalChildCount = 0;
            term.children.forEach(child => {
              const childCount = counts[child.name] || 0;
              child.count = childCount;
              child.percentage = totalCells > 0 ? ((childCount / totalCells) * 100).toFixed(1) : '0.0';
              totalChildCount += childCount;
            });
            // Update parent count to sum of children
            term.count = totalChildCount;
            term.percentage = totalCells > 0 ? ((totalChildCount / totalCells) * 100).toFixed(1) : '0.0';
          }
        });
      });

      this.totalWBCCounted = totalCells;
      console.log('=== Cell counts update completed ===');

      } catch (error) {
        console.error('Error in updateCellTypeCounts:', error);
      }
    },

    // RBC region selection
    selectRegion(regionId) {
      this.rbcRegions.forEach(region => {
        region.active = region.id === regionId;
      });
    },

    // Status formatting
    formatStatus(status) {
      const statusMap = {
        'pending_review': 'Pending Review',
        'in_progress': 'In Progress',
        'completed': 'Completed'
      };
      return statusMap[status] || status;
    },

    // Update analysis status and save to database
    updateAnalysisStatus(newStatus) {
      const oldStatus = this.analysisSession.status;
      this.analysisSession.status = newStatus;

      console.log(`Analysis status updated: ${oldStatus} -> ${newStatus}`);

      // Save status to database if needed
      try {
        this.saveAnalysisStatusToDatabase();
      } catch (error) {
        console.error('Failed to save analysis status:', error);
      }
    },

    // Save analysis status to database
    async saveAnalysisStatusToDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Missing project or image ID for status storage');
          return;
        }

        const propertyKey = `bloodfilm_analysis_status_${imageId}`;
        const propertyValue = JSON.stringify({
          status: this.analysisSession.status,
          runId: this.analysisSession.runId,
          currentStep: this.analysisSession.currentStep,
          timestamp: new Date().toISOString(),
          userId: this.currentUser?.id || 'unknown',
          userName: this.currentUser?.name || 'Unknown User'
        });

        const property = new Property({
          object: this.project,
          key: propertyKey,
          value: propertyValue
        });

        await property.save();
        console.log(`Analysis status saved to database: ${this.analysisSession.status}`);

      } catch (error) {
        console.error('Error saving analysis status to database:', error);
        throw error;
      }
    },

    // Load analysis status from database
    async loadAnalysisStatusFromDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Missing project or image ID for status loading');
          return;
        }

        const propertyCollection = new PropertyCollection({
          object: this.project
        });

        await propertyCollection.fetchAll();

        const properties = propertyCollection.array || [];
        const statusKey = `bloodfilm_analysis_status_${imageId}`;

        const statusProperty = properties.find(p => p.key === statusKey);

        if (statusProperty) {
          const statusData = JSON.parse(statusProperty.value);
          this.analysisSession.status = statusData.status;
          this.analysisSession.runId = statusData.runId || this.analysisSession.runId;
          this.analysisSession.currentStep = statusData.currentStep || this.analysisSession.currentStep;

          console.log(`Analysis status loaded from database: ${this.analysisSession.status}`);
        }

      } catch (error) {
        console.error('Error loading analysis status from database:', error);
      }
    },

    // Load ontology data and transform for blood film analysis
    async loadOntologyData() {
      try {
        await this.$store.dispatch('currentProject/fetchOntology');
        this.transformOntologyData();
      } catch (error) {
        console.error('Failed to load ontology:', error);
        // Fallback to mock data for development
        this.loadMockOntologyData();
      }
    },

    // Transform Cytomine ontology to blood film structure
    transformOntologyData() {
      const ontology = this.ontology;
      console.log('Transforming ontology data:', ontology);

      if (!ontology) {
        console.log('No ontology available, using mock data');
        this.loadMockOntologyData();
        return;
      }

      // allTerms is computed from ontology automatically
      console.log('All terms loaded:', this.allTerms.length);

      this.ontologyCategories = [];

      try {
        // Handle different ontology response structures
        let categories = [];

        // Check if ontology is an array (list of terms)
        if (Array.isArray(ontology)) {
          console.log('Ontology is array format');
          categories = [{
            name: 'Terms',
            children: ontology
          }];
        }
        // Check if ontology has children property (hierarchical format)
        else if (ontology.children && Array.isArray(ontology.children)) {
          console.log('Ontology has children property');
          categories = ontology.children;
        }
        // Check if ontology has terms property
        else if (ontology.terms && Array.isArray(ontology.terms)) {
          console.log('Ontology has terms property');
          categories = [{
            name: 'Terms',
            children: ontology.terms
          }];
        }
        // Single ontology object with direct properties
        else if (ontology.id && ontology.name) {
          console.log('Single ontology object format');
          categories = [ontology];
        }
        else {
          console.log('Unrecognized ontology format:', Object.keys(ontology));
          this.loadMockOntologyData();
          return;
        }

        // Transform all categories (not just WBC/Others)
        categories.forEach(category => {
          if (category && (category.children || category.terms)) {
            const categoryTerms = this.transformCategoryTerms(category);
            if (categoryTerms.length > 0) {
              this.ontologyCategories.push({
                name: category.name || 'Unnamed Category',
                terms: categoryTerms
              });
            }
          }
        });

        // If no categories found, use mock data
        if (this.ontologyCategories.length === 0) {
          console.log('No valid categories found in ontology, using mock data');
          this.loadMockOntologyData();
        } else {
          console.log('Loaded ontology categories:', this.ontologyCategories.map(cat => `${cat.name} (${cat.terms.length} terms)`));
        }

      } catch (error) {
        console.error('Error transforming ontology data:', error);
        this.loadMockOntologyData();
      }
    },

    // Transform category terms to blood film structure
    transformCategoryTerms(category) {
      const terms = [];

      // Get terms from either children or terms property
      let termsList = category.children || category.terms || [];

      // If category itself is a term (has id and name), treat it as a single term
      if (!termsList.length && category.id && category.name) {
        termsList = [category];
      }

      termsList.forEach(term => {
        if (term && term.id && term.name) {
          // Skip RBC terms (terms that start with "rbc") from WBC ontology
          /*if (term.name.toLowerCase().startsWith('rbc')) {
            console.log(`Filtering out RBC term from WBC ontology: ${term.name}`);
            return;
          }*/

          const transformedTerm = {
            id: term.id,
            name: term.name,
            count: 0,
            percentage: '0.0',
            isSupported: !term.name.includes('*'), // Mark unsupported if name has *
            children: null
          };

          // Handle hierarchical terms (like Lymphocytes)
          const childTerms = term.children || term.terms || [];
          if (childTerms.length > 0) {
            transformedTerm.children = childTerms
              .filter(child => {
                if (!child || !child.id || !child.name) return false;
                // Filter out RBC terms from child terms too
                /*if (child.name.toLowerCase().startsWith('rbc')) {
                  console.log(`Filtering out RBC child term from WBC ontology: ${child.name}`);
                  return false;
                }*/
                return true;
              })
              .map(child => ({
                id: child.id,
                name: child.name,
                count: 0,
                percentage: '0.0',
                isSupported: !child.name.includes('*')
              }));
          }

          terms.push(transformedTerm);
        }
      });

      return terms;
    },

    // Fallback mock data for development/testing
    loadMockOntologyData() {
      this.ontologyCategories = [
        {
          name: 'WBC',
          terms: [
            { id: 1, name: 'Neutrophil', count: 139, percentage: '69.5', isSupported: true },
            {
              id: 2,
              name: 'Lymphocytes',
              count: 38,
              percentage: '19.0',
              isSupported: true,
              children: [
                { id: 21, name: 'Lymphocyte', count: 19, percentage: '9.5', isSupported: true },
                { id: 22, name: 'Large Granular Lymphocyte', count: 2, percentage: '1.0', isSupported: true },
                { id: 23, name: 'Reactive Lymphocyte', count: 17, percentage: '8.5', isSupported: true }
              ]
            },
            { id: 3, name: 'Monocyte', count: 7, percentage: '3.5', isSupported: true },
            { id: 4, name: 'Eosinophil', count: 8, percentage: '4.0', isSupported: true },
            { id: 5, name: 'Basophil', count: 1, percentage: '0.5', isSupported: true },
            { id: 6, name: 'Metamyelocyte', count: 1, percentage: '0.5', isSupported: true },
            { id: 7, name: 'Blast', count: 6, percentage: '3.0', isSupported: true }
          ]
        },
        {
          name: 'Others',
          terms: [
            { id: 8, name: 'Smudge Cell*', count: 5, percentage: '-', isSupported: false },
            { id: 9, name: 'Artifact*', count: 3, percentage: '-', isSupported: false },
            { id: 10, name: 'Giant Platelet*', count: 7, percentage: '-', isSupported: false },
            { id: 11, name: 'Unclassified*', count: 10, percentage: '-', isSupported: false }
          ]
        }
      ];

      this.totalWBCCounted = 200;
    },

    // Cytomine annotation integration methods
    async updateCellAnnotation(cellId, oldClassification, newClassification) {
      try {
        // Get annotation
        const annotation = await Annotation.fetch(cellId);

        if (!annotation) {
          throw new Error(`Annotation ${cellId} not found`);
        }

        // Find old and new term IDs
        const oldTermId = this.getTermIdByName(oldClassification);
        const newTermId = this.getTermIdByName(newClassification);

        // Remove old term association if exists
        if (oldTermId) {
          try {
            await AnnotationTerm.delete(cellId, oldTermId, this.currentUser.id);
          } catch (error) {
            console.warn('Failed to remove old term association:', error);
          }
        }

        // Add new term association
        if (newTermId) {
          const annotationTerm = new AnnotationTerm({
            annotation: cellId,
            term: newTermId,
            user: this.currentUser.id
          });
          await annotationTerm.save();
        }

        // Update annotation properties
        await annotation.addProperty('userClassification', newClassification);
        await annotation.addProperty('lastModified', new Date().toISOString());

        // Emit event for real-time sync
        this.$eventBus.$emit('bloodFilm:cellReclassified', {
          cellId,
          oldClassification,
          newClassification,
          sessionId: this.analysisSession.runId
        });

      } catch (error) {
        console.error('Failed to update cell annotation:', error);
        throw error;
      }
    },

    // Get term ID by name from ontology
    getTermIdByName(termName) {
      if (!termName) return null;

      for (const category of this.ontologyCategories) {
        for (const term of category.terms) {
          if (term.name === termName) {
            return term.id;
          }

          // Check children for hierarchical terms
          if (term.children) {
            for (const child of term.children) {
              if (child.name === termName) {
                return child.id;
              }
            }
          }
        }
      }

      return null;
    },

    async loadCellAnnotations() {
      try {
        const imageId = this.image.id;
        const sliceId = this.imageWrapper.activeSlice?.id;

        if (!imageId) {
          console.warn('No image ID available for loading annotations');
          return [];
        }

        console.log(`Starting optimized single-request annotation loading for image ${imageId}`);
        const startTime = performance.now();

        // Show loading progress
        this.annotationLoadingProgress = { loaded: 0, total: 'unknown', message: 'Loading all annotations...' };

        // OPTIMIZATION: Try single request first, fallback to efficient batching if needed
        let allAnnotations = [];
        let totalLoaded = 0;

        if (this.loadingConfig.useSingleRequest) {
          try {
            console.log('🚀 Attempting to load ALL annotations in single request...');

            const collection = new AnnotationCollection({
              project: this.project.id,
              image: imageId,
              slice: sliceId,
              showWKT: false, // Skip geometries for performance
              showTerm: true, // Need terms for classification filtering
              showGIS: false, // Skip GIS data for performance
              showTag: false, // Skip tags for performance
              showBasic: true, // Load basic annotation data
              max: 0 // 0 means load ALL annotations at once (no pagination)
            });

            const result = await collection.fetchAll();
            allAnnotations = result.array || [];
            totalLoaded = allAnnotations.length;

            console.log(`✅ Single request successful: ${totalLoaded} annotations loaded in one go!`);

          } catch (singleRequestError) {
            console.warn('⚠️ Single request failed, falling back to optimized large batch loading:', singleRequestError);

            // FALLBACK: Use configurable large batches for better performance
            const BATCH_SIZE = this.loadingConfig.largeBatchSize;
            let offset = 0;
            let hasMore = true;

            while (hasMore) {
              console.log(`📦 Loading large batch (${BATCH_SIZE} items), offset: ${offset}`);

              const collection = new AnnotationCollection({
                project: this.project.id,
                image: imageId,
                slice: sliceId,
                showWKT: false,
                showTerm: true,
                showGIS: false,
                showTag: false,
                max: BATCH_SIZE,
                offset: offset
              });

              const result = await collection.fetchAll();

              if (!result.array || result.array.length === 0) {
                hasMore = false;
                break;
              }

              allAnnotations = allAnnotations.concat(result.array);
              totalLoaded += result.array.length;

              // Update progress during batching
              this.annotationLoadingProgress = {
                loaded: totalLoaded,
                total: result.size || 'unknown',
                message: `Loading annotations... ${totalLoaded} loaded`
              };

              console.log(`📦 Batch loaded: ${result.array.length}, Total: ${totalLoaded}`);

              // Check if we have more data
              if (result.array.length < BATCH_SIZE) {
                hasMore = false;
              } else {
                offset += BATCH_SIZE;
              }

              // Skip delays if configured for maximum speed
              if (hasMore && !this.loadingConfig.skipDelays) {
                await new Promise(resolve => setTimeout(resolve, 10)); // Minimal delay
              }
            }
          }
        } else {
          // Direct batching approach (if single request is disabled)
          console.log('📦 Using batch loading approach...');
          const BATCH_SIZE = this.loadingConfig.largeBatchSize;
          let offset = 0;
          let hasMore = true;

          while (hasMore) {
            const collection = new AnnotationCollection({
              project: this.project.id,
              image: imageId,
              slice: sliceId,
              showWKT: false,
              showTerm: true,
              showGIS: false,
              showTag: false,
              max: BATCH_SIZE,
              offset: offset
            });

            const result = await collection.fetchAll();

            if (!result.array || result.array.length === 0) {
              hasMore = false;
              break;
            }

            allAnnotations = allAnnotations.concat(result.array);
            totalLoaded += result.array.length;

            if (result.array.length < BATCH_SIZE) {
              hasMore = false;
            } else {
              offset += BATCH_SIZE;
            }
          }
        }

        // Update final progress
        this.annotationLoadingProgress = {
          loaded: totalLoaded,
          total: totalLoaded,
          message: `Loaded ${totalLoaded} annotations`
        };

        console.log(`📊 Total loading time: ${((performance.now() - startTime) / 1000).toFixed(2)}s for ${totalLoaded} annotations`);

        // Ensure ontology and allTerms are loaded before processing
        if (this.ontology && this.allTerms.length === 0) {
          console.log('Waiting for ontology to load for proper RBC term detection...');
        }
        console.log(`Available terms: ${this.allTerms ? this.allTerms.length : 0} total terms`);

        // OPTIMIZATION: Client-side filtering and processing (much faster than individual API calls)
        this.annotationLoadingProgress.message = 'Processing annotations...';

        // Separate annotations by type using client-side filtering
        const wbcAnnotations = [];
        const rbcAnnotations = [];
        const pltAnnotations = [];

        // Process all annotations in memory (much faster than API calls)
        for (const annotation of allAnnotations) {
          if (!annotation.term || !Array.isArray(annotation.term)) continue;

          let hasWBC = false;
          let hasRBC = false;
          let hasPLT = false;
          let classification = null;

          // Check terms for each annotation
          for (const termId of annotation.term) {
            const termName = this.getTermNameById(termId);
            if (!termName) continue;

            const termLower = termName.toLowerCase();

            if (termLower.startsWith('wbc')) {
              hasWBC = true;
              if (this.isWBCType(termName)) {
                classification = termName;
              }
            } else if (termLower.startsWith('rbc')) {
              hasRBC = true;
              console.log(`Found RBC term: ${termName} (ID: ${termId})`);
            } else if (termLower.includes('platelet') || termLower.includes('thrombocyte')) {
              hasPLT = true;
            }
          }

          // Add to appropriate collections
          if (hasWBC) {
            wbcAnnotations.push({
              id: annotation.id,
              aiClassification: classification,
              userClassification: annotation.properties?.userClassification || null,
              terms: annotation.term.map(id => this.getTermNameById(id)).filter(n => n),
              annotation: annotation
            });
          }

          if (hasRBC) {
            rbcAnnotations.push({
              id: annotation.id,
              terms: annotation.term.map(id => this.getTermNameById(id)).filter(n => n),
              annotation: annotation
            });
          }

          if (hasPLT) {
            pltAnnotations.push({
              id: annotation.id,
              terms: annotation.term.map(id => this.getTermNameById(id)).filter(n => n),
              annotation: annotation
            });
          }
        }

        // Assign to component data
        this.currentCells = wbcAnnotations;
        this.rbcCells = rbcAnnotations;
        this.pltCells = pltAnnotations;

        // Update progress
        this.annotationLoadingProgress.message = 'Loading complete!';

        console.log(`\n=== PROCESSING RESULTS ===`);
        console.log(`WBC annotations: ${wbcAnnotations.length}`);
        console.log(`RBC annotations: ${rbcAnnotations.length}`);
        console.log(`PLT annotations: ${pltAnnotations.length}`);
        console.log(`Total processed: ${totalLoaded}`);

        // Debug RBC cell types detected
        if (rbcAnnotations.length > 0) {
          console.log(`\n=== RBC CELL TYPES DETECTED ===`);
          const rbcTypeCounts = {};
          rbcAnnotations.forEach((cell, index) => {
            if (cell.terms && Array.isArray(cell.terms)) {
              cell.terms.forEach(term => {
                if (term && term.toLowerCase().startsWith('rbc')) {
                  rbcTypeCounts[term] = (rbcTypeCounts[term] || 0) + 1;
                }
              });
            }
          });

          Object.keys(rbcTypeCounts).sort().forEach(cellType => {
            console.log(`  ${cellType}: ${rbcTypeCounts[cellType]} cells`);
          });

          console.log(`=== Total unique RBC types: ${Object.keys(rbcTypeCounts).length} ===`);
        }

        const loadTime = performance.now() - startTime;
        console.log(`Total loading time: ${loadTime.toFixed(2)}ms`);

        // Update counts after loading
        this.updateCellTypeCounts();

        // Clear loading progress after a delay
        setTimeout(() => {
          this.annotationLoadingProgress = null;
        }, 2000);

        return this.currentCells;

      } catch (error) {
        console.error('Failed to load cell annotations:', error);
        // Keep using mock data on error
        return this.currentCells;
      }
    },

    // Helper method to get term name by ID from ontology
    getTermNameById(termId) {
      // First try to find in allTerms (includes all terms including RBC)
      if (this.allTerms) {
        const term = this.allTerms.find(t => t.id == termId);
        if (term) {
          console.log(`Found term ${termId} in allTerms: ${term.name}`);
          return term.name;
        }
      }

      // Fallback to ontology categories (mainly for WBC terms)
      for (const category of this.ontologyCategories) {
        for (const term of category.terms) {
          if (term.id == termId) {
            console.log(`Found term ${termId} in categories: ${term.name}`);
            return term.name;
          }

          // Check children terms as well
          if (term.children) {
            for (const child of term.children) {
              if (child.id == termId) {
                console.log(`Found term ${termId} in children: ${child.name}`);
                return child.name;
              }
            }
          }
        }
      }

      console.log(`Term ID ${termId} not found in any source`);
      return null;
    },

    // Helper method to check if a term name represents a WBC type
    isWBCType(termName) {
      if (!termName) return false;

      const lowerTermName = termName.toLowerCase();

      // Common WBC types
      const wbcTypes = [
        'neutrophil', 'lymphocyte', 'monocyte', 'eosinophil', 'basophil',
        'blast', 'band', 'metamyelocyte', 'myelocyte', 'promyelocyte',
        'plasma cell', 'atypical lymphocyte', 'reactive lymphocyte',
        'large lymphocyte', 'small lymphocyte'
      ];

      // Check if term contains any WBC type keywords
      return wbcTypes.some(wbcType =>
        lowerTermName.includes(wbcType.toLowerCase())
      );
    },

    // Initialize analysis session
    initializeAnalysisSession() {
      this.analysisSession = {
        runId: `demo${Date.now()}`,
        imageId: this.image?.id || 'demo123',
        status: 'pending_review',
        currentStep: 'wbc'
      };
    },

    async updateRBCMorphologyScore(itemName, score, source = 'manual') {
      // Update status to 'in_progress' when user starts editing
      if (this.analysisSession.status === 'pending_review') {
        this.updateAnalysisStatus('in_progress');
      }

      // Find and update the score
      this.rbcMorphologyCategories.forEach(category => {
        const item = category.items.find(i => i.name === itemName);
        if (item) {
          item.score = score;
        }
      });

      // Store score data for database persistence
      this.rbcMorphologyScores[itemName] = {
        score: score,
        source: source, // 'auto' or 'manual'
        timestamp: new Date().toISOString(),
        userId: this.currentUser?.id || 'unknown',
        userName: this.currentUser?.name || 'Unknown User',
        projectId: this.project?.id,
        imageId: this.image?.id,
        morphologyType: itemName
      };

      console.log(`Updated ${itemName} score to ${score}+ (${source})`);

      // Save to database
      try {
        await this.saveRBCMorphologyScoreToDatabase(itemName, this.rbcMorphologyScores[itemName]);
      } catch (error) {
        console.error('Failed to save RBC morphology score to database:', error);
      }

      // Emit event for real-time sync if needed
      this.$eventBus?.$emit('bloodFilm:rbcMorphologyScored', {
        itemName,
        score,
        source,
        sessionId: this.analysisSession.runId
      });
    },

    get totalWBCCounted() {
      if (!this.currentCells) return 0;
      return this.currentCells.length;
    },

    // Add this helper method:
    countRBCType(morphologyName) {
      if (!this.rbcCells || this.rbcCells.length === 0) return 0;

      let count = 0;
      this.rbcCells.forEach(cell => {
        if (cell.terms && Array.isArray(cell.terms)) {
          const hasMatchingTerm = cell.terms.some(term => {
            const termName = typeof term === 'object' ? term.name : term;
            return termName && termName.toLowerCase() === morphologyName.toLowerCase();
          });
          if (hasMatchingTerm) count++;
        }
      });

      return count;
    },

    // Calculate automatic score based on RBC term count for specific morphology type
    calculateMorphologyScore(item) {
      if (!this.rbcCells || this.rbcCells.length === 0) {
        console.log(`No RBC cells available for calculating score for ${item.name}`);
        return null;
      }

      // Get total RBC count (including normal RBCs)
      const totalRBCs = this.getTotalRBCCount();
      if (totalRBCs === 0) {
        console.log('No RBCs found for percentage calculation');
        return null;
      }

      // Count annotations with terms matching the morphology type
      let count = 0;
      const searchTerm = item.name; // Use full term name for term-based matching

      console.log(`\n=== Calculating morphology score for ${item.name} ===`);
      console.log(`Search term: ${searchTerm}`);
      console.log(`Total RBC cells to check: ${this.rbcCells.length}`);
      console.log(`Total RBCs for percentage: ${totalRBCs}`);

      this.rbcCells.forEach((cell, index) => {
        if (cell.terms && Array.isArray(cell.terms) && cell.terms.length > 0) {
          const hasMatchingTerm = cell.terms.some(term => {
            if (!term) return false;

            const termName = typeof term === 'object' ? term.name : term;
            const termLower = termName.toLowerCase();
            const searchLower = searchTerm.toLowerCase();

            // Check if the term exactly matches the search term
            // For PDF spec terms like "rbc1-helmet-schistocyte"
            const match = termLower === searchLower ||
                        termName === searchTerm;

            if (match) {
              console.log(`  Term "${termName}" matches "${searchTerm}"`);
            }
            return match;
          });

          if (hasMatchingTerm) {
            count++;
            console.log(`  ✓ Match found! Count now: ${count}`);
          }
        }
      });

      // Calculate percentage per 100 RBCs
      const percentage = (count / totalRBCs) * 100;

      console.log(`${item.displayName} (${item.name}): found ${count} cells`);
      console.log(`Percentage: ${percentage.toFixed(2)}% (${count}/${totalRBCs})`);

      // Store the percentage for reporting
      if (!item.calculatedPercentage) {
        item.calculatedPercentage = percentage;
      }

      // Calculate score based on percentage thresholds from PDF
      if (!item.referenceRanges) return null;

      const range1 = item.referenceRanges[1];
      const range2 = item.referenceRanges[2];
      const range3 = item.referenceRanges[3];

      console.log(`Reference ranges: 1+=${range1}, 2+=${range2}, 3+=${range3}`);

      // SPECIAL CASE: Nucleated RBC (rbc15-erythroblast) - count per 100 RBCs
      if (item.name === 'rbc15-erythroblast') {
        // For nucleated RBCs, we use count per 100 RBCs (percentage)
        console.log(`Nucleated RBC percentage: ${percentage.toFixed(2)}%`);

        if (percentage > 10) return 3;      // >10% = 3+
        if (percentage > 5) return 2;       // >5% = 2+
        if (count > 0) return 1;            // Any presence = 1+
        return null;
      }

      // Handle percentage-based thresholds for all other RBC types
      let score = null;

      if (range1 && range1 !== 'NA') {
        // Handle different range patterns

        // Pattern 1: "1-2", "3-6", ">6" (schistocytes)
        if (range1.includes('-') && range2 && range2.includes('-') && range3 && range3.startsWith('>')) {
          const [min1, max1] = range1.split('-').map(n => parseInt(n));
          const [min2, max2] = range2.split('-').map(n => parseInt(n));
          const threshold3 = parseInt(range3.substring(1));

          if (percentage > threshold3) {
            score = 3;
          } else if (percentage >= min2 && percentage <= max2) {
            score = 2;
          } else if (percentage >= min1 && percentage <= max1) {
            score = 1;
          }
        }
        // Pattern 2: "15-61", ">61" (most RBC types)
        else if (range1.includes('-') && range2 && range2.startsWith('>') && (!range3 || range3 === 'null' || range3 === 'NA')) {
          const [min1, max1] = range1.split('-').map(n => parseInt(n));
          const threshold2 = parseInt(range2.substring(1));

          if (percentage > threshold2) {
            score = 2;
          } else if (percentage >= min1 && percentage <= max1) {
            score = 1;
          }
        }
        // Pattern 3: "3-6", ">6" (drepanocytes/sickle cells)
        else if (range1.includes('-') && range2 && range2.startsWith('>') && (!range3 || range3 === 'null' || range3 === 'NA')) {
          const [min1, max1] = range1.split('-').map(n => parseInt(n));
          const threshold2 = parseInt(range2.substring(1));

          if (percentage > threshold2) {
            score = 2;
          } else if (percentage >= min1 && percentage <= max1) {
            score = 1;
          }
        }
        // Pattern 4: "33-61", ">61" (hypochromia, macrocyte, microcyte)
        else if (range1.includes('-') && range2 && range2.startsWith('>') && (!range3 || range3 === 'null' || range3 === 'NA')) {
          const [min1, max1] = range1.split('-').map(n => parseInt(n));
          const threshold2 = parseInt(range2.substring(1));

          if (percentage > threshold2) {
            score = 2;
          } else if (percentage >= min1 && percentage <= max1) {
            score = 1;
          }
        }
        // Pattern 5: "Any", ">5", ">10" (nucleated RBC - already handled above)
      }

      console.log(`Calculated score for ${item.name}: ${score}`);
      return score;
    },

    // Get total RBC count including normal RBCs
    getTotalRBCCount() {
      if (!this.rbcCells || this.rbcCells.length === 0) {
        return 0;
      }

      // Count all cells that have any RBC term
      let totalCount = 0;
      this.rbcCells.forEach(cell => {
        if (cell.terms && Array.isArray(cell.terms) && cell.terms.length > 0) {
          // Check if this cell has any RBC-related term
          const hasRBCTerm = cell.terms.some(term => {
            const termName = typeof term === 'object' ? term.name : term;
            return termName && (
              termName.toLowerCase().startsWith('rbc') ||
              termName.toLowerCase().includes('rbc') ||
              termName.toLowerCase().includes('erythro')
            );
          });
          if (hasRBCTerm) {
            totalCount++;
          }
        }
      });

      return totalCount;
    },

    // Auto-calculate all morphology scores based on current term annotations
    async autoCalculateMorphologyScores(manualTrigger = true) {
      // Set loading state
      this.isAutoCalculating = true;

      try {
        console.log('=== Auto-calculating morphology scores ===');
        if (manualTrigger) {
          console.log('=== Resetting all manual changes (user triggered) ===');
        } else {
          console.log('=== Initial auto-calculation ===');
        }

      // First, reset ALL scores (both manual and auto) to clear the state
      this.rbcMorphologyScores = {};

      // Clear all displayed scores in the UI as well
      this.rbcMorphologyCategories.forEach(category => {
        category.items.forEach(item => {
          item.score = null; // Reset UI display
        });
      });

      // Clear all RBC scores from database to remove any manual user changes
      console.log('=== Clearing all RBC scores from database ===');
      await this.clearRBCMorphologyScoresFromDatabase();

      console.log('=== All scores reset - performing fresh auto-calculation ===');
      console.log('Total RBC cells available:', this.rbcCells ? this.rbcCells.length : 0);

      // Log sample RBC cells for debugging
      if (this.rbcCells && this.rbcCells.length > 0) {
        console.log('Sample RBC cells (first 5):');
        this.rbcCells.slice(0, 5).forEach((cell, idx) => {
          console.log(`  Cell ${idx}:`, {
            id: cell.id,
            terms: cell.terms
          });
        });
      }

      const autoCalculationPromises = [];

      this.rbcMorphologyCategories.forEach(category => {
        category.items.forEach(item => {
          const autoScore = this.calculateMorphologyScore(item);
          if (autoScore !== null) {
            console.log(`Auto-set ${item.displayName} to ${autoScore}+`);
            // Use 'auto' source for auto-calculated scores
            autoCalculationPromises.push(
              this.updateRBCMorphologyScore(item.name, autoScore, 'auto')
            );
          }
        });
      });

      // Wait for all auto-calculations to be saved
      try {
        await Promise.all(autoCalculationPromises);
        console.log('=== All auto-calculations saved to database ===');

        // Show user notification about successful save to database
        if (manualTrigger) {
          this.$notify({
            type: 'success',
            text: 'RBC morphology scores calculated and saved to database successfully.'
          });
        }

      } catch (error) {
        console.error('Error saving auto-calculated scores:', error);
        if (manualTrigger) {
          this.$notify({
            type: 'error',
            text: 'Failed to auto-calculate scores. Please try again.'
          });
        }
      }

      } catch (error) {
        console.error('Error in auto-calculation process:', error);
        if (manualTrigger) {
          this.$notify({
            type: 'error',
            text: 'Failed to auto-calculate RBC morphology scores. Please try again.'
          });
        }
      } finally {
        // Always clear loading state
        this.isAutoCalculating = false;
        console.log('=== Completed auto-calculation ===');
      }
    },

    // Set up event listeners for real-time updates
    setupEventListeners() {
      // Listen for annotation classification changes
      if (this.$eventBus) {
        this.$eventBus.$on('annotation:termAdded', this.handleAnnotationTermUpdate);
        this.$eventBus.$on('annotation:termRemoved', this.handleAnnotationTermUpdate);
        this.$eventBus.$on('annotation:updated', this.handleAnnotationUpdate);
        this.$eventBus.$on('bloodFilm:cellReclassified', this.handleCellReclassified);
      }

      // Listen for store changes in annotation data
      this.storeUnwatch = this.$store.watch(
        state => state.currentProject?.currentViewer?.annotations,
        () => {
          if (this.activeTab === 'wbc') {
            this.$nextTick(() => {
              this.refreshWBCData();
            });
          }
        },
        { deep: true }
      );
    },

    // Remove event listeners
    removeEventListeners() {
      if (this.$eventBus) {
        this.$eventBus.$off('annotation:termAdded', this.handleAnnotationTermUpdate);
        this.$eventBus.$off('annotation:termRemoved', this.handleAnnotationTermUpdate);
        this.$eventBus.$off('annotation:updated', this.handleAnnotationUpdate);
        this.$eventBus.$off('bloodFilm:cellReclassified', this.handleCellReclassified);
      }

      if (this.storeUnwatch) {
        this.storeUnwatch();
      }
    },

    // Handle annotation term updates
    handleAnnotationTermUpdate(data) {
      console.log('Annotation term updated:', data);
      if (this.activeTab === 'wbc') {
        this.$nextTick(() => {
          this.refreshWBCData();
        });
      }
    },

    // Handle general annotation updates
    handleAnnotationUpdate(data) {
      console.log('Annotation updated:', data);
      if (this.activeTab === 'wbc') {
        this.$nextTick(() => {
          this.refreshWBCData();
        });
      }
    },

    // Handle cell reclassification events
    handleCellReclassified(data) {
      console.log('Cell reclassified:', data);
      if (this.activeTab === 'wbc') {
        this.$nextTick(() => {
          this.updateCellTypeCounts();
        });
      }
    },

    // Inline comment editing methods
    startEditingComment(tabId) {
      this.editingComments[tabId] = true;
      this.currentCommentText = this.tabComments[tabId] || '';
    },

    cancelEditComment(tabId) {
      this.editingComments[tabId] = false;
      this.currentCommentText = '';
    },

    async saveCommentInline(tabId) {
      const projectId = this.project?.id;

      if (!tabId) {
        console.error('Missing tabId for saving comment');
        return;
      }

      if (!projectId) {
        console.warn('No project ID available, saving comment locally only');
      }

      // Update status to 'in_progress' when user writes comment
      if (this.analysisSession.status === 'pending_review' && this.currentCommentText.trim()) {
        this.updateAnalysisStatus('in_progress');
      }

      // Update comment data
      this.tabComments[tabId] = this.currentCommentText.trim();
      this.commentMetadata[tabId] = {
        timestamp: new Date().toISOString(),
        userId: this.currentUser?.id || 'unknown',
        userName: this.currentUser?.name || 'Unknown User',
        projectId: projectId || 'no-project'
      };

      // Save to Cytomine database
      try {
        await this.saveCommentsToDatabase();
        console.log(`Comment saved for ${tabId} tab in database`);
      } catch (error) {
        console.error('Failed to save comment:', error);
      }

      // Exit editing mode
      this.editingComments[tabId] = false;
      this.currentCommentText = '';
    },

    async deleteCommentInline(tabId) {
      if (!tabId) return;

      // Clear comment data
      this.tabComments[tabId] = '';
      this.commentMetadata[tabId] = null;

      // Update database
      try {
        await this.saveCommentsToDatabase();
        console.log(`Comment deleted for ${tabId} tab`);
      } catch (error) {
        console.error('Failed to delete comment:', error);
      }

      // Exit editing mode
      this.editingComments[tabId] = false;
      this.currentCommentText = '';
    },

    getCommentDate(tabId) {
      const metadata = this.commentMetadata[tabId];
      if (!metadata || !metadata.timestamp) return '';

      const date = new Date(metadata.timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    },

    getTabDisplayName(tabId) {
      const tabNames = {
        'annotations': 'Annotation List',
        'wbc': 'White Blood Cells (WBC)',
        'rbc': 'Red Blood Cells (RBC)',
        'plt': 'Platelets (PLT)',
        'report': 'Report'
      };
      return tabNames[tabId] || tabId.toUpperCase();
    },

    // Explicit method for the "Load All Annotations" button
    async loadAllAnnotations() {
      console.log('=== LOAD ALL ANNOTATIONS BUTTON CLICKED ===');

      // Force reload by resetting the loading flag if it's stuck
      if (this.isLoadingAnnotations) {
        console.log('Resetting stuck loading state...');
        this.isLoadingAnnotations = false;
      }

      // Clear existing annotations and force reload
      this.allAnnotations = [];
      this.filteredAnnotations = [];

      // Call the main loading method
      await this.loadAnnotationsForList();

      // Ensure filtering is applied after loading
      await this.filterAnnotations();

      console.log(`=== LOAD COMPLETE: ${this.allAnnotations.length} total, ${this.filteredAnnotations.length} filtered ===`);
    },

    async loadAnnotationsForList() {
      // Prevent multiple simultaneous loads
      if (this.isLoadingAnnotations) {
        console.log('Annotation loading already in progress, skipping...');
        return;
      }

      console.log('Starting annotation loading for BloodFilmPanel...');
      this.isLoadingAnnotations = true;

      // Reset annotations array
      this.allAnnotations = [];
      this.filteredAnnotations = [];

      try {
        // First try to load real annotations from Cytomine API
        console.log('Step 1: Trying to load real annotations from Cytomine API...');
        await this.loadRealAnnotationsFromCytomine();
        console.log(`Step 1 result: ${this.allAnnotations.length} annotations loaded from Cytomine`);

        // If no real annotations found, use currentCells data as fallback
        if (this.allAnnotations.length === 0) {
          console.log('Step 2: No Cytomine annotations found, checking currentCells fallback...');
          console.log('currentCells available:', !!this.currentCells);
          console.log('currentCells is array:', Array.isArray(this.currentCells));
          console.log('currentCells length:', this.currentCells?.length || 0);

          if (this.currentCells && Array.isArray(this.currentCells) && this.currentCells.length > 0) {
            console.log('Step 2: Using currentCells data as fallback for annotation list:', this.currentCells.length);

            // Check if we have a valid image ID for thumbnails
            const imageId = this.getImageId();
            if (!imageId) {
              console.warn('No valid image ID found - thumbnails will not work properly');
            }

            this.allAnnotations = this.currentCells.map((cell, index) => {
            // Generate mock location based on cell index for testing
            const mockLocation = this.generateMockLocation(cell.id, index);

            // Transform terms to proper objects with id and name properties
            let processedTerms = [];
            if (cell.terms && Array.isArray(cell.terms)) {
              processedTerms = cell.terms.map(term => {
                if (typeof term === 'string') {
                  // Convert string term to object with id lookup
                  const termId = this.getTermIdByName(term);
                  return {
                    id: termId || term,
                    name: term
                  };
                } else if (typeof term === 'object' && term.id) {
                  // Already an object with id
                  return term;
                } else {
                  // Fallback for other formats
                  return {
                    id: term,
                    name: term.toString()
                  };
                }
              }).filter(term => term.id); // Filter out terms without valid IDs
            } else if (cell.aiClassification) {
              // Use AI classification as fallback term
              const termId = this.getTermIdByName(cell.aiClassification);
              if (termId) {
                processedTerms = [{
                  id: termId,
                  name: cell.aiClassification
                }];
              }
            }

            // Get current user ID for layer assignment
            const currentUserId = this.currentUser?.id || 1;
            const currentSliceId = this.slices?.[0]?.id || imageId; // Use first slice or image ID as fallback

            const annotation = {
              id: cell.id,
              terms: processedTerms,
              termObjects: cell.termObjects || [],
              location: mockLocation.wkt, // Use WKT format for AnnotationLayer
              user: currentUserId, // User ID for layer belonging logic
              userId: currentUserId,
              username: cell.user?.username || 'System',
              slice: currentSliceId, // Required for layer belonging logic
              image: imageId,
              type: 'USER', // Required for layer belonging logic (not REVIEWED)
              created: cell.created || new Date().toISOString(),
              updated: cell.updated || new Date().toISOString(),
              aiClassification: cell.aiClassification,
              // Store coordinate data for other uses
              _coordinates: {
                x: mockLocation.x,
                y: mockLocation.y
              },
              // Flag to identify this as a BloodFilmPanel annotation for debugging
              _isBloodFilmAnnotation: true
            };

            return annotation;
          });
          } else {
            console.log('Step 2: No currentCells data available for fallback');
          }
        }

        // Step 3: Final fallback if nothing else worked
        if (this.allAnnotations.length === 0) {
          console.log('Step 3: Creating minimal mock annotations as final fallback...');
          this.allAnnotations = this.createMockAnnotations();
        }

        console.log('Final result: Loaded annotations for list:', this.allAnnotations.length);
        if (this.allAnnotations.length > 0) {
          console.log('Sample annotation with location:', this.allAnnotations[0]);
          console.log('Sample annotation WKT:', this.allAnnotations[0].location);
        }

        this.filteredAnnotations = [...this.allAnnotations];

        // Push annotations to the annotation layers when on annotations tab
        const imageId = this.getImageId();
        if (imageId && this.allAnnotations.length > 0 && this.activeTab === 'annotations') {
          console.log('Adding annotations to annotation layers:', this.allAnnotations.length);

          // First, clear existing annotations to avoid duplicates
          //this.$eventBus?.$emit('reloadAnnotations', {
          //  idImage: imageId,
          //  clear: true,
          //  hard: false // Don't hard reload, just clear
          //});

          // Wait a bit for clearing to complete, then add our annotations
          this.$nextTick(() => {
            setTimeout(async () => {
              console.log('=== STARTING ANNOTATION LAYER INTEGRATION ===');
              console.log('ImageWrapper:', this.imageWrapper);
              console.log('Available layers in viewer:', this.imageWrapper?.layers || 'No layers info');
              if (this.imageWrapper?.layers) {
                console.log('Layer details:');
                Object.keys(this.imageWrapper.layers).forEach(key => {
                  const layer = this.imageWrapper.layers[key];
                  console.log(`  - Layer ${key}:`, {
                    id: layer.id,
                    name: layer.name,
                    visible: layer.visible,
                    isReview: layer.isReview
                  });
                });
              }
              console.log('Current user ID:', this.currentUser?.id);
              console.log('Active slices:', this.slices?.map(s => s.id) || 'No slices');

              // Emit all annotations to layers (for functionality)
              console.log(`Processing ${this.allAnnotations.length} annotations for display...`);

              // Process only first few annotations with detailed debug to prevent console spam
              const maxDebugAnnotations = Math.min(3, this.allAnnotations.length);

              for (let index = 0; index < this.allAnnotations.length; index++) {
                const annotation = this.allAnnotations[index];

                // Show detailed debug only for first few annotations
                if (index < maxDebugAnnotations) {
                  console.log(`\n[${index + 1}/${this.allAnnotations.length}] Adding annotation ${annotation.id} to layers:`);
                  console.log('  - Location (WKT):', typeof annotation.location === 'string' ?
                             annotation.location.substring(0, 50) + '...' :
                             typeof annotation.location);
                  console.log('  - User ID:', annotation.user);
                  console.log('  - Slice ID:', annotation.slice);
                  console.log('  - Type:', annotation.type);
                  console.log('  - Terms:', annotation.terms?.length || 0);

                  // Test WKT parsing for debug annotations
                  try {
                    if (typeof annotation.location === 'string') {
                      const WKT = await import('ol/format/WKT');
                      const wktFormat = new WKT.default();
                      const geometry = wktFormat.readGeometry(annotation.location);
                      console.log('  - WKT parsing SUCCESS:', {
                        type: geometry.getType(),
                        coordinateCount: geometry.getCoordinates()[0]?.length || 0
                      });
                    }
                  } catch (wktError) {
                    console.error('  - WKT parsing FAILED:', wktError.message);
                  }

                  console.log('  - Emitting addAnnotation event...');
                }

                // Emit addAnnotation event for ALL annotations (not just debug ones)
                this.$eventBus?.$emit('addAnnotation', annotation);

                if (index < maxDebugAnnotations) {
                  console.log('  - Event emitted successfully');
                }
              }

              if (this.allAnnotations.length > maxDebugAnnotations) {
                console.log(`\n... (${this.allAnnotations.length - maxDebugAnnotations} more annotations processed without debug output)`);
              }

              console.log('\n=== FINISHED ADDING ALL ANNOTATIONS TO LAYERS ===');

              // Also emit a global event to indicate annotations are ready
              this.$eventBus?.$emit('annotationsLoaded', {
                count: this.allAnnotations.length,
                imageId: imageId
              });

            }, 100); // Small delay to ensure clearing is complete
          });
        }

        // Initialize filtered annotations after loading
        if (!this.selectedAnnotationTerm) {
          this.filteredAnnotations = [...this.allAnnotations];
        }

      } catch (error) {
        console.error('Error loading annotations:', error);
        this.allAnnotations = [];
        this.filteredAnnotations = [];
      } finally {
        this.isLoadingAnnotations = false;
      }
    },

    // Load real annotations from Cytomine API
    async loadRealAnnotationsFromCytomine() {
      try {
        console.log('Attempting to load real annotations from Cytomine API...');

        const projectId = this.project?.id;
        const imageId = this.getImageId();

        if (!projectId || !imageId) {
          console.log('Missing project or image ID for annotation loading:', { projectId, imageId });
          return;
        }

        // Import Cytomine client for annotation loading
        const { AnnotationCollection } = await import('cytomine-client');

        const collection = new AnnotationCollection({
          project: projectId,
          image: imageId,
          showWKT: true, // Enable WKT geometry data
          showMeta: true, // Enable metadata
          showGIS: true   // Enable GIS information
        });

        // Limit the number for initial testing
        const result = await collection.fetchPage(0, 50);
        const annotations = result.array || [];

        console.log(`Loaded ${annotations.length} real annotations from Cytomine`);

        this.allAnnotations = annotations.map(annotation => {
          // Get the raw WKT location string directly from annotation.location
          let location = annotation.location;

          // console.log(`Processing annotation ${annotation.id} with location:`, location);

          // Convert POINT locations to small circular polygons for visibility
          if (typeof location === 'string' && location.includes('POINT')) {
            const pointMatch = location.match(/POINT\s*\(\s*([0-9.-]+)\s+([0-9.-]+)\s*\)/i);
            if (pointMatch) {
              const x = parseFloat(pointMatch[1]);
              const y = parseFloat(pointMatch[2]);
              const radius = 15; // Small circle radius for cell visibility

              // Generate circular polygon around the point
              const vertices = [];
              for (let i = 0; i < 8; i++) {
                const angle = (i / 8) * 2 * Math.PI;
                const px = x + (radius * Math.cos(angle));
                const py = y + (radius * Math.sin(angle));
                vertices.push(`${px} ${py}`);
              }
              vertices.push(vertices[0]); // Close the polygon

              location = `POLYGON((${vertices.join(', ')}))`;
              console.log(`Converted POINT to visible POLYGON for annotation ${annotation.id}`);
            }
          } else if (!location || typeof location !== 'string') {
            // Fallback: try to get location from other sources
            const fallbackLocation = this.parseAnnotationLocationFromCytomine(annotation);
            if (fallbackLocation && fallbackLocation.x && fallbackLocation.y) {
              // Convert coordinate object to POINT WKT
              location = `POINT (${fallbackLocation.x} ${fallbackLocation.y})`;
              console.log(`Created POINT location from coordinates for annotation ${annotation.id}`);
            } else {
              console.warn(`No valid location found for annotation ${annotation.id}`);
              return null; // Skip this annotation
            }
          }

          // Process terms to proper objects
          let processedTerms = [];
          if (annotation.term && Array.isArray(annotation.term)) {
            processedTerms = annotation.term.map(termId => {
              const termName = this.getTermNameById(termId);
              return {
                id: termId,
                name: termName || `Term ${termId}`
              };
            });
          }

          // Debug the final location format (commented out)
          // console.log(`Final location for annotation ${annotation.id}:`, {
          //   type: typeof location,
          //   value: location,
          //   isString: typeof location === 'string',
          //   length: location?.length
          // });

          return {
            id: annotation.id,
            terms: processedTerms,
            termObjects: annotation.userByTerm || [],
            location: location,
            user: annotation.user || annotation.userId || 60, // Default user ID
            userId: annotation.user || annotation.userId || 60,
            username: annotation.username || 'System',
            slice: annotation.slice || this.slices?.[0]?.id || this.getImageId(), // Use slice or image ID
            image: annotation.image || this.getImageId(),
            type: 'USER', // Set proper type for layer belonging
            created: annotation.created,
            updated: annotation.updated,
            // Store original annotation for reference
            _originalAnnotation: annotation,
            // Flag for debugging
            _isBloodFilmAnnotation: true
          };
        }).filter(annotation => annotation !== null); // Remove failed annotations

        console.log(`Successfully loaded ${this.allAnnotations.length} real annotations with valid locations`);

      } catch (error) {
        console.warn('Failed to load real annotations from Cytomine:', error);
        // Fallback will be handled in the calling method
      }
    },

    // Get term name by ID from available terms
    getTermNameById(termId) {
      const term = this.availableAnnotationTerms.find(t => t.id == termId);
      return term ? term.name : null;
    },

    // Parse annotation location from Cytomine annotation object
    parseAnnotationLocationFromCytomine(annotation) {
      // Try multiple location sources in order of preference

      // 1. WKT geometry data
      if (annotation.location) {
        const wktLocation = this.parseWKTLocation(annotation.location);
        if (wktLocation) {
          console.log(`Parsed WKT location for annotation ${annotation.id}:`, wktLocation);
          return wktLocation;
        }
      }

      // 2. Direct x, y coordinates
      if (annotation.x !== undefined && annotation.y !== undefined) {
        return {
          x: parseFloat(annotation.x),
          y: parseFloat(annotation.y)
        };
      }

      // 3. Centroid coordinates
      if (annotation.centroid) {
        const centroid = this.parseWKTLocation(annotation.centroid);
        if (centroid) {
          return centroid;
        }
      }

      // 4. Bounding box center
      if (annotation.topLeftX !== undefined && annotation.topLeftY !== undefined &&
          annotation.bottomRightX !== undefined && annotation.bottomRightY !== undefined) {
        return {
          x: (parseFloat(annotation.topLeftX) + parseFloat(annotation.bottomRightX)) / 2,
          y: (parseFloat(annotation.topLeftY) + parseFloat(annotation.bottomRightY)) / 2
        };
      }

      console.log(`No location found for annotation ${annotation.id}`);
      return null;
    },

    // Generate mock locations for testing when real data is not available
    generateMockLocation(cellId, index) {
      // Generate locations in a grid pattern for testing
      const gridWidth = 4;
      const startX = 1000;
      const startY = 1000;
      const spacing = 200;
      const cellRadius = 30; // Cell circle radius

      const col = index % gridWidth;
      const row = Math.floor(index / gridWidth);

      const centerX = startX + (col * spacing) + (Math.random() * 50);
      const centerY = startY + (row * spacing) + (Math.random() * 50);

      // Generate a circular polygon to represent the cell
      // Create polygon vertices around the center point
      const numVertices = 12; // 12-sided polygon approximates a circle
      const vertices = [];

      for (let i = 0; i < numVertices; i++) {
        const angle = (i / numVertices) * 2 * Math.PI;
        const x = centerX + (cellRadius * Math.cos(angle));
        const y = centerY + (cellRadius * Math.sin(angle));
        vertices.push(`${x} ${y}`);
      }

      // Close the polygon by adding the first vertex at the end
      vertices.push(vertices[0]);

      // Return WKT format that AnnotationLayer can parse
      const wktPolygon = `POLYGON((${vertices.join(', ')}))`;

      // Debug the generated polygon
      console.log(`Generated WKT polygon for cell ${cellId}:`, {
        center: { x: centerX, y: centerY },
        radius: cellRadius,
        vertices: vertices.length,
        wkt: wktPolygon
      });

      return {
        // Provide both formats for compatibility
        x: centerX,
        y: centerY,
        wkt: wktPolygon,
        // This is what AnnotationLayer.createFeature expects
        toString: () => wktPolygon
      };
    },

    async filterAnnotations() {
      // Ensure annotations are loaded before filtering
      if (this.allAnnotations.length === 0) {
        await this.loadAnnotationsForList();
      }

      if (!this.selectedAnnotationTerm) {
        this.filteredAnnotations = [...this.allAnnotations];
      } else {
        const selectedTermId = parseInt(this.selectedAnnotationTerm);
        console.log('Filtering by term ID:', selectedTermId);

        this.filteredAnnotations = this.allAnnotations.filter(annotation => {
          if (!annotation.terms || !Array.isArray(annotation.terms)) {
            return false;
          }

          const hasMatchingTerm = annotation.terms.some(term => {
            // Handle different term formats
            let termId = null;
            if (typeof term === 'object' && term.id) {
              termId = parseInt(term.id);
            } else if (typeof term === 'string' || typeof term === 'number') {
              termId = parseInt(term);
            }

            const matches = termId === selectedTermId;
            if (matches) {
              console.log(`Annotation ${annotation.id} matches term ${selectedTermId}:`, term);
            }
            return matches;
          });

          return hasMatchingTerm;
        });
      }

      console.log(`Filtered annotations: ${this.filteredAnnotations.length} of ${this.allAnnotations.length} total`);
      console.log('Selected term ID:', this.selectedAnnotationTerm);
      if (this.allAnnotations.length > 0) {
        console.log('Sample annotation terms:', this.allAnnotations[0].terms);
      }
    },

    // Create minimal mock annotations as final fallback
    createMockAnnotations() {
      console.log('Creating mock annotations for testing...');
      const mockTerms = [
        { id: 1, name: 'Neutrophil' },
        { id: 2, name: 'Lymphocyte' },
        { id: 3, name: 'Monocyte' }
      ];

      return mockTerms.map((term, index) => ({
        id: `mock_${index + 1}`,
        terms: [term],
        termObjects: [term],
        location: `POLYGON ((${100 + index * 100} 100, ${150 + index * 100} 100, ${150 + index * 100} 150, ${100 + index * 100} 150, ${100 + index * 100} 100))`,
        user: { id: 1, username: 'Mock User' },
        slice: 1,
        type: 'UserAnnotation',
        image: this.getImageId() || 1,
        thumbnailUrl: null,
        aiClassification: term.name,
        _coordinates: { x: 125 + index * 100, y: 125 },
        _isBloodFilmAnnotation: true
      }));
    },

    getAnnotationTermName(annotation) {
      if (!annotation.terms || !annotation.terms.length) {
        // Fallback to AI classification if available
        return annotation.aiClassification || 'No classification';
      }

      const term = annotation.terms[0];

      // Handle new data structure where term is an object with id and name
      if (typeof term === 'object' && term.name) {
        return this.cleanTermName(term.name);
      }

      // Try to get term ID - handle different data structures
      let termId = term.id || term.termId || term;
      if (typeof termId === 'object' && termId.id) {
        termId = termId.id;
      }

      // First try to find in availableAnnotationTerms
      let foundTerm = this.availableAnnotationTerms.find(t => t.id == termId);
      if (foundTerm) {
        return foundTerm.name; // Already cleaned in availableAnnotationTerms
      }

      // If not found, try to get name directly from term object and clean it
      if (term.name) {
        return this.cleanTermName(term.name);
      }

      // If annotation has termObjects, try those and clean the name
      if (annotation.termObjects && annotation.termObjects.length > 0) {
        const termObj = annotation.termObjects[0];
        if (termObj.name) {
          return this.cleanTermName(termObj.name);
        }
      }

      // Fallback
      return `Term #${termId}`;
    },

    cleanTermName(termName) {
      if (!termName) return '';

      // Remove prefixes like "wbc0-", "rbc0-", "plt0-" etc.
      const cleaned = termName.replace(/^(wbc\d*-|rbc\d*-|plt\d*-)/i, '');

      // Capitalize first letter
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    },

    formatAnnotationDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    },

    goToAnnotation(annotation) {
      // Set as current selection
      this.currentSelectedAnnotation = annotation;

      // Parse the location to ensure we have proper coordinates
      const location = this.parseAnnotationLocation(annotation.location, annotation);
      const imageId = this.getImageId();

      console.log(`Going to annotation ${annotation.id}:`, {
        annotation: annotation,
        parsedLocation: location,
        originalLocation: annotation.location,
        currentImageId: imageId
      });

      if (!location || !location.x || !location.y) {
        console.warn('Cannot navigate to annotation: invalid location', { location });
        this.$notify({
          type: 'warning',
          text: `Cannot navigate to annotation #${annotation.id}: location not available`
        });
        return;
      }

      // Immediately trigger cell highlighting by emitting selectAnnotationInLayer
      const annotationData = {
        id: annotation.id,
        image: imageId,
        location: annotation.location || this.convertLocationToWKT(location), // Use original location if available
        x: location.x,
        y: location.y,
        slice: annotation.slice || null,
        terms: annotation.terms || [],
        user: annotation.user || { id: null, username: 'System' },
        // Add missing methods to prevent errors
        recordAction: () => {}, // No-op function for BloodFilm annotations
        term: annotation.terms || [], // Alias for terms to match Cytomine format
        // Special marker to identify this as a BloodFilm annotation
        _isBloodFilmAnnotation: true,
        _hideStandardPanel: true // Flag to hide the standard current selection panel
      };

      // Use selectAnnotationInLayer to properly highlight the polygon
      // We'll handle the duplicate panel issue differently
      this.$eventBus?.$emit('selectAnnotationInLayer', {
        index: this.index,
        annot: annotationData
      });

      // Create enhanced annotation object for centering
      // Must match the format expected by centerViewOnAnnot method
      const enhancedAnnotation = {
        ...annotation,
        // REQUIRED: Image ID must match current image
        image: imageId,
        // REQUIRED: Location as WKT string (expected by centerViewOnAnnot)
        location: annotation.location || this.convertLocationToWKT(location), // Preserve original POLYGON if available
        // REQUIRED: Centroid for fallback centering
        centroid: { x: location.x, y: location.y },
        // Additional properties that might be needed
        x: location.x,
        y: location.y,
        slice: this.imageWrapper?.activeSlice?.id || null
      };

      // Validate the enhanced annotation
      const isValid = enhancedAnnotation.image &&
                     enhancedAnnotation.location &&
                     enhancedAnnotation.centroid;

      console.log('Enhanced annotation for centering:', {
        ...enhancedAnnotation,
        isValid,
        locationPreserved: enhancedAnnotation.location === annotation.location,
        originalLocation: annotation.location,
        validation: {
          hasImage: !!enhancedAnnotation.image,
          hasLocation: !!enhancedAnnotation.location,
          hasCentroid: !!enhancedAnnotation.centroid
        }
      });

      // Auto zoom and center on the annotation
      this.$emit('centerView', {
        annot: enhancedAnnotation,
        duration: 500
      });

      // Fallback: try direct viewer manipulation if event doesn't work
      this.$nextTick(() => {
        this.fallbackCenterView(location);
      });
    },

    clearSelection() {
      this.currentSelectedAnnotation = null;
      this.selectedNewTerm = '';
    },

    // Convert location coordinates to WKT format expected by centerViewOnAnnot
    convertLocationToWKT(location) {
      if (!location || !location.x || !location.y) {
        return null;
      }
      // Return WKT POINT format
      return `POINT (${location.x} ${location.y})`;
    },

    // Fallback method to center view when centerView event fails
    fallbackCenterView(location) {
      try {
        // Try to access the OpenLayers view directly
        const viewComponent = this.$parent?.$refs?.view ||
                             document.querySelector('.ol-viewport')?.parentElement;

        if (viewComponent && typeof viewComponent.setCenter === 'function') {
          console.log('Using fallback center view method');
          viewComponent.setCenter([location.x, location.y]);
        } else if (viewComponent && viewComponent.$emit) {
          console.log('Using event-based center view method');
          viewComponent.$emit('update:center', [location.x, location.y]);
        } else {
          console.log('Fallback center view not available');

          // Last resort: emit selectAnnotation event
          const annotationData = {
            id: this.currentSelectedAnnotation.id,
            image: this.getImageId(),
            location: this.currentSelectedAnnotation.location || this.convertLocationToWKT(location), // Preserve original
            x: location.x,
            y: location.y
          };

          // Emit selectAnnotation for navigation and centering
          this.$eventBus?.$emit('selectAnnotation', {
            index: this.index,
            annot: annotationData,
            center: true
          });

          // Also emit selectAnnotationInLayer for proper cell highlighting
          this.$eventBus?.$emit('selectAnnotationInLayer', {
            index: this.index,
            annot: annotationData
          });
        }
      } catch (error) {
        console.error('Fallback center view failed:', error);
      }
    },

    // Check if a term ID is currently assigned to the selected annotation
    isCurrentTerm(termId) {
      if (!this.currentSelectedAnnotation || !this.currentSelectedAnnotation.terms) {
        return false;
      }
      return this.currentSelectedAnnotation.terms.some(term =>
        term.id === parseInt(termId) || term.id === termId
      );
    },

    // Handle term selection change
    async onTermSelectionChange() {
      if (!this.selectedNewTerm || !this.currentSelectedAnnotation) {
        return;
      }

      // Prevent updating to the same term
      if (this.isCurrentTerm(this.selectedNewTerm)) {
        this.selectedNewTerm = '';
        this.$notify({
          type: 'warning',
          text: 'This term is already assigned to the annotation.'
        });
        return;
      }

      try {
        this.isUpdatingAnnotation = true;

        // Get the new term details
        const newTermId = parseInt(this.selectedNewTerm);
        const newTerm = this.findTermById(newTermId);

        if (!newTerm) {
          throw new Error('Selected term not found');
        }

        // Update the annotation
        await this.updateAnnotationTerm(this.currentSelectedAnnotation, newTermId, newTerm);

        // Show success notification
        this.$notify({
          type: 'success',
          text: `Successfully updated annotation #${this.currentSelectedAnnotation.id} to "${newTerm.name}"`
        });

        // Reset selection
        this.selectedNewTerm = '';

        // Emit event to refresh other components
        this.$emit('annotation-updated', {
          annotationId: this.currentSelectedAnnotation.id,
          newTerm: newTerm
        });

      } catch (error) {
        console.error('Failed to update annotation term:', error);
        this.$notify({
          type: 'error',
          text: 'Failed to update annotation. Please try again.'
        });
        this.selectedNewTerm = '';
      } finally {
        this.isUpdatingAnnotation = false;
      }
    },

    // Find term by ID across all categories
    findTermById(termId) {
      for (const category of this.termCategories) {
        const term = category.terms.find(t => t.id === parseInt(termId) || t.id === termId);
        if (term) {
          return term;
        }
      }
      return null;
    },

    // Update annotation term (this would integrate with Cytomine API)
    async updateAnnotationTerm(annotation, newTermId, newTerm) {
      // For now, update the local annotation object
      // In a real implementation, this would call the Cytomine API

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update local annotation object
      if (annotation.terms && annotation.terms.length > 0) {
        // Replace the first term (most common case for blood cell classification)
        annotation.terms[0] = {
          id: newTermId,
          name: newTerm.originalName || newTerm.name,
          rate: annotation.terms[0].rate || 1.0 // Keep confidence if available
        };
      } else {
        // Add new term if none existed
        annotation.terms = [{
          id: newTermId,
          name: newTerm.originalName || newTerm.name,
          rate: 1.0
        }];
      }

      // Update in the annotations list as well
      const listAnnotation = this.allAnnotations.find(a => a.id === annotation.id);
      if (listAnnotation) {
        listAnnotation.terms = [...annotation.terms];
      }

      // Update in filtered list
      const filteredAnnotation = this.filteredAnnotations.find(a => a.id === annotation.id);
      if (filteredAnnotation) {
        filteredAnnotation.terms = [...annotation.terms];
      }

      // TODO: Integrate with Cytomine API
      // Example API call (uncomment and modify when integrating):
      /*
      const updatedAnnotation = await AnnotationTerm.save({
        annotation: annotation.id,
        term: newTermId,
        expectedTerm: annotation.terms[0]?.id, // For optimistic updates
        rate: 1.0
      });
      */

      console.log(`Updated annotation ${annotation.id} to term ${newTermId} (${newTerm.name})`);
    },

    // Format location display with better UX
    getFormattedLocation(annotation) {
      if (!annotation || !annotation.location) {
        return 'Location: Not available';
      }

      const location = this.parseAnnotationLocation(annotation.location, annotation);
      if (!location || (!location.x && !location.y)) {
        return 'Location: Not available';
      }

      return `Location: (${Math.round(location.x)}, ${Math.round(location.y)})`;
    },

    getTermDisplayName(termId) {
      const term = this.availableAnnotationTerms.find(t => t.id === termId);
      return term ? term.name : `Term #${termId}`;
    },

    parseWKTLocation(wktString) {
      // Parse WKT (Well-Known Text) string to extract coordinates
      try {
        if (!wktString || typeof wktString !== 'string') {
          return null;
        }

        // Handle POINT geometry: "POINT (x y)"
        const pointMatch = wktString.match(/POINT\s*\(\s*([^\)]+)\)/i);
        if (pointMatch) {
          const coords = pointMatch[1].trim().split(/\s+/);
          if (coords.length >= 2) {
            return {
              x: parseFloat(coords[0]),
              y: parseFloat(coords[1])
            };
          }
        }

        // Handle POLYGON geometry - get centroid
        const polygonMatch = wktString.match(/POLYGON\s*\(\(([^\)]+)\)\)/i);
        if (polygonMatch) {
          const coordsString = polygonMatch[1];
          const coordPairs = coordsString.split(',');
          let sumX = 0, sumY = 0, count = 0;

          for (const pair of coordPairs) {
            const coords = pair.trim().split(/\s+/);
            if (coords.length >= 2) {
              sumX += parseFloat(coords[0]);
              sumY += parseFloat(coords[1]);
              count++;
            }
          }

          if (count > 0) {
            return {
              x: sumX / count,
              y: sumY / count
            };
          }
        }

        console.warn('Could not parse WKT location:', wktString);
        return null;
      } catch (error) {
        console.error('Error parsing WKT location:', error);
        return null;
      }
    },

    toggleAnnotationDetails(annotationId) {
      this.$set(this.expandedAnnotations, annotationId, !this.expandedAnnotations[annotationId]);
    },

    getAnnotationThumbnailStyle(annotation) {
      // Create thumbnail style similar to annot-preview
      const thumbnailSize = 64;
      const imageId = this.getImageId();

      if (!imageId || !annotation.id) {
        return {
          width: thumbnailSize + 'px',
          height: thumbnailSize + 'px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6c757d',
          fontSize: '12px'
        };
      }

      // Build the crop URL similar to AnnotationPreview.vue
      const serverUrl = this.cytomineApiBaseUrl || '';
      let cropUrl = `${serverUrl}/api/userannotation/${annotation.id}/crop.jpg?maxSize=${thumbnailSize}`;

      // Use the appendShortTermToken utility to properly add authentication
      const urlWithAuth = appendShortTermToken(cropUrl, this.shortTermToken);

      // console.log(`Annotation ${annotation.id} thumbnail URL:`, urlWithAuth); // Disabled to reduce console noise

      const baseStyle = {
        backgroundImage: `url(${urlWithAuth})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        width: thumbnailSize + 'px',
        height: thumbnailSize + 'px',
        border: '3px solid white',
        boxShadow: '0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1)',
        backgroundColor: '#f8f9fa'
      };

      return baseStyle;
    },

    // Parse annotation location from various formats
    parseAnnotationLocation(location, annotation = null) {
      if (!location) {
        // Check if annotation has stored coordinates as fallback
        if (annotation && annotation._coordinates) {
          return annotation._coordinates;
        }
        return null;
      }

      // If location is already an object with x, y
      if (typeof location === 'object' && location.x !== undefined && location.y !== undefined) {
        return location;
      }

      // If location is a WKT string, parse it
      if (typeof location === 'string') {
        const parsed = this.parseWKTLocation(location);
        if (parsed) {
          return parsed;
        }

        // Try to extract center from POLYGON if POINT parsing failed
        if (location.includes('POLYGON')) {
          return this.extractPolygonCenter(location);
        }

        // If location is a string with coordinates (e.g., "x,y")
        if (location.includes(',')) {
          const coords = location.split(',');
          if (coords.length >= 2) {
            return {
              x: parseFloat(coords[0].trim()),
              y: parseFloat(coords[1].trim())
            };
          }
        }
      }

      // Check if annotation has stored coordinates as final fallback
      if (annotation && annotation._coordinates) {
        return annotation._coordinates;
      }

      return null;
    },

    // Extract center point from POLYGON WKT
    extractPolygonCenter(wktPolygon) {
      try {
        // Match POLYGON((x1 y1, x2 y2, ...))
        const match = wktPolygon.match(/POLYGON\s*\(\s*\((.*?)\)\s*\)/i);
        if (!match) return null;

        const coordinates = match[1];
        const points = coordinates.split(',').map(point => {
          const [x, y] = point.trim().split(/\s+/);
          return { x: parseFloat(x), y: parseFloat(y) };
        });

        if (points.length === 0) return null;

        // Calculate centroid
        let sumX = 0, sumY = 0, validPoints = 0;
        for (const point of points) {
          if (!isNaN(point.x) && !isNaN(point.y)) {
            sumX += point.x;
            sumY += point.y;
            validPoints++;
          }
        }

        if (validPoints === 0) return null;

        return {
          x: sumX / validPoints,
          y: sumY / validPoints
        };
      } catch (error) {
        console.warn('Failed to extract polygon center:', error);
        return null;
      }
    },

    // Get image ID from multiple possible sources
    getImageId() {
      // Try multiple sources for image ID
      let imageId = null;

      // Method 1: From imageWrapper.imageInstance.id (most reliable)
      if (this.imageWrapper?.imageInstance?.id) {
        imageId = this.imageWrapper.imageInstance.id;
      }
      // Method 2: From direct image object
      else if (this.image?.id) {
        imageId = this.image.id;
      }
      // Method 3: From imageWrapper.id (sometimes present)
      else if (this.imageWrapper?.id) {
        imageId = this.imageWrapper.id;
      }
      // Method 4: From route parameters
      else if (this.$route?.params?.idImage) {
        imageId = parseInt(this.$route.params.idImage);
      }
      // Method 5: From URL parameters
      else if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const urlImageId = urlParams.get('imageId') || urlParams.get('image');
        if (urlImageId) {
          imageId = parseInt(urlImageId);
        }
      }

      // Debug only if needed - disabled to reduce console noise
      // console.log('Image ID detection:', { finalImageId: imageId });

      return imageId;
    },

    // AI Comment methods
    startEditingAIComment(commentType) {
      this.editingAIComments[commentType] = true;
      this.currentAICommentText = this.aiComments[commentType] || '';
    },

    cancelEditAIComment(commentType) {
      this.editingAIComments[commentType] = false;
      this.currentAICommentText = '';
    },

    async saveAICommentInline(commentType) {
      const projectId = this.project?.id;

      if (!commentType) {
        console.error('Missing commentType for saving AI comment');
        return;
      }

      if (!projectId) {
        console.warn('No project ID available, saving AI comment locally only');
      }

      // Update status to 'in_progress' when user writes AI comment
      if (this.analysisSession.status === 'pending_review' && this.currentAICommentText.trim()) {
        this.updateAnalysisStatus('in_progress');
      }

      // Update AI comment data
      this.aiComments[commentType] = this.currentAICommentText.trim();
      this.aiCommentMetadata[commentType] = {
        timestamp: new Date().toISOString(),
        userId: this.currentUser?.id || 'unknown',
        userName: this.currentUser?.name || 'Unknown User',
        projectId: projectId || 'no-project'
      };

      // Save to Cytomine database
      try {
        await this.saveAICommentsToDatabase();
        console.log(`AI comment saved for ${commentType} in database`);
      } catch (error) {
        console.error('Failed to save AI comment:', error);
      }

      // Exit editing mode
      this.editingAIComments[commentType] = false;
      this.currentAICommentText = '';
    },

    async deleteAICommentInline(commentType) {
      if (!commentType) return;

      // Clear AI comment data
      this.aiComments[commentType] = '';
      this.aiCommentMetadata[commentType] = null;

      // Update database
      try {
        await this.saveAICommentsToDatabase();
        console.log(`AI comment deleted for ${commentType}`);
      } catch (error) {
        console.error('Failed to delete AI comment:', error);
      }

      // Exit editing mode
      this.editingAIComments[commentType] = false;
      this.currentAICommentText = '';
    },

    getAICommentDate(commentType) {
      const metadata = this.aiCommentMetadata[commentType];
      if (!metadata || !metadata.timestamp) return '';

      const date = new Date(metadata.timestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    },

    // Storage methods for AI comments - Using Cytomine Properties
    async saveAICommentsToDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot save AI comments: missing project or image ID');
          return;
        }

        // Save each AI comment as a separate property
        const savePromises = Object.keys(this.aiComments).map(async (commentType) => {
          const comment = this.aiComments[commentType];
          const metadata = this.aiCommentMetadata[commentType];

          if (comment && comment.trim()) {
            // Save AI comment with metadata
            const propertyKey = `bloodfilm_ai_comment_${imageId}_${commentType}`;
            const propertyValue = JSON.stringify({
              comment: comment,
              metadata: metadata
            });

            console.log(`Saving AI comment to database: ${propertyKey}`, propertyValue);

            const property = new Property({
              object: this.project,
              key: propertyKey,
              value: propertyValue
            });

            await property.save();
          } else if (metadata === null) {
            // Delete property if comment was deleted
            const propertyKey = `bloodfilm_ai_comment_${imageId}_${commentType}`;
            console.log(`Deleting AI comment property: ${propertyKey}`);

            try {
              const property = new Property({
                object: this.project,
                key: propertyKey
              });
              await property.delete();
            } catch (error) {
              console.log('Property does not exist or already deleted');
            }
          }
        });

        await Promise.all(savePromises);
        console.log('All AI comments saved to database');

      } catch (error) {
        console.error('Error saving AI comments to database:', error);
        throw error;
      }
    },

    async loadAICommentsFromDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot load AI comments: missing project or image ID');
          return;
        }

        console.log('Loading AI comments from database...');

        // Load all properties for this project
        const propertyCollection = new PropertyCollection({object: this.project});
        await propertyCollection.fetchAll();

        const properties = propertyCollection.array || [];
        const aiCommentPrefix = `bloodfilm_ai_comment_${imageId}_`;

        // Find AI comment properties
        const aiCommentProperties = properties.filter(property =>
          property.key && property.key.startsWith(aiCommentPrefix)
        );

        console.log(`Found ${aiCommentProperties.length} AI comment properties`);

        // Process each AI comment property
        aiCommentProperties.forEach(property => {
          try {
            const commentType = property.key.replace(aiCommentPrefix, '');
            const data = JSON.parse(property.value);

            this.aiComments[commentType] = data.comment || '';
            this.aiCommentMetadata[commentType] = data.metadata || null;

            console.log(`Loaded AI comment for ${commentType}:`, data.comment);
          } catch (parseError) {
            console.error('Error parsing stored AI comment data:', parseError);
          }
        });

      } catch (error) {
        console.error('Error loading AI comments from database:', error);
      }
    },

    // Storage methods for comments - Using Cytomine Properties
    async saveCommentsToDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot save comments: missing project or image ID');
          return;
        }

        // Save each tab comment as a separate property
        const savePromises = Object.keys(this.tabComments).map(async (tabId) => {
          const comment = this.tabComments[tabId];
          const metadata = this.commentMetadata[tabId];

          if (comment && comment.trim()) {
            // Save comment with metadata
            const propertyKey = `bloodfilm_comment_${imageId}_${tabId}`;
            const propertyValue = JSON.stringify({
              comment: comment,
              metadata: metadata,
              lastModified: new Date().toISOString()
            });

            try {
              // Create or update property
              const property = new Property({
                object: this.project,
                key: propertyKey,
                value: propertyValue
              });

              await property.save();
              console.log(`Saved comment for ${tabId} tab to database`);
            } catch (error) {
              console.error(`Failed to save comment for ${tabId}:`, error);
            }
          } else if (metadata) {
            // Delete property if comment is empty
            try {
              await this.deleteCommentProperty(tabId, imageId);
            } catch (error) {
              console.error(`Failed to delete empty comment for ${tabId}:`, error);
            }
          }
        });

        await Promise.allSettled(savePromises);
        console.log('All comments saved to database');

      } catch (error) {
        console.error('Error saving comments to database:', error);
        throw error;
      }
    },

    async loadCommentsFromDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.log('No project or image ID available for loading comments');
          return;
        }

        // Load all properties for this project
        const propertyCollection = new PropertyCollection({object: this.project});
        await propertyCollection.fetchAll();

        const properties = propertyCollection.array || [];
        const commentPrefix = `bloodfilm_comment_${imageId}_`;

        // Filter and process comment properties
        properties.forEach(property => {
          if (property.key.startsWith(commentPrefix)) {
            try {
              const tabId = property.key.replace(commentPrefix, '');
              const data = JSON.parse(property.value);

              this.tabComments[tabId] = data.comment || '';
              this.commentMetadata[tabId] = data.metadata || null;

              console.log(`Loaded comment for ${tabId} tab from database`);
            } catch (parseError) {
              console.error(`Error parsing comment property ${property.key}:`, parseError);
            }
          }
        });

        console.log('Comments loaded from database');

      } catch (error) {
        console.error('Failed to load comments from database:', error);
      }
    },

    async deleteCommentProperty(tabId, imageId) {
      try {
        const propertyKey = `bloodfilm_comment_${imageId}_${tabId}`;

        // Find and delete the property
        const propertyCollection = new PropertyCollection({object: this.project});
        await propertyCollection.fetchAll();

        const properties = propertyCollection.array || [];
        const property = properties.find(p => p.key === propertyKey);

        if (property) {
          await Property.delete(property.id, this.project);
          console.log(`Deleted comment property for ${tabId}`);
        }
      } catch (error) {
        console.error(`Error deleting comment property for ${tabId}:`, error);
      }
    },

    // RBC Morphology Score Database Storage Methods - Using Cytomine Properties
    async saveRBCMorphologyScoreToDatabase(morphologyName, scoreData) {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Missing project or image ID for RBC morphology score storage');
          return;
        }

        const propertyKey = `bloodfilm_rbc_morphology_${imageId}_${morphologyName}`;
        const propertyValue = JSON.stringify(scoreData);

        try {
          // Create or update property using Cytomine Property class
          const property = new Property({
            object: this.project,
            key: propertyKey,
            value: propertyValue
          });

          await property.save();
          console.log(`Saved RBC morphology score for ${morphologyName} to database`);

        } catch (error) {
          console.error('Failed to save RBC morphology score to database:', error);
          throw error;
        }

      } catch (error) {
        console.error('Error in saveRBCMorphologyScoreToDatabase:', error);
        throw error;
      }
    },

    async loadRBCMorphologyScoresFromDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Missing project or image ID for loading RBC morphology scores');
          return;
        }

        // Load all properties for this project using PropertyCollection
        const propertyCollection = new PropertyCollection({object: this.project});
        await propertyCollection.fetchAll();

        const properties = propertyCollection.array || [];
        const propertyPrefix = `bloodfilm_rbc_morphology_${imageId}_`;

        // Filter and process RBC morphology properties
        properties.forEach(property => {
          if (property.key.startsWith(propertyPrefix)) {
            try {
              const morphologyName = property.key.replace(propertyPrefix, '');
              const scoreData = JSON.parse(property.value);

              this.rbcMorphologyScores[morphologyName] = scoreData;

              // Apply score to UI
              this.rbcMorphologyCategories.forEach(category => {
                const item = category.items.find(i => i.name === morphologyName);
                if (item) {
                  item.score = scoreData.score;
                }
              });

              console.log(`Loaded RBC morphology score for ${morphologyName} from database`);
            } catch (parseError) {
              console.error(`Error parsing stored score for ${property.key}:`, parseError);
            }
          }
        });

        console.log('RBC morphology scores loaded from database');

      } catch (error) {
        console.error('Error in loadRBCMorphologyScoresFromDatabase:', error);
      }
    },

    // Storage methods for RBC morphology scores (localStorage fallback)
    async saveRBCMorphologyScoresToStorage() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot save RBC scores: missing project or image ID');
          return;
        }

        const storageKey = `bloodfilm_rbc_morphology_${projectId}_${imageId}`;
        const scoreData = {
          scores: this.rbcMorphologyScores,
          lastModified: new Date().toISOString(),
          projectId: projectId,
          imageId: imageId
        };

        localStorage.setItem(storageKey, JSON.stringify(scoreData));
        console.log('RBC morphology scores saved to localStorage');

      } catch (error) {
        console.error('Error saving RBC morphology scores to storage:', error);
      }
    },

    async loadRBCMorphologyScoresFromStorage() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot load RBC scores: missing project or image ID');
          return;
        }

        const storageKey = `bloodfilm_rbc_morphology_${projectId}_${imageId}`;
        const stored = localStorage.getItem(storageKey);

        if (stored) {
          const scoreData = JSON.parse(stored);
          this.rbcMorphologyScores = { ...scoreData.scores };

          // Apply scores to UI
          Object.keys(this.rbcMorphologyScores).forEach(morphologyName => {
            const scoreInfo = this.rbcMorphologyScores[morphologyName];

            this.rbcMorphologyCategories.forEach(category => {
              const item = category.items.find(i => i.name === morphologyName);
              if (item) {
                item.score = scoreInfo.score;
              }
            });
          });

          console.log('RBC morphology scores loaded from localStorage');
        }

      } catch (error) {
        console.error('Error loading RBC morphology scores from storage:', error);
      }
    },

    // Batch save multiple RBC morphology scores (for performance)
    async batchSaveRBCMorphologyScores() {
      try {
        console.log('Batch saving RBC morphology scores...');

        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot batch save: missing project or image ID');
          return;
        }

        const savePromises = Object.keys(this.rbcMorphologyScores).map(morphologyName => {
          const scoreData = this.rbcMorphologyScores[morphologyName];
          const propertyKey = `bloodfilm_rbc_morphology_${imageId}_${morphologyName}`;
          const propertyValue = JSON.stringify(scoreData);

          const property = new Property({
            object: this.project,
            key: propertyKey,
            value: propertyValue
          });

          return property.save()
            .catch(error => {
              console.warn(`Failed to save ${morphologyName} to database:`, error);
            });
        });

        await Promise.allSettled(savePromises);
        console.log('Batch save to database completed');

      } catch (error) {
        console.error('Error in batch save RBC morphology scores:', error);
      }
    },

    // Clear all RBC morphology scores (for testing/reset)
    async clearAllRBCMorphologyScores() {
      if (confirm('Are you sure you want to clear all RBC morphology scores? This action cannot be undone.')) {
        this.rbcMorphologyScores = {};

        // Clear from UI
        this.rbcMorphologyCategories.forEach(category => {
          category.items.forEach(item => {
            item.score = null;
          });
        });

        // Clear from storage
        await this.saveRBCMorphologyScoresToStorage();

        console.log('All RBC morphology scores cleared');
      }
    },

    // Clear all RBC morphology scores from database (used by auto-calculate)
    async clearRBCMorphologyScoresFromDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot clear RBC scores from database: missing project or image ID');
          return;
        }

        console.log('Clearing all RBC morphology scores from database...');

        // Load all properties for this project to find RBC morphology properties
        const propertyCollection = new PropertyCollection({object: this.project});
        await propertyCollection.fetchAll();

        const properties = propertyCollection.array || [];
        const rbcMorphologyPrefix = `bloodfilm_rbc_morphology_${imageId}_`;

        // Find all RBC morphology properties
        const rbcProperties = properties.filter(property =>
          property.key && property.key.startsWith(rbcMorphologyPrefix)
        );

        console.log(`Found ${rbcProperties.length} RBC morphology properties to delete`);

        // Delete all RBC morphology properties
        const deletePromises = rbcProperties.map(async (property) => {
          try {
            console.log(`Deleting RBC property: ${property.key}`);
            await property.delete();
          } catch (error) {
            console.warn(`Failed to delete property ${property.key}:`, error);
          }
        });

        await Promise.all(deletePromises);
        console.log('All RBC morphology scores cleared from database');

      } catch (error) {
        console.error('Error clearing RBC scores from database:', error);
        throw error;
      }
    },

    // PLT Count Management Methods
    async updatePLTCount(value) {
      // Update status to 'in_progress' when user starts editing
      if (this.analysisSession.status === 'pending_review') {
        this.updateAnalysisStatus('in_progress');
      }

      const numValue = parseInt(value);

      if (isNaN(numValue)) {
        // If invalid input, reset to auto-calculated
        this.pltData.averageCount = null;
        this.pltData.source = 'auto';
      } else {
        // User manually set a value
        this.pltData.averageCount = numValue;
        this.pltData.source = 'manual';
        this.pltData.lastModified = new Date().toISOString();
        this.pltData.userId = this.currentUser?.id || 'unknown';
        this.pltData.userName = this.currentUser?.name || 'Unknown User';
      }

      // Save to database
      try {
        await this.savePLTDataToDatabase();
        console.log('PLT count updated and saved to database');
      } catch (error) {
        console.error('Failed to save PLT count to database:', error);
      }
    },

    // PLT Database Storage Methods
    async savePLTDataToDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot save PLT data: missing project or image ID');
          return;
        }

        const propertyKey = `bloodfilm_plt_data_${imageId}`;
        const propertyValue = JSON.stringify(this.pltData);

        const property = new Property({
          object: this.project,
          key: propertyKey,
          value: propertyValue
        });

        await property.save();
        console.log('PLT data saved to database');

      } catch (error) {
        console.error('Error saving PLT data to database:', error);
        throw error;
      }
    },

    async loadPLTDataFromDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot load PLT data: missing project or image ID');
          return;
        }

        // Load all properties for this project
        const propertyCollection = new PropertyCollection({object: this.project});
        await propertyCollection.fetchAll();

        const properties = propertyCollection.array || [];
        const propertyKey = `bloodfilm_plt_data_${imageId}`;

        // Find PLT data property
        const pltProperty = properties.find(property => property.key === propertyKey);

        if (pltProperty) {
          try {
            const pltData = JSON.parse(pltProperty.value);
            this.pltData = { ...this.pltData, ...pltData };

            console.log('PLT data loaded from database:', this.pltData);
          } catch (parseError) {
            console.error('Error parsing stored PLT data:', parseError);
          }
        } else {
          console.log('No stored PLT data found, using defaults');
        }

      } catch (error) {
        console.error('Error loading PLT data from database:', error);
      }
    },

    async initializeProstateAnalysis() {
      try {
        console.log('Initializing prostate analysis...');

        // Reset prostate data
        this.initializeProstateData();

        // Load annotations
        await this.loadProstateAnnotations();

        this.$notify({
          type: 'success',
          text: 'Prostate analysis initialized successfully'
        });

      } catch (error) {
        console.error('Error initializing prostate analysis:', error);
        this.$notify({
          type: 'error',
          text: 'Failed to initialize prostate analysis'
        });
      }
    },

    async refreshReportData() {
      console.log('Refreshing report data...');

      // Force recalculate percentages
      if (this.prostateData.totalMalignantArea > 0) {
        this.calculatePatternPercentages();
      }

      console.log('Report data refreshed:', {
        percentages: this.prostateData.percentages,
        areas: this.prostateData.areas
      });
    },

    // Prostate-specific methods
    switchProstateTab(tabId) {
      console.log('Switching to prostate tab:', tabId);

      // Reset any problematic states
      this.currentSelectedAnnotation = null;

      this.$nextTick(() => {
        this.activeProstateTab = tabId;

        // Refresh data when switching to report tab
        if (tabId === 'report') {
          console.log('Report tab activated - current data:', {
            gleasonScore: this.prostateData.gleasonScore,
            totalMalignantArea: this.prostateData.totalMalignantArea,
            areas: this.prostateData.areas
          });
          this.refreshReportData();
        }

        // Load data for specific tabs
        try {
          if (tabId === 'annotations') {
            this.loadProstateAnnotations();
          } else if (tabId === 'scoring' || tabId === 'report') {
            if (this.prostateData.totalMalignantArea > 0) {
              this.refreshProstateCalculations();
            }
          }
        } catch (error) {
          console.error('Error in prostate tab switch:', error);
          this.$notify({
            type: 'error',
            text: 'Error loading prostate tab data'
          });
        }
      });
    },

    refreshProstateCalculations() {
      console.log('Refreshing all prostate calculations...');

      // Recalculate areas if we have annotations
      if (this.allProstateAnnotations.length > 0) {
        this.calculateProstateAreas();
      } else {
        // If no annotations but we have areas data, just recalculate percentages
        this.calculatePatternPercentages();
      }

      // Recalculate Gleason score if we have malignant area
      if (this.prostateData.totalMalignantArea > 0) {
        this.calculateGleasonScore();
      }

      console.log('Prostate calculations refreshed:', {
        areas: this.prostateData.areas,
        percentages: this.prostateData.percentages,
        gleasonScore: this.prostateData.gleasonScore
      });
    },

    previousProstateStep() {
      const tabOrder = ['scoring', 'report'];
      const currentIndex = tabOrder.indexOf(this.activeProstateTab);

      if (currentIndex > 0) {
        const previousTab = tabOrder[currentIndex - 1];
        this.switchProstateTab(previousTab);
      }
    },

    nextProstateStep() {
      const tabOrder = ['scoring', 'report'];
      const currentIndex = tabOrder.indexOf(this.activeProstateTab);

      if (currentIndex < tabOrder.length - 1) {
        const nextTab = tabOrder[currentIndex + 1];
        this.switchProstateTab(nextTab);
      } else {
        // Generate report when on the last step
        this.generateProstateReportPDF();
      }
    },

    // Prostate annotation management
    async loadProstateAnnotations() {
      if (this.isLoadingProstateAnnotations) {
        console.log('Prostate annotation loading already in progress');
        return;
      }

      // === ADD VALIDATION ===
      const projectId = this.project?.id;
      const imageId = this.image?.id;

      if (!projectId || !imageId) {
        console.error('Missing project or image ID for prostate annotations:', {
          projectId,
          imageId,
          project: this.project,
          image: this.image
        });
        this.$notify({
          type: 'error',
          text: 'Cannot load prostate annotations: Missing project or image information'
        });
        return;
      }

      this.isLoadingProstateAnnotations = true;

      try {
        console.log('Loading prostate annotations for:', { projectId, imageId });

        // Reset annotations
        this.allProstateAnnotations = [];
        this.filteredProstateAnnotations = [];

        // Try to load real annotations first
        await this.loadRealProstateAnnotations();

        // If no real annotations found, provide sample data
        /*if (this.allProstateAnnotations.length === 0) {
          console.log('No prostate annotations found in Cytomine, creating sample data...');
          await this.createSampleProstateAnnotations();
        }*/

        // Apply filtering
        this.filterProstateAnnotations();

        // Update counts after loading
        this.updateProstateAnnotationCounts();

        console.log(`Loaded ${this.allProstateAnnotations.length} prostate annotations`);

        this.$notify({
          type: 'success',
          text: `Loaded ${this.allProstateAnnotations.length} prostate annotations`
        });

      } catch (error) {
        console.error('Error loading prostate annotations:', error);
        this.$notify({
          type: 'error',
          text: `Failed to load prostate annotations: ${error.message}`
        });
      } finally {
        this.isLoadingProstateAnnotations = false;
      }
    },

    async loadRealProstateAnnotations() {
      try {
        const imageId = this.image?.id;
        const projectId = this.project?.id;

        if (!imageId || !projectId) {
          console.warn('Missing image or project ID for prostate annotation loading');
          throw new Error('Missing image or project ID');
        }

        console.log(`Loading prostate annotations for image ${imageId}, project ${projectId}`);

        const { AnnotationCollection } = await import('cytomine-client');
        const collection = new AnnotationCollection({
          project: projectId,
          image: imageId,
          showWKT: true,
          showTerm: true,
          showGIS: false,
          showTag: false,
          max: 100000000
        });

        console.log('Fetching annotation collection...');
        const result = await collection.fetchAll();
        const annotations = result.array || [];

        console.log(`Found ${annotations.length} total annotations, filtering for prostate terms...`);

        // Filter for prostate-specific terms and transform
        const prostateAnnotations = annotations
          .filter(annotation => {
            try {
              return this.isProstateAnnotation(annotation);
            } catch (error) {
              console.warn('Error checking if annotation is prostate:', error);
              return false;
            }
          })
          .map(annotation => this.transformProstateAnnotation(annotation))
          .filter(annotation => annotation !== null); // Remove failed transformations

        this.allProstateAnnotations = prostateAnnotations;

        console.log(`Filtered to ${this.allProstateAnnotations.length} prostate annotations`);

        // Update annotation counts for display
        this.updateProstateAnnotationCounts();

      } catch (error) {
        console.error('Failed to load prostate annotations from Cytomine:', error);
        throw new Error(`Server error: ${error.message}`);
      }
    },

    // Safe method to load all prostate annotations
    async loadAllProstateAnnotations() {
      console.log('=== Loading all prostate annotations ===');

      // Reset loading state if stuck
      if (this.isLoadingProstateAnnotations) {
        this.isLoadingProstateAnnotations = false;
        await this.$nextTick();
      }

      // Clear existing data
      this.allProstateAnnotations = [];
      this.filteredProstateAnnotations = [];
      this.selectedProstateAnnotationTerm = '';

      this.isLoadingProstateAnnotations = true;

      try {
        // Try to load real annotations first
        await this.loadRealProstateAnnotations();

        // If no real annotations found, provide sample data
        /*if (this.allProstateAnnotations.length === 0) {
          console.log('No prostate annotations found in Cytomine, creating sample data...');
          await this.createSampleProstateAnnotations();
        }*/

        // Apply filtering
        this.filterProstateAnnotations();

        // Update counts after loading
        this.updateProstateAnnotationCounts();

        console.log(`Loaded ${this.allProstateAnnotations.length} prostate annotations`);

        this.$notify({
          type: 'success',
          text: `Loaded ${this.allProstateAnnotations.length} prostate annotations`
        });

      } catch (error) {
        console.error('Error loading prostate annotations:', error);
        this.$notify({
          type: 'error',
          text: `Failed to load prostate annotations: ${error.message}`
        });
      } finally {
        this.isLoadingProstateAnnotations = false;
      }
    },

    isProstateAnnotation(annotation) {
      if (!annotation.term || !Array.isArray(annotation.term)) {
        console.log(`Annotation ${annotation.id} has no terms array`);
        return false;
      }

      // Check if annotation has prostate-related terms
      const hasProstateTerm = annotation.term.some(termId => {
        const termName = this.getTermNameById(termId);
        if (!termName) {
          console.log(`Term ID ${termId} not found in ontology`);
          return false;
        }

        const lowerTermName = termName.toLowerCase();
        const isProstate = (
          lowerTermName.includes('gleason') ||
          lowerTermName.includes('stroma') ||
          lowerTermName.includes('benign') ||
          lowerTermName.includes('prostate') ||
          lowerTermName.includes('pattern') ||
          lowerTermName.includes('g3') ||
          lowerTermName.includes('g4') ||
          lowerTermName.includes('g5') ||
          // Add more inclusive terms
          lowerTermName.includes('acinar') ||
          lowerTermName.includes('cribriform') ||
          lowerTermName.includes('fused') ||
          lowerTermName.includes('gland')
        );

        if (isProstate) {
          console.log(`Annotation ${annotation.id} identified as prostate via term: ${termName}`);
        }

        return isProstate;
      });

      if (!hasProstateTerm) {
        console.log(`Annotation ${annotation.id} has no prostate terms. Terms:`, annotation.term.map(id => this.getTermNameById(id)));
      }

      return hasProstateTerm;
    },

    transformProstateAnnotation(annotation) {
      try {
        const location = this.parseAnnotationLocation(annotation.location, annotation);
        const area = this.calculateAnnotationArea(annotation);

        // Use the new method - handle case where it might not exist during initialization
        let term = 'unknown';
        if (typeof this.getProstateAnnotationTerm === 'function') {
          term = this.getProstateAnnotationTerm(annotation);
        } else {
          console.warn('getProstateAnnotationTerm not available, using fallback');
          term = this.getProstateAnnotationTermFallback(annotation);
        }

        return {
          id: annotation.id,
          terms: annotation.term ? annotation.term.map(termId => ({
            id: termId,
            name: this.getTermNameById(termId) || `Term ${termId}`
          })) : [],
          location: annotation.location,
          user: annotation.user || annotation.userId || this.currentUser?.id,
          userId: annotation.user || annotation.userId || this.currentUser?.id,
          username: annotation.username || 'System',
          slice: annotation.slice || this.slices?.[0]?.id,
          image: annotation.image || this.image?.id,
          type: 'USER',
          created: annotation.created || new Date().toISOString(),
          updated: annotation.updated || new Date().toISOString(),
          area: area,
          _coordinates: location,
          _originalAnnotation: annotation,
          _isProstateAnnotation: true
        };
      } catch (error) {
        console.error('Error transforming prostate annotation:', error, annotation);
        return null;
      }
    },

    updateProstateAnnotationCounts() {
      console.log('=== Updating prostate annotation counts ===');

      // Initialize counts if they don't exist
      if (!this.prostateData.annotationCounts) {
        this.prostateData.annotationCounts = {
          gleason3: 0,
          gleason4: 0,
          gleason5: 0,
          stroma: 0,
          benign: 0
        };
      }

      // Reset counts
      Object.keys(this.prostateData.annotationCounts).forEach(key => {
        this.prostateData.annotationCounts[key] = 0;
      });

      // Count annotations by type
      this.allProstateAnnotations.forEach(annotation => {
        try {
          let termType = 'unknown';
          if (typeof this.getProstateAnnotationTerm === 'function') {
            termType = this.getProstateAnnotationTerm(annotation);
          } else {
            termType = this.getProstateAnnotationTermFallback(annotation);
          }

          console.log(`Annotation ${annotation.id} has term: ${termType}`);

          if (this.prostateData.annotationCounts.hasOwnProperty(termType)) {
            this.prostateData.annotationCounts[termType]++;
          }
        } catch (error) {
          console.warn(`Error counting annotation ${annotation.id}:`, error);
        }
      });

      console.log('Prostate annotation counts updated:', this.prostateData.annotationCounts);
    },

    filterProstateAnnotations() {
      console.log('=== Filtering prostate annotations ===');
      console.log('Selected term:', this.selectedProstateAnnotationTerm);
      console.log('Total annotations before filter:', this.allProstateAnnotations.length);

      if (!this.selectedProstateAnnotationTerm) {
        this.filteredProstateAnnotations = [...this.allProstateAnnotations];
      } else {
        this.filteredProstateAnnotations = this.allProstateAnnotations.filter(annotation => {
          const annotationTerm = this.getProstateAnnotationTerm(annotation);
          const matches = annotationTerm === this.selectedProstateAnnotationTerm;

          if (matches) {
            console.log(`Annotation ${annotation.id} matches filter: ${annotationTerm}`);
          }

          return matches;
        });
      }

      console.log(`Prostate annotations filtered: ${this.filteredProstateAnnotations.length} of ${this.allProstateAnnotations.length}`);
    },

    // Area calculation methods based on PDF formulas
    calculateAnnotationArea(annotation) {
      try {
        const areaPx = this.calculateAreaFromWKT(annotation.location);
        // Convert to mm² using pixel size
        return areaPx * Math.pow(this.prostateData.pixelSizeMm, 2);
      } catch (error) {
        console.warn('Error calculating annotation area:', error);
        return 0;
      }
    },

    calculateAreaFromWKT(wktGeometry) {
      try {
        if (wktGeometry.includes('POLYGON')) {
          const match = wktGeometry.match(/POLYGON\s*\(\s*\((.*?)\)\s*\)/i);
          if (match) {
            const coords = match[1].split(',').map(coord => {
              const [x, y] = coord.trim().split(/\s+/);
              return { x: parseFloat(x), y: parseFloat(y) };
            });

            // Use shoelace formula for accurate polygon area
            return this.shoelaceArea(coords);
          }
        } else if (wktGeometry.includes('POINT')) {
          // For points, use a default small area
          return 100; // 10x10 pixel area
        }
        return 0;
      } catch (error) {
        console.warn('Error calculating area from WKT:', error);
        return 0;
      }
    },

    shoelaceArea(vertices) {
      let area = 0;
      const n = vertices.length;

      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += vertices[i].x * vertices[j].y;
        area -= vertices[j].x * vertices[i].y;
      }

      return Math.abs(area) / 2;
    },

    calculatePatternPercentages() {
      console.log('=== Calculating Pattern Percentages (respecting manual overrides) ===');

      if (!this.prostateData.percentages) {
        this.prostateData.percentages = {
          gleason3: 0,
          gleason4: 0,
          gleason5: 0
        };
      }

      const totalMalignant = this.prostateData.totalMalignantArea;

      if (totalMalignant > 0) {
        // Only calculate percentages for patterns that aren't manually overridden
        let totalAutoPercentage = 100;
        let autoPatterns = [];

        ['gleason3', 'gleason4', 'gleason5'].forEach(pattern => {
          if (this.manualOverrides.percentages[pattern]) {
            totalAutoPercentage -= this.prostateData.percentages[pattern];
          } else {
            autoPatterns.push(pattern);
          }
        });

        // Distribute remaining percentage among auto-calculated patterns
        if (autoPatterns.length > 0) {
          autoPatterns.forEach(pattern => {
            const area = this.prostateData.areas[pattern] || 0;
            this.prostateData.percentages[pattern] = (area / totalMalignant) * totalAutoPercentage;
          });
        }

        console.log('Calculated percentages (with overrides):', this.prostateData.percentages);
      } else {
        // Reset to 0 if no malignant area
        ['gleason3', 'gleason4', 'gleason5'].forEach(pattern => {
          if (!this.manualOverrides.percentages[pattern]) {
            this.prostateData.percentages[pattern] = 0;
          }
        });
      }

      this.$set(this.prostateData, 'percentages', {...this.prostateData.percentages});
    },

    updatePatternDistribution() {
      console.log('=== Updating Pattern Distribution for Scoring Tab ===');

      // Ensure the percentages are available for the scoring tab
      if (this.prostateData.percentages) {
        console.log('Current percentages for scoring:', this.prostateData.percentages);

        // Force Vue to react to changes in the percentages object
        this.$nextTick(() => {
          // This ensures the scoring tab re-renders with updated data
          if (this.activeProstateTab === 'scoring') {
            this.$forceUpdate();
          }
        });
      }
    },

    autoCalculateGleasonScore() {
      this.calculateProstateAreas();
      this.calculateGleasonScore();
    },

    calculateTotalTumorArea() {
      this.prostateData.totalTumorArea =
        (this.prostateData.areas.gleason3 || 0) +
        (this.prostateData.areas.gleason4 || 0) +
        (this.prostateData.areas.gleason5 || 0) +
        (this.prostateData.areas.stroma || 0) +
        (this.prostateData.areas.benign || 0);

      console.log('Total tumor area calculated:', {
        malignant: this.prostateData.totalMalignantArea,
        stroma: this.prostateData.areas.stroma,
        benign: this.prostateData.areas.benign,
        totalTumorArea: this.prostateData.totalTumorArea
      });
    },

    async calculateProstateAreas() {
      if (this.isCalculatingAreas) {
        console.log('Area calculation already in progress');
        return;
      }

      this.isCalculatingAreas = true;

      try {
        console.log('Calculating prostate areas...');

        // Reset areas
        this.prostateData.areas = {
          gleason3: 0,
          gleason4: 0,
          gleason5: 0,
          stroma: 0,
          benign: 0
        };

        // Reset counts
        this.prostateData.annotationCounts = {
          gleason3: 0,
          gleason4: 0,
          gleason5: 0,
          stroma: 0,
          benign: 0
        };

        // Calculate areas for each annotation
        this.allProstateAnnotations.forEach(annotation => {
          const term = this.getProstateAnnotationTerm(annotation);
          const area = annotation.area || this.calculateAnnotationArea(annotation);

          if (this.prostateData.areas.hasOwnProperty(term)) {
            this.prostateData.areas[term] += area;
            this.prostateData.annotationCounts[term]++;
          }
        });

        // Calculate totals
        this.prostateData.totalMalignantArea =
          this.prostateData.areas.gleason3 +
          this.prostateData.areas.gleason4 +
          this.prostateData.areas.gleason5;

        // === FIX: Calculate total tumor area ===
        this.calculateTotalTumorArea();

        this.prostateData.totalClassifiedArea =
          this.prostateData.totalMalignantArea +
          this.prostateData.areas.stroma +
          this.prostateData.areas.benign;

        // Calculate percentages
        this.calculatePatternPercentages();

        console.log('Prostate areas calculated:', {
          areas: this.prostateData.areas,
          totalMalignant: this.prostateData.totalMalignantArea,
          totalTumor: this.prostateData.totalTumorArea, // This should now have a value
          totalClassified: this.prostateData.totalClassifiedArea,
          percentages: this.prostateData.percentages,
          tumorPercentage: this.prostateData.tumorPercentage
        });

        this.$notify({
          type: 'success',
          text: `Areas calculated: ${this.prostateData.totalMalignantArea.toFixed(3)} mm² malignant tissue`
        });

      } catch (error) {
        console.error('Error calculating prostate areas:', error);
        this.$notify({
          type: 'error',
          text: 'Failed to calculate areas'
        });
        throw error;
      } finally {
        this.isCalculatingAreas = false;
      }
    },

    getPatternPercentage(patternId) {
      const patternKey = 'gleason' + patternId;
      return (this.prostateData.percentages && this.prostateData.percentages[patternKey]) || 0;
    },

    getPatternPercentageText(patternId) {
      const patternKey = 'gleason' + patternId;
      const percentage = (this.prostateData.percentages && this.prostateData.percentages[patternKey]) || 0;
      return percentage.toFixed(1);
    },

    // Helper method to get term name by ID
    getProstateTermNameById(termId) {
      // First try to find in availableAnnotationTerms
      const foundTerm = this.availableAnnotationTerms.find(t => t.id == termId);
      if (foundTerm) {
        return foundTerm.name.toLowerCase();
      }

      // Fallback mapping for common prostate terms
      const termMap = {
        'gleason3': 'gleason pattern 3',
        'gleason4': 'gleason pattern 4',
        'gleason5': 'gleason pattern 5',
        'stroma': 'stroma',
        'benign': 'benign'
      };

      return termMap[termId] || `term ${termId}`;
    },

    getProstateAnnotationTermName(annotation) {
      const term = this.getProstateAnnotationTerm(annotation);
      const termMap = {
        'gleason3': 'Gleason 3',
        'gleason4': 'Gleason 4',
        'gleason5': 'Gleason 5',
        'stroma': 'Stroma',
        'benign': 'Benign'
      };
      return termMap[term] || term;
    },

    getProstateAnnotationTerm(annotation) {
      if (!annotation || !annotation.terms || !annotation.terms.length) {
        console.log(`Annotation ${annotation?.id} has no terms`);
        return 'unknown';
      }

      // Get the first term name
      let termName = '';
      if (typeof annotation.terms[0] === 'string') {
        termName = annotation.terms[0].toLowerCase();
      } else if (annotation.terms[0].name) {
        termName = annotation.terms[0].name.toLowerCase();
      } else if (annotation.terms[0].id) {
        // Handle case where we only have term ID
        termName = this.getProstateTermNameById(annotation.terms[0].id);
      } else {
        console.log(`Annotation ${annotation.id} has invalid term structure:`, annotation.terms[0]);
        return 'unknown';
      }

      console.log(`Annotation ${annotation.id} term name: ${termName}`);

      // Map to standard prostate terms
      if (termName.includes('gleason3') || termName.includes('pattern3') || termName.includes('g3') || termName === 'gleason3') {
        return 'gleason3';
      }
      if (termName.includes('gleason4') || termName.includes('pattern4') || termName.includes('g4') || termName === 'gleason4') {
        return 'gleason4';
      }
      if (termName.includes('gleason5') || termName.includes('pattern5') || termName.includes('g5') || termName === 'gleason5') {
        return 'gleason5';
      }
      if (termName.includes('stroma') || termName === 'stroma') {
        return 'stroma';
      }
      if (termName.includes('benign') || termName === 'benign') {
        return 'benign';
      }

      console.log(`Annotation ${annotation.id} term '${termName}' not recognized as prostate term`);
      return 'unknown';
    },

    // Temporary fallback method
    getProstateAnnotationTermFallback(annotation) {
      if (!annotation.term || !annotation.term.length) return 'unknown';

      // Simple mapping based on term IDs or names
      const firstTerm = annotation.term[0];
      const termId = typeof firstTerm === 'object' ? firstTerm.id : firstTerm;

      const termMap = {
        'gleason3': 'gleason3',
        'gleason4': 'gleason4',
        'gleason5': 'gleason5',
        'stroma': 'stroma',
        'benign': 'benign'
      };

      return termMap[termId] || 'unknown';
    },

    getGleasonPatternClass(annotation) {
      const term = this.getProstateAnnotationTerm(annotation);
      return `pattern-${term.replace('gleason', '')}`;
    },

    updatePixelSize() {
      // Convert μm to mm
      this.prostateData.pixelSizeMm = this.prostateData.pixelSizeUm / 1000;

      // Recalculate areas if they exist
      if (this.prostateData.totalMalignantArea > 0) {
        this.calculateProstateAreas();
      }
    },

    // Gleason scoring methods based on PDF logic
    calculateGleasonScore() {
      if (!this.prostateData.totalMalignantArea || this.prostateData.totalMalignantArea === 0) {
        this.$notify({ type: 'warning', text: 'No malignant area detected. Calculate areas first.' });
        return;
      }

      try {
        console.log('Calculating Gleason score with updated ISUP/GUPS logic...');

        // Ensure specimenType defaults to biopsy for conservative scoring unless explicitly set
        if (!this.prostateData.specimenType) this.prostateData.specimenType = 'biopsy';

        // Ensure percentages are present
        if (!this.prostateData.percentages ||
            typeof this.prostateData.percentages.gleason3 === 'undefined' ||
            typeof this.prostateData.percentages.gleason4 === 'undefined' ||
            typeof this.prostateData.percentages.gleason5 === 'undefined') {
          this.calculatePatternPercentages();
        }

        // Identify primary/secondary/tertiary according to rules
        this.identifyGleasonPatterns();

        // Finalize Gleason score string and grade group
        this.determineFinalGleasonScore();
        this.assignISUPGradeGroup();

        console.log('Gleason score calculated:', {
          score: this.prostateData.gleasonScore,
          gradeGroup: this.prostateData.gradeGroup,
          patterns: {
            primary: this.prostateData.primaryPattern,
            secondary: this.prostateData.secondaryPattern,
            tertiary: this.prostateData.tertiaryPattern
          },
          percentages: this.prostateData.percentages
        });

        this.$notify({
          type: 'success',
          text: `Gleason score: ${this.prostateData.gleasonScore} (${this.prostateData.gradeGroup})`
        });

      } catch (error) {
        console.error('Error calculating Gleason score:', error);
        this.$notify({ type: 'error', text: 'Failed to calculate Gleason score' });
        throw error;
      }
    },

    identifyGleasonPatterns() {
      // Build patterns list from supplied mm² areas and percentages
      const patterns = [
        { pattern: 3, area: this.prostateData.areas.gleason3 || 0, percentage: this.prostateData.percentages.gleason3 || 0 },
        { pattern: 4, area: this.prostateData.areas.gleason4 || 0, percentage: this.prostateData.percentages.gleason4 || 0 },
        { pattern: 5, area: this.prostateData.areas.gleason5 || 0, percentage: this.prostateData.percentages.gleason5 || 0 }
      ].filter(p => p.area > 0 && p.percentage > 0);

      if (patterns.length === 0) {
        throw new Error('No malignant patterns found');
      }

      // Sort helpers
      const sortedByArea = [...patterns].sort((a, b) => b.area - a.area);      // most common first
      const sortedByGrade = [...patterns].sort((a, b) => b.pattern - a.pattern); // highest grade first

      const mostCommon = sortedByArea[0];      // primary by area (default)
      const highestGrade = sortedByGrade[0];

      // Reset assignments
      this.prostateData.primaryPattern = null;
      this.prostateData.secondaryPattern = null;
      this.prostateData.tertiaryPattern = null;

      // Single pattern
      if (patterns.length === 1) {
        this.prostateData.primaryPattern = mostCommon.pattern;
        this.prostateData.secondaryPattern = mostCommon.pattern;
        this.prostateData.tertiaryPattern = null;
        return;
      }

      // Two patterns: usually primary = most common; secondary = other OR highest grade
      if (patterns.length === 2) {
        const other = patterns.find(p => p.pattern !== mostCommon.pattern);

        // For biopsies: if the other pattern is higher grade than primary, treat it as secondary regardless of percentage
        if (this.prostateData.specimenType === 'biopsy') {
          // Primary remains most common, secondary = highest grade (per ISUP biopsy rule)
          this.prostateData.primaryPattern = mostCommon.pattern;
          this.prostateData.secondaryPattern = Math.max(mostCommon.pattern, other.pattern);
        } else {
          // For prostatectomy: if the higher-grade component is <5% consider as tertiary (i.e., do NOT include it in score)
          const higherPattern = mostCommon.pattern >= other.pattern ? mostCommon : other;
          const lowerPattern = mostCommon.pattern >= other.pattern ? other : mostCommon;

          if (higherPattern.pattern > lowerPattern.pattern && higherPattern.percentage < 5) {
            // keep primary/secondary by area (higher-grade remains tertiary)
            this.prostateData.primaryPattern = mostCommon.pattern;
            this.prostateData.secondaryPattern = lowerPattern.pattern;
            this.prostateData.tertiaryPattern = higherPattern.pattern;
          } else {
            // include higher grade as secondary
            this.prostateData.primaryPattern = mostCommon.pattern;
            this.prostateData.secondaryPattern = Math.max(mostCommon.pattern, other.pattern);
          }
        }
        return;
      }

      // Three patterns: most complex
      if (patterns.length === 3) {
        const secondByArea = sortedByArea[1];
        const thirdByArea = sortedByArea[2];

        // Primary is the most common by area
        this.prostateData.primaryPattern = mostCommon.pattern;

        // Biopsy rule: any highest-grade pattern present should be used as secondary
        if (this.prostateData.specimenType === 'biopsy') {
          // If highestGrade is not the most common, set it as secondary
          if (highestGrade.pattern !== mostCommon.pattern) {
            this.prostateData.secondaryPattern = highestGrade.pattern;
            // tertiary = the remaining pattern (the one that was second most common by area)
            const remaining = sortedByArea.find(p => p.pattern !== mostCommon.pattern && p.pattern !== highestGrade.pattern);
            this.prostateData.tertiaryPattern = remaining ? remaining.pattern : null;
          } else {
            // highest grade == primary (i.e., most common is also highest grade)
            // secondary becomes secondByArea
            this.prostateData.secondaryPattern = secondByArea.pattern;
            this.prostateData.tertiaryPattern = thirdByArea.pattern;
          }
        } else {
          // Prostatectomy rule: if highest grade component <5% -> tertiary (do NOT include in score)
          if (highestGrade.pattern !== mostCommon.pattern && highestGrade.percentage < 5) {
            // keep primary + second most common as secondary; highest becomes tertiary
            this.prostateData.secondaryPattern = secondByArea.pattern;
            this.prostateData.tertiaryPattern = highestGrade.pattern;
          } else {
            // highestGrade is either the most common or >=5% -> include it as secondary if it's higher
            if (highestGrade.pattern !== mostCommon.pattern && highestGrade.percentage >= 5) {
              this.prostateData.secondaryPattern = highestGrade.pattern;
              // tertiary = remaining
              const remaining = sortedByArea.find(p => p.pattern !== mostCommon.pattern && p.pattern !== highestGrade.pattern);
              this.prostateData.tertiaryPattern = remaining ? remaining.pattern : null;
            } else {
              // highestGrade equals the primary (most common) OR there is no small tertiary: use secondByArea
              this.prostateData.secondaryPattern = secondByArea.pattern;
              this.prostateData.tertiaryPattern = thirdByArea.pattern;
            }
          }
        }
        return;
      }
    },

    handleSinglePattern(primaryPattern) {
      // Rule: Single pattern gets same pattern for secondary (3+3, 4+4, 5+5)
      this.prostateData.primaryPattern = primaryPattern.pattern;
      this.prostateData.secondaryPattern = primaryPattern.pattern;
      this.prostateData.tertiaryPattern = null;

      console.log(`Single pattern: ${primaryPattern.pattern}+${primaryPattern.pattern}`);
    },

    handleTwoPatterns(patterns, primaryPattern, highestGradePattern) {
      const otherPattern = patterns.find(p => p.pattern !== primaryPattern.pattern);

      // SCENARIO 2A: Primary is highest grade
      if (primaryPattern.pattern === highestGradePattern.pattern) {
        this.prostateData.primaryPattern = primaryPattern.pattern;
        this.prostateData.secondaryPattern = otherPattern.pattern;
      }
      // SCENARIO 2B: Primary is NOT highest grade
      else {
        this.prostateData.primaryPattern = primaryPattern.pattern;
        this.prostateData.secondaryPattern = highestGradePattern.pattern;
      }

      this.prostateData.tertiaryPattern = null;

      console.log(`Two patterns: ${this.prostateData.primaryPattern}+${this.prostateData.secondaryPattern}`);
    },

    handleThreePatterns(patterns, sortedByArea, sortedByGrade, primaryPattern, highestGradePattern) {
      const secondByArea = sortedByArea[1];
      const thirdByArea = sortedByArea[2];

      // SCENARIO 3A: Highest grade is also primary (most common case in practice)
      if (primaryPattern.pattern === highestGradePattern.pattern) {
        this.prostateData.primaryPattern = primaryPattern.pattern;

        // Critical decision: Use second most common as secondary
        // Exception: If second most common is <5%, check if we should use tertiary
        if (secondByArea.percentage >= 5) {
          this.prostateData.secondaryPattern = secondByArea.pattern;
          this.prostateData.tertiaryPattern = thirdByArea.pattern;
        } else {
          // If second most common is <5%, use highest grade pattern available
          this.prostateData.secondaryPattern = Math.max(secondByArea.pattern, thirdByArea.pattern);
          this.prostateData.tertiaryPattern = Math.min(secondByArea.pattern, thirdByArea.pattern);
        }
      }
      // SCENARIO 3B: Highest grade is NOT primary but is second most common
      else if (secondByArea.pattern === highestGradePattern.pattern) {
        this.prostateData.primaryPattern = primaryPattern.pattern;
        this.prostateData.secondaryPattern = highestGradePattern.pattern;
        this.prostateData.tertiaryPattern = thirdByArea.pattern;
      }
      // SCENARIO 3C: Highest grade is least common (tertiary pattern)
      else {
        this.handleTertiaryHighestGrade(patterns, sortedByArea, highestGradePattern);
      }
    },

    handleTertiaryHighestGrade(patterns, sortedByArea, highestGradePattern) {
      const primaryPattern = sortedByArea[0];
      const secondByArea = sortedByArea[1];

      // SPECIAL RULE: For needle biopsies, ANY amount of highest grade becomes secondary
      // For prostatectomy, need >5% to become secondary
      const isBiopsy = this.determineSpecimenType(); // You'll need to implement this

      if (isBiopsy) {
        // Biopsy: Any highest grade pattern becomes secondary
        this.prostateData.primaryPattern = primaryPattern.pattern;
        this.prostateData.secondaryPattern = highestGradePattern.pattern;
        this.prostateData.tertiaryPattern = secondByArea.pattern;
      } else {
        // Prostatectomy: Highest grade needs >5% to become secondary
        if (highestGradePattern.percentage > 5) {
          this.prostateData.primaryPattern = primaryPattern.pattern;
          this.prostateData.secondaryPattern = highestGradePattern.pattern;
          this.prostateData.tertiaryPattern = secondByArea.pattern;
        } else {
          // If <5%, highest grade remains tertiary
          this.prostateData.primaryPattern = primaryPattern.pattern;
          this.prostateData.secondaryPattern = secondByArea.pattern;
          this.prostateData.tertiaryPattern = highestGradePattern.pattern;
        }
      }
    },

    determineSpecimenType() {
      // Implement logic to determine if this is a biopsy or prostatectomy
      // For now, default to biopsy (more conservative)
      return true; // true = biopsy, false = prostatectomy
    },

    determineFinalGleasonScore() {
      if (!this.prostateData.primaryPattern || !this.prostateData.secondaryPattern) {
        throw new Error('Primary and secondary patterns required for Gleason score');
      }

      // For display, primary+secondary string — use numeric values, ensure primary is left operand
      // (Note: primary remains the most common pattern by area; secondary may be higher grade per biopsy rule)
      this.prostateData.gleasonScore = `${this.prostateData.primaryPattern}+${this.prostateData.secondaryPattern}`;

      // Basic validity checks
      const total = this.prostateData.primaryPattern + this.prostateData.secondaryPattern;
      if (total < 6 || total > 10) {
        console.warn('Calculated Gleason score outside expected 6-10 range:', total);
      }
    },

    assignISUPGradeGroup() {
      if (!this.prostateData.gleasonScore) throw new Error('Gleason score required for Grade Group assignment');

      const [p, s] = this.prostateData.gleasonScore.split('+').map(Number);
      const total = p + s;

      if (total <= 6) this.prostateData.gradeGroup = 'Grade Group 1';
      else if (p === 3 && s === 4) this.prostateData.gradeGroup = 'Grade Group 2';
      else if (p === 4 && s === 3) this.prostateData.gradeGroup = 'Grade Group 3';
      else if (total === 8) this.prostateData.gradeGroup = 'Grade Group 4';
      else if (total >= 9) this.prostateData.gradeGroup = 'Grade Group 5';
      else this.prostateData.gradeGroup = 'Unknown';
    },

    calculateAnnotationArea(annotation) {
      try {
        const areaPx = this.calculateAreaFromWKT(annotation.location);
        // Convert to mm² using pixel size
        return areaPx * Math.pow(this.prostateData.pixelSizeMm, 2);
      } catch (error) {
        console.warn('Error calculating annotation area:', error);
        return 0;
      }
    },

    getGleasonScoreDescription(score) {
      const descriptions = {
        '3+3': 'Well differentiated - individual, separate, closely packed glands',
        '3+4': 'Moderately differentiated - predominantly well-formed glands with lesser component of poorly formed/fused/cribriform glands',
        '4+3': 'Moderately differentiated - predominantly poorly formed/fused/cribriform glands with lesser component of well-formed glands',
        '4+4': 'Poorly differentiated - only poorly formed/fused/cribriform glands',
        '4+5': 'Poorly differentiated - predominantly poorly formed/fused/cribriform glands with lesser component lacking glands',
        '5+4': 'Poorly differentiated - predominantly lacking glands with lesser component of poorly formed/fused/cribriform glands',
        '5+5': 'Undifferentiated - only lacking glands'
      };
      return descriptions[score] || 'Gleason pattern score';
    },

    getGradeGroupDescription(gradeGroup) {
      const descriptions = {
        'Grade Group 1': 'Grade Group 1: Gleason score ≤6 - Well differentiated, favorable prognosis',
        'Grade Group 2': 'Grade Group 2: Gleason score 3+4=7 - Moderately differentiated, intermediate risk',
        'Grade Group 3': 'Grade Group 3: Gleason score 4+3=7 - Moderately differentiated, intermediate-high risk',
        'Grade Group 4': 'Grade Group 4: Gleason score 8 - Poorly differentiated, high risk',
        'Grade Group 5': 'Grade Group 5: Gleason scores 9-10 - Poorly differentiated/undifferentiated, very high risk'
      };
      return descriptions[gradeGroup] || 'Grade group interpretation not available';
    },

    getPrognosticSignificance(score) {
      const significance = {
        '3+3': 'Favorable prognosis. Active surveillance often appropriate for low-risk disease.',
        '3+4': 'Intermediate risk. Treatment typically recommended based on risk stratification.',
        '4+3': 'Intermediate-high risk. Definitive treatment usually recommended.',
        '4+4': 'High risk. Aggressive treatment typically needed.',
        '4+5': 'High risk with poor prognosis features present.',
        '5+4': 'Very high risk with poor prognosis.',
        '5+5': 'Highest risk category with very poor prognosis.'
      };
      return significance[score] || 'Prognosis varies based on score and clinical factors.';
    },

    // Prostate annotation UI methods
    getProstateAnnotationThumbnailStyle(annotation) {
      const thumbnailSize = 64;
      const imageId = this.getImageId();

      if (!imageId || !annotation.id) {
        return {
          width: thumbnailSize + 'px',
          height: thumbnailSize + 'px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6c757d',
          fontSize: '12px',
          borderRadius: '4px'
        };
      }

      // Build thumbnail URL for prostate annotations
      const serverUrl = this.cytomineApiBaseUrl || '';
      let cropUrl = `${serverUrl}/api/annotation/${annotation.id}/crop.jpg?maxSize=${thumbnailSize}&draw=true&complete=true`;

      // Add authentication token
      if (this.shortTermToken) {
        cropUrl += `&Authorization=${encodeURIComponent('Bearer ' + this.shortTermToken)}`;
      }

      console.log(`Prostate annotation thumbnail URL for ${annotation.id}:`, cropUrl);

      const baseStyle = {
        backgroundImage: `url(${cropUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        width: thumbnailSize + 'px',
        height: thumbnailSize + 'px',
        border: '3px solid white',
        boxShadow: '0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1)',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px'
      };

      return baseStyle;
    },

    getFormattedProstateLocation(annotation) {
      const location = this.parseAnnotationLocation(annotation.location, annotation);
      if (!location || (!location.x && !location.y)) {
        return 'Location: Not available';
      }

      return `Location: (${Math.round(location.x)}, ${Math.round(location.y)})`;
    },

    getAnnotationArea(annotation) {
      return annotation.area ? annotation.area.toFixed(3) : '0.000';
    },

    goToProstateAnnotation(annotation) {
      this.currentSelectedAnnotation = annotation;

      const location = this.parseAnnotationLocation(annotation.location, annotation);
      const imageId = this.getImageId();

      if (!location || !location.x || !location.y) {
        console.warn('Cannot navigate to prostate annotation: invalid location');
        return;
      }

      // Emit event to center view on annotation
      this.$emit('centerView', {
        annot: {
          ...annotation,
          image: imageId,
          location: annotation.location,
          centroid: { x: location.x, y: location.y },
          x: location.x,
          y: location.y,
          slice: this.imageWrapper?.activeSlice?.id
        },
        duration: 500
      });
    },

    toggleProstateAnnotationDetails(annotationId) {
      this.$set(this.expandedProstateAnnotations, annotationId,
        !this.expandedProstateAnnotations[annotationId]);
    },

    async runCompleteProstateAnalysis() {
      try {
        console.log('=== Starting Complete Prostate Analysis ===');

        // Update status to in progress
        this.updateAnalysisStatus('in_progress');

        // Step 1: Load annotations
        this.$notify({
          type: 'info',
          text: 'Step 1: Loading prostate annotations...'
        });

        await this.loadAllProstateAnnotations();

        if (this.allProstateAnnotations.length === 0) {
          this.$notify({
            type: 'warning',
            text: 'No annotations found. Please create annotations first.'
          });
          return;
        }

        // Step 2: Calculate areas
        this.$notify({
          type: 'info',
          text: 'Step 2: Calculating areas...'
        });

        await this.calculateProstateAreas();

        if (this.prostateData.totalMalignantArea === 0) {
          this.$notify({
            type: 'warning',
            text: 'No malignant tissue detected in annotations.'
          });
          return;
        }

        // Step 3: Calculate Gleason score
        this.$notify({
          type: 'info',
          text: 'Step 3: Calculating Gleason score...'
        });

        await this.calculateGleasonScore();

        // Step 4: Navigate to report tab
        this.activeProstateTab = 'report';

        // Update status to completed
        this.updateAnalysisStatus('completed');

        this.$notify({
          type: 'success',
          text: 'Complete prostate analysis finished successfully!'
        });

        console.log('=== Complete Prostate Analysis Finished ===');

      } catch (error) {
        console.error('Error in complete prostate analysis:', error);
        this.$notify({
          type: 'error',
          text: `Analysis failed: ${error.message}`
        });
      }
    },

    // Prostate comment management
    startEditingProstateComment(commentType) {
      this.editingProstateComments[commentType] = true;
      this.currentProstateCommentText = this.prostateComments[commentType] || '';
    },

    cancelEditProstateComment(commentType) {
      this.editingProstateComments[commentType] = false;
      this.currentProstateCommentText = '';
    },

    async saveProstateCommentInline(commentType) {
      this.prostateComments[commentType] = this.currentProstateCommentText.trim();

      // Save to database
      try {
        await this.saveProstateCommentsToDatabase();
        console.log(`Prostate comment saved for ${commentType}`);
      } catch (error) {
        console.error('Failed to save prostate comment:', error);
      }

      this.editingProstateComments[commentType] = false;
      this.currentProstateCommentText = '';
    },

    async deleteProstateCommentInline(commentType) {
      this.prostateComments[commentType] = '';

      try {
        await this.saveProstateCommentsToDatabase();
        console.log(`Prostate comment deleted for ${commentType}`);
      } catch (error) {
        console.error('Failed to delete prostate comment:', error);
      }

      this.editingProstateComments[commentType] = false;
      this.currentProstateCommentText = '';
    },

    getProstateCommentDate(commentType) {
      // In a real implementation, this would come from stored metadata
      return new Date().toLocaleDateString();
    },

    // Prostate data persistence
    async saveProstateCommentsToDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot save prostate comments: missing project or image ID');
          return;
        }

        const propertyKey = `prostate_comments_${imageId}`;
        const propertyValue = JSON.stringify(this.prostateComments);

        const { Property } = await import('cytomine-client');
        const property = new Property({
          object: this.project,
          key: propertyKey,
          value: propertyValue
        });

        await property.save();
        console.log('Prostate comments saved to database');

      } catch (error) {
        console.error('Error saving prostate comments to database:', error);
        throw error;
      }
    },

    async loadProstateCommentsFromDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot load prostate comments: missing project or image ID');
          return;
        }

        const { PropertyCollection } = await import('cytomine-client');
        const propertyCollection = new PropertyCollection({object: this.project});
        await propertyCollection.fetchAll();

        const properties = propertyCollection.array || [];
        const propertyKey = `prostate_comments_${imageId}`;

        const prostateProperty = properties.find(property => property.key === propertyKey);

        if (prostateProperty) {
          this.prostateComments = JSON.parse(prostateProperty.value);
          console.log('Prostate comments loaded from database');
        }

      } catch (error) {
        console.error('Error loading prostate comments from database:', error);
      }
    },

    async saveProstateDataToDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot save prostate data: missing project or image ID');
          return;
        }

        const propertyKey = `prostate_analysis_data_${imageId}`;
        const propertyValue = JSON.stringify({
          prostateData: this.prostateData,
          lastCalculated: new Date().toISOString()
        });

        const { Property } = await import('cytomine-client');
        const property = new Property({
          object: this.project,
          key: propertyKey,
          value: propertyValue
        });

        await property.save();
        console.log('Prostate analysis data saved to database');

      } catch (error) {
        console.error('Error saving prostate data to database:', error);
        throw error;
      }
    },

    async loadProstateDataFromDatabase() {
      try {
        const projectId = this.project?.id;
        const imageId = this.image?.id;

        if (!projectId || !imageId) {
          console.warn('Cannot load prostate data: missing project or image ID');
          return;
        }

        const { PropertyCollection } = await import('cytomine-client');
        const propertyCollection = new PropertyCollection({object: this.project});
        await propertyCollection.fetchAll();

        const properties = propertyCollection.array || [];
        const propertyKey = `prostate_analysis_data_${imageId}`;

        const prostateProperty = properties.find(property => property.key === propertyKey);

        if (prostateProperty) {
          const storedData = JSON.parse(prostateProperty.value);
          this.prostateData = { ...this.prostateData, ...storedData.prostateData };
          console.log('Prostate analysis data loaded from database');
        }

      } catch (error) {
        console.error('Error loading prostate data from database:', error);
      }
    },

    // Prostate value editing methods
    startEditingProstateValue(field, subField = null) {
      if (subField) {
        this.$set(this.editingProstateValues[field], subField, true);
        this.tempProstateValues[field][subField] = this.prostateData[field][subField];
      } else {
        this.editingProstateValues[field] = true;
        this.tempProstateValues[field] = this.prostateData[field];
      }
    },

    cancelEditProstateValue(field, subField = null) {
      if (subField) {
        this.$set(this.editingProstateValues[field], subField, false);
        this.tempProstateValues[field][subField] = '';
      } else {
        this.editingProstateValues[field] = false;
        this.tempProstateValues[field] = '';
      }
    },

    async saveProstateValue(field, subField = null) {
      try {
        let newValue;
        let fieldPath = field;

        if (subField) {
          newValue = parseFloat(this.tempProstateValues[field][subField]);
          fieldPath = `${field}.${subField}`;

          // Validate percentage (0-100)
          if (isNaN(newValue) || newValue < 0 || newValue > 100) {
            throw new Error('Percentage must be between 0 and 100');
          }

          // Update the data with manual override
          this.$set(this.prostateData[field], subField, newValue);
          this.$set(this.manualOverrides[field], subField, true);

          // Recalculate other percentages if needed
          if (field === 'percentages') {
            this.recalculatePercentagesAfterManualEdit(subField, newValue);
          }
        } else {
          if (field === 'gleasonScore') {
            // Validate Gleason score format (e.g., "3+4", "4+3", etc.)
            if (!/^[3-5]\+[3-5]$/.test(this.tempProstateValues[field])) {
              throw new Error('Gleason score must be in format like "3+4", "4+3", etc.');
            }
            this.prostateData.gleasonScore = this.tempProstateValues[field];
            this.manualOverrides.gleasonScore = true;

            // Auto-update grade group based on manual Gleason score
            this.updateGradeGroupFromManualGleasonScore(this.tempProstateValues[field]);
          }
          else if (field === 'gradeGroup') {
            this.prostateData.gradeGroup = this.tempProstateValues[field];
            this.manualOverrides.gradeGroup = true;
          }
          else if (field === 'tumorPercentage') {
            newValue = parseFloat(this.tempProstateValues[field]);
            if (isNaN(newValue) || newValue < 0 || newValue > 100) {
              throw new Error('Tumor percentage must be between 0 and 100');
            }
            this.prostateData.tumorPercentage = newValue;
            this.manualOverrides.tumorPercentage = true;

            console.log('Tumor percentage manually set to:', newValue);
          }
        }

        // Save to database
        await this.saveProstateDataToDatabase();

        // Exit editing mode
        this.cancelEditProstateValue(field, subField);

        this.$notify({
          type: 'success',
          text: `${this.getFieldDisplayName(field, subField)} updated successfully`
        });

      } catch (error) {
        console.error('Error saving prostate value:', error);
        this.$notify({
          type: 'error',
          text: error.message || 'Failed to save value'
        });
      }
    },

    resetTumorPercentage() {
      this.manualOverrides.tumorPercentage = false;

      // Recalculate tumor percentage from areas
      if (this.prostateData.totalTumorArea > 0) {
        this.prostateData.tumorPercentage = (this.prostateData.totalMalignantArea / this.prostateData.totalTumorArea) * 100;
      } else if (this.prostateData.totalClassifiedArea > 0) {
        this.prostateData.tumorPercentage = (this.prostateData.totalMalignantArea / this.prostateData.totalClassifiedArea) * 100;
      } else {
        this.prostateData.tumorPercentage = 0;
      }

      this.saveProstateDataToDatabase();

      this.$notify({
        type: 'success',
        text: 'Tumor percentage reset to auto-calculated value'
      });
    },

    recalculatePercentagesAfterManualEdit(modifiedPattern, newValue) {
      // When one percentage is manually set, adjust the others to maintain 100% total
      const patterns = ['gleason3', 'gleason4', 'gleason5'];
      const totalManual = patterns.reduce((sum, pattern) => {
        return sum + (this.manualOverrides.percentages[pattern] ? this.prostateData.percentages[pattern] : 0);
      }, 0);

      if (totalManual > 100) {
        // If manual overrides exceed 100%, normalize them
        patterns.forEach(pattern => {
          if (this.manualOverrides.percentages[pattern]) {
            this.prostateData.percentages[pattern] = (this.prostateData.percentages[pattern] / totalManual) * 100;
          }
        });
      } else {
        // Distribute remaining percentage among non-manual patterns
        const remaining = 100 - totalManual;
        const nonManualPatterns = patterns.filter(pattern =>
          !this.manualOverrides.percentages[pattern] && pattern !== modifiedPattern
        );

        if (nonManualPatterns.length > 0) {
          const share = remaining / nonManualPatterns.length;
          nonManualPatterns.forEach(pattern => {
            this.prostateData.percentages[pattern] = share;
          });
        }
      }
    },

    updateGradeGroupFromManualGleasonScore(score) {
      // Update grade group based on manual Gleason score input
      const totalScore = parseInt(score.split('+')[0]) + parseInt(score.split('+')[1]);

      if (totalScore <= 6) {
        this.prostateData.gradeGroup = 'Grade Group 1';
      } else if (score === '3+4') {
        this.prostateData.gradeGroup = 'Grade Group 2';
      } else if (score === '4+3') {
        this.prostateData.gradeGroup = 'Grade Group 3';
      } else if (totalScore === 8) {
        this.prostateData.gradeGroup = 'Grade Group 4';
      } else if (totalScore >= 9) {
        this.prostateData.gradeGroup = 'Grade Group 5';
      }
    },

    getFieldDisplayName(field, subField = null) {
      const names = {
        gleasonScore: 'Gleason Score',
        gradeGroup: 'Grade Group',
        tumorPercentage: 'Tumor Percentage',
        percentages: {
          gleason3: 'Gleason 3 Percentage',
          gleason4: 'Gleason 4 Percentage',
          gleason5: 'Gleason 5 Percentage'
        }
      };

      return subField ? names[field][subField] : names[field];
    },

    resetManualOverride(field, subField = null) {
      if (subField) {
        this.manualOverrides[field][subField] = false;
        // Recalculate from areas
        this.calculatePatternPercentages();
      } else {
        this.manualOverrides[field] = false;

        if (field === 'gleasonScore' || field === 'gradeGroup') {
          // Recalculate from areas and percentages
          this.calculateGleasonScore();
        } else if (field === 'tumorPercentage') {
          // Use the new reset method
          this.resetTumorPercentage();
          return; // Don't continue with the rest of the method
        }
      }

      this.saveProstateDataToDatabase();

      this.$notify({
        type: 'success',
        text: `${this.getFieldDisplayName(field, subField)} reset to auto-calculated value`
      });
    },

    startEditingAllSummaryValues() {
      this.startEditingProstateValue('gleasonScore');
      this.startEditingProstateValue('gradeGroup');
      this.startEditingProstateValue('tumorPercentage');
    },

    startEditingAllPercentages() {
      this.startEditingProstateValue('percentages', 'gleason3');
      this.startEditingProstateValue('percentages', 'gleason4');
      this.startEditingProstateValue('percentages', 'gleason5');
    },

    generateProstateReport() {
      try {
        console.log('Generating prostate analysis report...');

        // Update status to completed
        this.updateAnalysisStatus('completed');

        // Generate PDF report
        this.generateProstateReportPDF();

      } catch (error) {
        console.error('Error generating prostate report:', error);
        this.$notify({
          type: 'error',
          text: 'Failed to generate prostate report'
        });
      }
    },

    // Report generation
    generateProstateReportPDF() {
      try {
        console.log('Generating prostate analysis PDF report...');

        const reportWindow = window.open('', '_blank', 'width=800,height=1000,resizable=yes,scrollbars=yes');

        if (!reportWindow) {
          this.$notify({
            type: 'error',
            text: 'Unable to open new window. Please allow pop-ups and try again.'
          });
          return;
        }

        const reportHTML = this.generateProstateReportHTML();
        reportWindow.document.write(reportHTML);
        reportWindow.document.close();
        reportWindow.focus();

        // Auto-print after a short delay
        setTimeout(() => {
          reportWindow.print();
        }, 500);

        this.$notify({
          type: 'success',
          text: 'Prostate analysis report generated successfully'
        });

      } catch (error) {
        console.error('Error generating prostate report:', error);
        this.$notify({
          type: 'error',
          text: 'Failed to generate prostate report'
        });
      }
    },

    // Generate prostate report HTML
    generateProstateReportHTML() {
      const currentDate = new Date().toLocaleDateString();
      const currentTime = new Date().toLocaleTimeString();

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Prostate Gleason Scoring Analysis Report</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
              line-height: 1.4;
              margin: 0;
              padding: 0;
              color: #333;
            }
            .report-container {
              width: 100%;
              max-width: 210mm;
              margin: 0 auto;
              padding: 10px;
            }
            .report-header {
              text-align: center;
              margin-bottom: 25px;
              padding-bottom: 15px;
              border-bottom: 2px solid #333;
            }
            .report-title {
              font-size: 24px;
              font-weight: bold;
              margin: 0 0 10px 0;
              color: #2c3e50;
            }
            .report-subtitle {
              font-size: 14px;
              color: #7f8c8d;
              margin: 5px 0;
            }
            .summary-section {
              margin: 20px 0;
              padding: 15px;
              background: #f8f9fa;
              border-radius: 5px;
              page-break-inside: avoid;
            }
            .summary-cards {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 15px;
              margin: 15px 0;
            }
            .summary-card {
              background: white;
              padding: 15px;
              border-radius: 5px;
              text-align: center;
              border-left: 4px solid #007bff;
              page-break-inside: avoid;
            }
            .summary-card.primary {
              border-left-color: #4CAF50;
            }
            .summary-card.secondary {
              border-left-color: #FF9800;
            }
            .summary-label {
              font-size: 12px;
              color: #666;
              margin-bottom: 8px;
              font-weight: bold;
            }
            .summary-value.score {
              font-size: 28px;
              font-weight: bold;
              color: #2c3e50;
            }
            .summary-value.group {
              font-size: 22px;
              font-weight: bold;
              color: #e74c3c;
            }
            .summary-value.area, .summary-value.percentage {
              font-size: 18px;
              font-weight: bold;
              color: #34495e;
            }
            .distribution-section {
              margin: 25px 0;
              page-break-inside: avoid;
            }
            .distribution-table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
              font-size: 11px;
            }
            .distribution-table th,
            .distribution-table td {
              border: 1px solid #ddd;
              padding: 10px 12px;
              text-align: left;
            }
            .distribution-table th {
              background: #f8f9fa;
              font-weight: bold;
              color: #2c3e50;
            }
            .pattern-3 { background-color: #e8f5e8; }
            .pattern-4 { background-color: #fff3e0; }
            .pattern-5 { background-color: #ffebee; }
            .primary-row { font-weight: bold; border-left: 4px solid #4CAF50; }
            .secondary-row { font-weight: bold; border-left: 4px solid #FF9800; }
            .tertiary-row { font-weight: bold; border-left: 4px solid #9C27B0; }
            .role-badge {
              padding: 4px 8px;
              border-radius: 3px;
              font-size: 10px;
              font-weight: bold;
              color: white;
            }
            .role-badge.primary { background: #4CAF50; }
            .role-badge.secondary { background: #FF9800; }
            .role-badge.tertiary { background: #9C27B0; }
            .clinical-notes {
              margin: 25px 0;
              padding: 20px;
              background: #e3f2fd;
              border-radius: 5px;
              border-left: 4px solid #2196F3;
              page-break-inside: avoid;
            }
            .clinical-notes h3 {
              margin-top: 0;
              color: #1976d2;
            }
            .note-item {
              margin: 10px 0;
              padding: 8px;
              background: white;
              border-radius: 3px;
            }
            .footer {
              margin-top: 30px;
              padding-top: 15px;
              border-top: 1px solid #ddd;
              font-size: 10px;
              color: #666;
              text-align: center;
            }
            @media print {
              body {
                margin: 0;
                font-size: 11px;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .report-container {
                width: 100%;
                padding: 0;
              }
              .summary-cards {
                grid-template-columns: repeat(2, 1fr);
              }
              .summary-card {
                page-break-inside: avoid;
              }
              .clinical-notes {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="report-container">
            <div class="report-header">
              <h1 class="report-title">Prostate Gleason Scoring Analysis Report</h1>
              <div class="report-subtitle">
                Generated on ${currentDate} at ${currentTime}
              </div>
              <div style="margin-top: 15px; font-size: 13px;">
                <strong>Image ID:</strong> ${this.image?.id || 'N/A'} |
                <strong>Filename:</strong> ${this.image?.originalFilename || this.image?.filename || 'N/A'} |
                <strong>User:</strong> ${this.currentUser?.name || this.currentUser?.username || 'N/A'}
              </div>
              <div style="margin-top: 8px; font-size: 12px; color: #666;">
                <strong>Pixel Size:</strong> ${this.prostateData.pixelSizeUm} μm (${this.prostateData.pixelSizeMm?.toFixed(4)} mm)
              </div>
            </div>

            ${this.prostateData.gleasonScore ? `
            <div class="summary-section">
              <h3 style="margin-top: 0; color: #2c3e50;">Gleason Score Summary</h3>
              <div class="summary-cards">
                <div class="summary-card primary">
                  <div class="summary-label">Gleason Score</div>
                  <div class="summary-value score">${this.prostateData.gleasonScore}</div>
                </div>
                <div class="summary-card secondary">
                  <div class="summary-label">Grade Group</div>
                  <div class="summary-value group">${this.prostateData.gradeGroup || 'N/A'}</div>
                </div>
                <div class="summary-card">
                  <div class="summary-label">Total Malignant Area</div>
                  <div class="summary-value area">${this.prostateData.totalMalignantArea?.toFixed(3) || '0.000'} mm²</div>
                </div>
                <div class="summary-card">
                  <div class="summary-label">Tumor Percentage</div>
                  <div class="summary-value percentage">
                    ${this.prostateData.totalTumorArea ? ((this.prostateData.totalMalignantArea / this.prostateData.totalTumorArea) * 100).toFixed(1) : '0.0'}%
                  </div>
                </div>
              </div>
            </div>
            ` : ''}

            ${this.prostateData.totalMalignantArea > 0 ? `
            <div class="distribution-section">
              <h3 style="color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 8px;">Pattern Distribution Analysis</h3>
              <table class="distribution-table">
                <thead>
                  <tr>
                    <th style="width: 25%;">Pattern</th>
                    <th style="width: 20%;">Area (mm²)</th>
                    <th style="width: 20%;">Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  ${this.gleasonPatterns.map(pattern => {
                    const patternKey = 'gleason' + pattern.id;
                    const area = this.prostateData.areas[patternKey] || 0;
                    const percentage = this.prostateData.percentages[patternKey] || 0;
                    const count = this.prostateData.annotationCounts[patternKey] || 0;

                    if (area > 0) {
                      return `
                      <tr class="pattern-${pattern.id} ${this.prostateData.primaryPattern === pattern.id ? 'primary-row' : ''} ${this.prostateData.secondaryPattern === pattern.id ? 'secondary-row' : ''} ${this.prostateData.tertiaryPattern === pattern.id ? 'tertiary-row' : ''}">
                        <td><strong>${pattern.name}</strong></td>
                        <td>${area.toFixed(3)}</td>
                        <td>${percentage.toFixed(1)}%</td>
                      </tr>
                      `;
                    }
                    return '';
                  }).join('')}
                </tbody>
              </table>
            </div>

            <div class="clinical-notes">
              <h3>Clinical Interpretation & Notes</h3>

              ${this.prostateComments.clinical ? `
              <div class="note-item">
                <strong>Pathologist Notes:</strong> ${this.prostateComments.clinical}
              </div>
              ` : '<div class="note-item"><em>No clinical notes provided.</em></div>'}

              ${this.prostateData.tertiaryPattern ? `
              <div class="note-item">
                <strong>Tertiary Pattern:</strong> Tertiary pattern ${this.prostateData.tertiaryPattern} detected (${this.prostateData.percentages['gleason' + this.prostateData.tertiaryPattern]?.toFixed(1)}%). Consider clinical context for treatment decisions.
              </div>
              ` : ''}

              ${this.prostateData.totalMalignantArea < 0.1 ? `
              <div class="note-item">
                <strong>Small Focus Note:</strong> Limited malignant area detected (${this.prostateData.totalMalignantArea.toFixed(3)} mm²). Consider additional sampling if clinically indicated.
              </div>
              ` : ''}

              ${this.prostateData.gradeGroup ? `
              <div class="note-item">
                <strong>Grade Group Interpretation:</strong> ${this.getGradeGroupDescription(this.prostateData.gradeGroup)}
              </div>
              ` : ''}

              ${this.prostateData.gleasonScore ? `
              <div class="note-item">
                <strong>Prognostic Significance:</strong> ${this.getPrognosticSignificance(this.prostateData.gleasonScore)}
              </div>
              ` : ''}
            </div>
            ` : `
            <div style="text-align: center; padding: 40px 20px; color: #7f8c8d; background: #f8f9fa; border-radius: 8px; margin: 20px 0;">
              <h3>No Analysis Data Available</h3>
              <p>Complete the prostate analysis workflow to complete full analysis data.</p>
            </div>
            `}

            <div class="footer">
              <p>This report was generated automatically by the Prostate Gleason Scoring Analysis system.</p>
              <p><strong>Clinical decisions should be based on comprehensive pathological evaluation.</strong></p>
              <p>Report generated: ${currentDate} ${currentTime}</p>
            </div>
          </div>
        </body>
        </html>`;
    },

    // Initialize prostate data
    async initializeProstateData() {
      try {
        // Reset any problematic states
        this.activeProstateTab = 'scoring';
        this.isCalculatingAreas = false;

        // Load stored data
        await Promise.all([
          this.loadProstateDataFromDatabase(),
          this.loadProstateCommentsFromDatabase()
        ]);

        // Set default pixel size if not loaded
        if (!this.prostateData.pixelSizeUm) {
          this.prostateData.pixelSizeUm = 0.25;
          this.updatePixelSize();
        }

        console.log('Prostate data initialized successfully');

      } catch (error) {
        console.error('Error initializing prostate data:', error);
        // Reset to safe state
        this.activeProstateTab = 'scoring';
      }
    },

    cleanup() {
      // Remove event listeners
      this.removeEventListeners();

      // Cancel any ongoing operations
      this.isLoadingAnnotations = false;
      this.isLoadingProstateAnnotations = false;
      this.isCalculatingAreas = false;
      this.isAutoCalculating = false;

      // Clear timeouts and intervals
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout);
      }
    },
  },

  async created() {
    console.log('=== AI Analysis Panel created() starting ===');

    try {
      // Initialize with prostate as default model
      await this.loadModelData(this.activeModel);

      // Initialize analysis session
      this.initializeAnalysisSession();
      console.log('Analysis session initialized');

      // Load ontology data first (required for other operations)
      await this.loadOntologyData();
      console.log('Ontology data loaded');

      // Initialize prostate data
      await this.initializeProstateData();
      console.log('Prostate data initialized');

      // Set up event listeners for real-time updates
      this.setupEventListeners();
      console.log('Event listeners set up');

      console.log('=== AI Analysis Panel created() completed ===');

    } catch (error) {
      console.error('Error during AI Analysis Panel initialization:', error);

      // Show user notification
      this.$notify({
        type: 'error',
        text: 'Failed to initialize analysis panel. Please refresh the page.'
      });
    }
  },

  beforeDestroy() {
    // Clean up event listeners
    this.removeEventListeners();
  }
};
</script>

<style scoped>
/* Main AI Analysis Panel */
.ai-analysis-panel {
  width: auto;
  min-width: 20vw;
  max-width: 35vw;
  height: 80vh !important;
  min-height: 80vh !important;
  max-height: 80vh !important;
  overflow-y: auto;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  border-radius: 0;
}

/* Model Selection Tabs */
.model-selection-tabs {
  background: #2c3e50;
  border-bottom: 1px solid #34495e;
}

.model-tab-header {
  display: flex;
  background: #2c3e50;
}

.model-tab-button {
  flex: 1;
  padding: 0.75rem 1rem;
  background: #6c757d;
  color: #ecf0f1;
  border: none;
  border-right: 1px solid #2c3e50;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 50px;
}

.model-tab-button:last-child {
  border-right: none;
}

.model-tab-button.active {
  background: #4a4a4a;
  color: white;
  box-shadow: inset 0 -3px 0 #2980b9;
}

.model-tab-button:not(.active):hover {
  background: #3c5570;
  color: #fff;
}

.model-tab-button i {
  font-size: 0.9rem;
}

/* Individual Model Panels */
.blood-film-analysis-panel,
.prostate-gleason-analysis-panel,
.breast-cancer-analysis-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Prostate-specific styles */
.prostate-gleason-analysis-panel .analysis-header {
  background: #363636;
}

.prostate-gleason-analysis-panel .prostate-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

.prostate-gleason-analysis-panel .prostate-content h4 {
  color: #363636;
  margin-bottom: 1rem;
  font-size: 1.5rem;
}

/* Breast cancer-specific styles */
.breast-cancer-analysis-panel .analysis-header {
  background: #e84393;
}

/* Ensure all panels have consistent header styling */
.blood-film-analysis-panel .analysis-header,
.prostate-gleason-analysis-panel .analysis-header,
.breast-cancer-analysis-panel .analysis-header {
  background: rgb(54, 54, 54);
  color: white;
  padding: 1rem;
  flex-shrink: 0;
}

/* Responsive design */
@media (max-width: 1200px) {
  .model-tab-button {
    font-size: 0.7rem;
    padding: 0.6rem 0.5rem;
  }

  .model-tab-button i {
    font-size: 0.8rem;
  }
}

@media (max-width: 768px) {
  .model-tab-header {
    flex-direction: column;
  }

  .model-tab-button {
    border-right: none;
    border-bottom: 1px solid #2c3e50;
    min-height: 45px;
  }
}

/* AI-Assisted Comments Section */
.ai-assisted-section {
  margin: 20px 0;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.ai-note-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 10px;
}

.ai-note-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 15px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.ai-note-card h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ai-note-card h4::before {
  content: '🤖';
  font-size: 16px;
}

.ai-note-display {
  min-height: 40px;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.4;
  color: #6c757d;
  font-style: italic;
}

.ai-note-display.has-content {
  background-color: #fff;
  color: #495057;
  font-style: normal;
  border: 1px solid #e9ecef;
}

.ai-note-textarea {
  width: 100%;
  min-height: 80px;
  padding: 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
}

.ai-note-textarea:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.ai-note-actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}

.ai-note-actions button {
  padding: 4px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.ai-note-actions button:hover {
  background-color: #f8f9fa;
}

.ai-note-actions button.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
}

.ai-note-actions button.btn-primary:hover {
  background-color: #0056b3;
  border-color: #0056b3;
}

.ai-note-actions button.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

.ai-note-actions button.btn-danger:hover {
  background-color: #c82333;
  border-color: #bd2130;
}

.add-ai-comment-btn {
  padding: 6px 12px;
  background-color: rgb(74, 74, 74);
  color: white;
  border: 1px solid rgb(74, 74, 74);
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.add-ai-comment-btn:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.ai-assisted-label {
  font-size: 11px;
  color: #6c757d;
  margin-bottom: 8px;
  font-weight: 500;
}

@media (max-width: 768px) {
  .ai-note-cards {
    grid-template-columns: 1fr;
  }
}
/* Main panel container */
/*.blood-film-analysis-panel {
  width: auto;
  min-width: 20vw;
  max-width: 35vw;
  height: 80vh !important;
  min-height: 80vh !important;
  max-height: 80vh !important;
  overflow-y: auto;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  border-radius: 0;
  font-weight: bold;
}*/
.blood-film-analysis-panel {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* Ensure all text elements within the panel are bold */
.blood-film-analysis-panel * {
  font-weight: bold !important;
}

/* Header styles */
.analysis-header {
  background: rgb(54, 54, 54);
  color: white;
  padding: 1rem;
  flex-shrink: 0;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0;
}

.header-left {
  display: flex;
  flex-direction: column;
}

.panel-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
}

.close-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.analysis-info {
  display: flex;
  justify-content: flex-start;
  gap: 2rem;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-item .label {
  font-size: 0.8rem;
  opacity: 0.8;
}

.info-item .value {
  font-weight: 600;
}

.value.status {
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  font-size: 0.8rem;
  text-transform: capitalize;
}

.status.pending_review {
  background: rgba(255, 193, 7, 0.2);
  color: #856404;
}

.status.in_progress {
  background: rgba(23, 162, 184, 0.2);
  color: #0c5460;
}

.status.completed {
  background: rgba(40, 167, 69, 0.2);
  color: #155724;
}

/* Main content area */
.analysis-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Tab navigation */
.analysis-tabs {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.tab-header {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  flex-shrink: 0;
}

.tab-button {
  flex: 1;
  padding: 0.75rem 1rem;
  background: #6c757d;
  color: white;
  border: none;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.tab-button.active {
  background: rgb(74, 74, 74);
}

.tab-button:not(.active):hover {
  background: #5a6268;
}

/* Tab content */
.tab-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Analysis layout (two-column) */
.analysis-layout {
  flex: 1;
  display: flex;
  min-height: 0;
}

/* Left sidebar styles */
.cell-type-sidebar,
.morphology-sidebar,
.plt-sidebar {
  width: 300px;
  background: #f8f9fa;
  border-right: 1px solid #dee2e6;
  padding: 1rem;
  overflow-y: auto;
  flex-shrink: 0;
}

.category-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
  color: #000000;
}

.cell-type-list {
  margin-bottom: 1rem;
}

.cell-type-item {
  margin-bottom: 0.5rem;
}

.cell-type-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.9rem;
}

.cell-name {
  font-weight: 500;
  flex: 1;
  color: #000000 !important;
}

.cell-count {
  margin-left: 0.5rem;
  color: #6c757d;
}

.cell-percentage {
  margin-left: 0.5rem;
  font-weight: 600;
  min-width: 50px;
  text-align: right;
}

.cell-type-children {
  margin-top: 0.25rem;
  margin-left: 1rem;
}

.cell-type-child {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0.5rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 3px;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

.child-name {
  flex: 1;
}

.child-count,
.child-percentage {
  margin-left: 0.5rem;
  color: #6c757d;
  font-size: 0.8rem;
}

.total-count {
  font-weight: 600;
  padding: 0.5rem 0;
  border-top: 1px solid #dee2e6;
  margin-top: 0.5rem;
  color: #495057;
}

/* Main content area */
.analysis-main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* View toggle */
.view-toggle {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 0.5rem 1rem;
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.toggle-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.toggle-btn.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.toggle-btn:not(.active):hover {
  background: #e9ecef;
}

/* View content */
.view-content {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
}

/* Cell grid */
.cell-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  max-width: 400px;
}

.cell-thumbnail {
  position: relative;
  aspect-ratio: 1;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.2s;
}

.cell-thumbnail:hover {
  border-color: #007bff;
  transform: scale(1.02);
}

.cell-thumbnail.ai-classified {
  border-color: #007bff;
}

.cell-thumbnail.ai-classified::before {
  content: '';
  position: absolute;
  top: 4px;
  left: 4px;
  width: 12px;
  height: 12px;
  background: #007bff;
  border-radius: 2px;
  z-index: 2;
}

.cell-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #f8f9fa;
}

.cell-number {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
  font-weight: 600;
}

/* Placeholder content */
.slide-placeholder,
.rbc-placeholder,
.plt-placeholder {
  height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  text-align: center;
}

/* RBC region buttons */
.region-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.region-buttons button {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.region-buttons button.active {
  background: rgb(74, 74, 74);
  color: white;
  border-color: rgb(74, 74, 74);
}

/* PLT Analysis Tab */
/* Annotations Analysis Tab */
.annotations-analysis-tab {
  padding: 1.5rem;
  height: 100%;
  overflow-y: auto;
}


.annotation-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.filter-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
}

.term-filter-select {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.9rem;
  min-width: 200px;
}

.load-all-btn {
  padding: 0.5rem 1rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  min-width: 150px;
  justify-content: center;
}

.load-all-btn:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-1px);
}

.load-all-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  transform: none;
}

.load-all-btn i {
  font-size: 0.8rem;
}

.annotation-stats {
  font-size: 0.9rem;
  color: #6c757d;
}

.annotation-list {
  height: calc(100% - 100px);
  overflow-y: auto;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #6c757d;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-annotations {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

.annotation-items {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  padding: 0.5rem;
}

.annotation-item {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  break-inside: avoid; /* Prevents items from breaking across columns */
}

.annotation-item.clickable {
  cursor: pointer;
}

.annotation-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
}

.annotation-preview-container {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.annotation-thumbnail {
  position: relative;
  flex-shrink: 0;
  display: inline-block;
  background-position: center center;
  background-repeat: no-repeat;
}

.annotation-thumbnail::before {
  content: 'No Image';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #6c757d;
  font-size: 10px;
  text-align: center;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.3s;
}

.annotation-thumbnail:not([style*="background-image"])::before,
.annotation-thumbnail[style*="background-image: url()"]::before {
  opacity: 1;
}

.preview-button {
  position: absolute;
  top: 3px;
  right: 3px;
  font-size: 10px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.preview-button:hover {
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.annotation-info {
  flex: 1;
}

.annotation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.annotation-id {
  font-weight: 600;
  color: #007bff;
}

.annotation-term {
  background: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  color: #495057;
}

.annotation-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #6c757d;
}

.annotation-location {
  font-family: monospace;
}

.annotation-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.annotation-user {
  font-weight: 500;
}

.plt-analysis-tab {
  padding: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.plt-content {
  max-width: 800px;
  width: 100%;
}

.plt-content h3 {
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.5rem;
  color: #495057;
}

/* PLT Header */
.plt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.plt-header h3 {
  margin: 0;
  font-size: 1.5rem;
  color: #fff;
}

/* PLT Calculation Display */
.plt-calculation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  background: white;
  padding: 2rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.calc-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.calc-label {
  font-size: 0.8rem;
  color: #6c757d;
  text-align: center;
  line-height: 1.2;
}

.plt-input {
  width: 80px;
  height: 60px;
  text-align: center;
  border: 2px solid #dee2e6;
  border-radius: 4px;
  font-size: 1.2rem;
  font-weight: 500;
}

.plt-input:focus {
  outline: none;
  border-color: #007bff;
}

.factor-display {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 4px;
  font-size: 1.2rem;
  font-weight: 500;
  color: #495057;
}

.operator .operator-symbol {
  font-size: 1.5rem;
  font-weight: 600;
  color: #495057;
}

.calc-section.result .result-value {
  font-size: 1.2rem;
  font-weight: 600;
  color: rgb(74, 74, 74);
  text-align: center;
  padding: 1rem;
  background: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 4px;
  min-width: 120px;
}

/* Bottom navigation */
.bottom-navigation {
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  padding: 1rem;
  flex-shrink: 0;
}

.nav-buttons {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.nav-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.nav-btn.primary {
  background: #ffc107;
  color: #212529;
}

.nav-btn.primary:hover {
  background: #e0a800;
}

.nav-btn.secondary {
  background: #6c757d;
  color: white;
}

.nav-btn.secondary:hover:not(:disabled) {
  background: #5a6268;
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Comments section */
.comments-section {
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  padding: 1rem;
  flex-shrink: 0;
}

.comments-section h3 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  color: #495057;
}

.comment-groups {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.comment-group {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.comment-label {
  font-weight: 600;
  color: #495057;
}

.add-comment-btn {
  background: rgb(74, 74, 74);
  color: white;
  border: none;
  padding: 0.25rem 0.75rem;
  border-radius: 3px;
  font-size: 0.8rem;
  cursor: pointer;
}

.add-comment-btn:hover {
  background: #218838;
}

.ai-diagnosis {
  padding: 0.75rem 1rem;
}

.ai-label {
  font-size: 0.9rem;
  color: #6c757d;
  display: block;
  margin-bottom: 0.5rem;
}

.ai-content {
  min-height: 2rem;
  background: #f8f9fa;
  border-radius: 3px;
  padding: 0.5rem;
  color: #6c757d;
  font-size: 0.9rem;
}

/* Slide viewer styles */
.slide-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.zoom-controls {
  display: flex;
  gap: 0.5rem;
}

.zoom-btn {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoom-btn:hover {
  background: #e9ecef;
}

.processing-legend {
  display: flex;
  gap: 1rem;
  font-size: 0.85rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  border: 1px solid #ccc;
}

.legend-color.ai-processed {
  background: #007bff;
}

.legend-color.unprocessed {
  background: #6c757d;
}

.slide-integration {
  position: relative;
  width: 100%;
  height: 100%;
}

.viewer-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.cell-annotations-overlay {
  position: relative;
  width: 100%;
  height: 100%;
}

.cell-marker {
  pointer-events: all;
  cursor: pointer;
  z-index: 10;
}

.cell-marker-dot {
  width: 8px;
  height: 8px;
  background: #dc3545;
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.cell-marker-dot.ai-classified {
  background: #007bff;
}

.cell-marker-label {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.8);
  color: white;
  padding: 1px 4px;
  border-radius: 2px;
  font-size: 0.7rem;
  white-space: nowrap;
}

.slide-placeholder h4 {
  margin-bottom: 1rem;
  color: #495057;
}

.slide-placeholder p {
  margin: 0.5rem 0;
  color: #6c757d;
}

.ai-color {
  color: #007bff;
  font-weight: 600;
}

.unprocessed-color {
  color: #6c757d;
  font-weight: 600;
}

.integration-status {
  margin-top: 2rem;
  padding: 1rem;
  background: #e8f4fd;
  border-radius: 6px;
  border-left: 4px solid #007bff;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #0c5460;
}

/* Blood Film Slide Viewer Styles */
.blood-film-slide-viewer {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.slide-viewer-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.slide-viewer-header {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.slide-viewer-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
}

.viewer-controls {
  display: flex;
  gap: 0.5rem;
}

.control-btn {
  background: white;
  border: 1px solid #dee2e6;
  color: #495057;
  padding: 0.375rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.control-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.slide-viewer-content {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #f8f9fa;
}

.slide-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.annotations-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.annotation-marker {
  position: absolute;
  pointer-events: all;
  cursor: pointer;
  z-index: 10;
}

.marker-dot {
  width: 12px;
  height: 12px;
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  background: #dc3545;
  transition: all 0.2s;
}

.marker-dot:hover {
  transform: scale(1.2);
}

.marker-dot.ai-classified {
  background: #007bff;
}

.marker-dot.user-classified {
  background: rgb(74, 74, 74);
}

/* Cell type specific colors */
.marker-dot.neutrophil {
  background: #ff6b6b;
}

.marker-dot.lymphocyte {
  background: #4ecdc4;
}

.marker-dot.monocyte {
  background: #45b7d1;
}

.marker-dot.eosinophil {
  background: #f9ca24;
}

.marker-dot.basophil {
  background: #6c5ce7;
}

.marker-label {
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75rem;
  white-space: nowrap;
  pointer-events: none;
}

.slide-loading,
.slide-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  font-size: 0.9rem;
}

.slide-error {
  color: #dc3545;
}

.slide-loading i,
.slide-error i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.slide-info {
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  padding: 0.75rem 1rem;
  flex-shrink: 0;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row .label {
  color: #6c757d;
  font-weight: 500;
}

.info-row .value {
  color: #495057;
  font-weight: 600;
}

/* New WBC Simple Layout Styles */
.wbc-simple-layout {
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.wbc-counts-container {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}

.wbc-header {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.wbc-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #495057;
}

.wbc-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.refresh-btn {
  background: rgb(74, 74, 74);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;
}
.refresh-btn:hover {
  background: #218838;
}
.total-count-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  margin: 20px 20px;
}

.total-label {
  color: #6c757d;
  font-weight: 500;
}

.total-value {
  color: rgb(74, 74, 74);
  font-weight: 700;
  font-size: 1.2rem;
}

.cell-type-categories {
  padding: 1.5rem;
}

.category-section {
  margin-bottom: 2rem;
}

.category-section:last-child {
  margin-bottom: 0;
}

.category-header {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color:rgb(255, 255, 255);
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.cell-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.cell-type-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
  transition: all 0.2s;
}

.cell-type-card.has-count {
  background: #ffffff;
  border-color: rgb(74, 74, 74);
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.1);
}

.cell-type-info {
  margin-bottom: 0.5rem;
}

.cell-type-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.cell-name {
  font-weight: 600;
  color: #495057;
  font-size: 0.95rem;
}

.cell-count {
  color: rgb(74, 74, 74);
  font-weight: 700;
  font-size: 0.9rem;
}

.cell-percentage {
  color: #007bff;
  font-weight: 600;
  font-size: 1.1rem;
}

.cell-subtypes {
  border-top: 1px solid #e9ecef;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
}

.cell-subtype {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0.5rem;
  margin-bottom: 0.25rem;
  background: #f1f3f4;
  border-radius: 3px;
  font-size: 0.85rem;
}

.cell-subtype.has-count {
  background: #e8f5e8;
  border-left: 3px solid rgb(74, 74, 74);
}

.subtype-name {
  flex: 1;
  color: #495057;
}

.subtype-count {
  color: rgb(74, 74, 74);
  font-weight: 600;
  margin: 0 0.5rem;
}

.subtype-percentage {
  color: #007bff;
  font-weight: 600;
  min-width: 50px;
  text-align: right;
}

.analysis-summary {
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  padding: 1.5rem;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 4px;
  border-left: 4px solid #007bff;
}

.stat-label {
  color: #6c757d;
  font-weight: 500;
  font-size: 0.9rem;
}

.stat-value {
  color: #495057;
  font-weight: 700;
  font-size: 1.1rem;
}

.reference-ranges {
  background: white;
  border-radius: 6px;
  padding: 1rem;
  border: 1px solid #dee2e6;
}

.reference-ranges h5 {
  margin: 0 0 0.75rem 0;
  color: #495057;
  font-size: 0.95rem;
  font-weight: 600;
}

.reference-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
}

.reference-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 3px;
  font-size: 0.85rem;
}

.ref-cell {
  color: #495057;
  font-weight: 500;
}

.ref-range {
  color: #6c757d;
  font-family: monospace;
}

/* RBC Simple Layout Styles */
.rbc-simple-layout {
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.rbc-morphology-container {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}

.rbc-header {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 1.5rem;
}

.rbc-header h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #495057;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}
.auto-calc-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;
  min-width: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.auto-calc-btn:hover:not(:disabled) {
  background: #0056b3;
}

.auto-calc-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.8;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.analysis-description {
  color: #6c757d;
  font-size: 0.9rem;
}

.morphology-categories {
  padding: 1.5rem;
}

.morphology-category-section {
  margin-bottom: 2rem;
}

.morphology-category-section:last-child {
  margin-bottom: 0;
}

.morphology-category-header {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.morphology-items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1rem;
}

.morphology-item-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
  transition: all 0.2s;
}

.morphology-item-card:hover {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.morphology-item-header {
  margin-bottom: 0.75rem;
}

.morphology-item-name {
  font-weight: 600;
  color: #495057;
  font-size: 0.95rem;
}

.morphology-scoring {
  margin-bottom: 0.75rem;
}

.score-options {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.score-option {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  font-size: 0.9rem;
}

.score-option input[type="radio"] {
  margin: 0;
  width: 16px;
  height: 16px;
}

.score-label {
  font-weight: 600;
  color: #495057;
}

.reference-range {
  font-size: 0.8rem;
  color: #6c757d;
  font-style: italic;
}

.range-label {
  background: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  font-family: monospace;
}

.morphology-description {
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.4;
}

/* RBC Count Display */
.rbc-count-display {
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  padding: 1.5rem;
  text-align: center;
}

.count-header h4 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1.1rem;
}

.count-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.count-number {
  font-size: 2rem;
  font-weight: 700;
  color: #007bff;
}

.count-label {
  font-size: 0.9rem;
  color: #6c757d;
}

/* Updated table styles for new structure */
.morphology-table .table-header {
  display: grid;
  grid-template-columns: 2fr 1.2fr 0.8fr 0.8fr 0.8fr;
  gap: 1rem;
  background: #f8f9fa;
  padding: 1rem;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
}

.morphology-table .table-row {
  display: grid;
  grid-template-columns: 2fr 1.2fr 0.8fr 0.8fr 0.8fr;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  align-items: center;
}

/* Radio button group in 0/1+2+3+ column */
.radio-group {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
}

.score-radio-container {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
}

.score-radio-container input[type="radio"] {
  transform: scale(1.1);
}

.radio-label {
  font-size: 0.85rem;
  color: #495057;
  font-weight: 500;
}

/* Threshold range display columns */
.threshold-column {
  text-align: center;
}

.threshold-range {
  font-size: 0.85rem;
  color: #495057;
  text-align: center;
  font-weight: 500;
  line-height: 1.3;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 4px;
  border-left: 4px solid #dc3545;
}

.stat-label {
  color: #6c757d;
  font-weight: 500;
  font-size: 0.9rem;
}

.stat-value {
  color: #495057;
  font-weight: 700;
  font-size: 1.1rem;
}

/* Radio button styling */
input[type="radio"] {
  accent-color: #dc3545;
}

.score-option:hover .score-label {
  color: #dc3545;
}

.score-option input[type="radio"]:checked + .score-label {
  color: #dc3545;
  font-weight: 700;
}

/* New RBC Morphology Scoring Styles */
.morphology-table-container {
  padding: 1.5rem;
}

.morphology-category-table {
  margin-bottom: 2rem;
}

.morphology-category-table:last-child {
  margin-bottom: 0;
}

.table-category-header {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dc3545;
  padding-bottom: 0.5rem;
}

.morphology-table {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  background: white;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1.5fr;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.header-cell {
  padding: 1rem;
  font-weight: 600;
  color: #495057;
  border-right: 1px solid #dee2e6;
  text-align: center;
  font-size: 0.9rem;
}

.header-cell:last-child {
  border-right: none;
}

.header-cell.morphology-name {
  text-align: left;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1.5fr;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
}

.table-row:hover {
  background-color: #f8f9fa;
}

.table-row.scored-row {
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
}

.table-row:last-child {
  border-bottom: none;
}

.table-cell {
  padding: 1rem;
  border-right: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
}

.table-cell:last-child {
  border-right: none;
}

.table-cell.morphology-name {
  justify-content: flex-start;
}

.morphology-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.morphology-title {
  font-weight: 600;
  color: #495057;
  font-size: 0.95rem;
}

.morphology-desc {
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.3;
}

.score-radio-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
  width: 100%;
}

.score-radio-container:hover {
  background-color: #e9ecef;
}

.score-radio-container input[type="radio"] {
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: #dc3545;
}

.threshold-info {
  font-size: 0.8rem;
  color: #6c757d;
  text-align: center;
  font-family: monospace;
  background: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  min-width: 60px;
}

.score-radio-container:has(input:checked) {
  background-color: #dc3545;
  color: white;
}

.score-radio-container:has(input:checked) .threshold-info {
  background-color: rgba(255, 255, 255, 0.9);
  color: #495057;
  font-weight: 600;
}

.selected-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.score-badge {
  background: #dc3545;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  font-weight: 600;
  font-size: 0.8rem;
}

.score-threshold {
  font-size: 0.8rem;
  color: #6c757d;
  font-family: monospace;
}

.no-score {
  color: #6c757d;
  font-style: italic;
  font-size: 0.85rem;
}

.rbc-analysis-summary {
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  padding: 1.5rem;
}

.summary-header {
  margin-bottom: 1rem;
}

.summary-header h4 {
  margin: 0;
  color: #495057;
  font-size: 1.1rem;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 4px;
  border-left: 4px solid #dc3545;
}

.stat-label {
  color: #6c757d;
  font-weight: 500;
  font-size: 0.9rem;
}

.stat-value {
  color: #495057;
  font-weight: 700;
  font-size: 1.1rem;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .table-header,
  .table-row {
    grid-template-columns: 2fr 0.8fr 0.8fr 0.8fr 1.2fr;
  }

  .header-cell,
  .table-cell {
    padding: 0.75rem 0.5rem;
    font-size: 0.85rem;
  }
}

@media (max-width: 768px) {
  .morphology-table {
    font-size: 0.8rem;
  }

  .table-header,
  .table-row {
    grid-template-columns: 1fr;
  }

  .table-cell {
    border-right: none;
    border-bottom: 1px solid #e9ecef;
    justify-content: flex-start;
  }

  .table-cell.score-column {
    flex-direction: row;
    justify-content: space-between;
  }
}

/* Comments Section Styles */
.comments-section {
  padding: 2rem;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
}
.comments-section h3 {
  margin: 0 0 1.5rem 0;
  color: #495057;
  font-size: 1.25rem;
  font-weight: 600;
}
.comment-groups {
  display: grid;
  gap: 1.5rem;
}
.comment-group {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
}
.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.comment-label {
  font-weight: 600;
  color: #495057;
  font-size: 1rem;
}
.add-comment-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;
}
.add-comment-btn:hover {
  background: #0056b3;
}
.existing-comment {
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-left: 4px solid #007bff;
  border-radius: 4px;
}
.comment-content {
  color: #495057;
  line-height: 1.5;
  margin-bottom: 0.5rem;
  white-space: pre-wrap;
}
.comment-meta {
  font-size: 0.8rem;
  color: #6c757d;
}
.comment-date {
  font-style: italic;
}

/* Inline Comment Editor Styles */
.comment-editor {
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.comment-textarea-inline {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.9rem;
  line-height: 1.5;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}
.comment-textarea-inline:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}
.comment-meta-info-inline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #dee2e6;
}
.user-info {
  display: flex;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #6c757d;
}
.user-name {
  font-weight: 600;
}
.comment-actions {
  display: flex;
  gap: 0.5rem;
}
.btn-secondary-small, .btn-primary-small, .btn-danger-small {
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 3px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background-color 0.2s;
}
.btn-secondary-small {
  background: #6c757d;
  color: white;
}
.btn-secondary-small:hover {
  background: #545b62;
}
.btn-primary-small {
  background: #007bff;
  color: white;
}
.btn-primary-small:hover {
  background: #0056b3;
}
.btn-danger-small {
  background: #dc3545;
  color: white;
}
.btn-danger-small:hover {
  background: #c82333;
}

/* PLT Data Source Indicator */
.data-source-indicator {
  display: block;
  font-size: 0.7rem;
  font-weight: normal;
  margin-top: 0.25rem;
}
.data-source-indicator.auto {
  color: rgb(74, 74, 74);
}
.data-source-indicator.manual {
  color: #007bff;
}

/* WBC Table Styles */
.wbc-table-container {
  margin: 20px 0;
}

.wbc-table {
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
}

.wbc-table .table-header {
  display: grid;
  grid-template-columns: 1fr auto;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
}

.wbc-table .cell-type-col {
  padding: 15px 20px;
  text-align: left;
}

.wbc-table .percentage-col {
  padding: 15px 20px;
  text-align: right;
  min-width: 80px;
}

.wbc-table .table-body {
  background: #ffffff;
}

.wbc-table .wbc-row {
  display: grid;
  grid-template-columns: 1fr auto;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
}

.wbc-table .wbc-row:hover {
  background-color: #f8f9fa;
}

.wbc-table .wbc-row:last-child {
  border-bottom: none;
}

.wbc-table .cell-type {
  padding: 12px 20px;
  color: #495057;
  font-size: 14px;
}

.wbc-table .percentage {
  padding: 12px 20px;
  text-align: right;
  color: #495057;
  font-size: 14px;
  font-weight: 500;
  min-width: 80px;
}

.wbc-table .count {
  color: #6c757d;
  font-weight: normal;
  margin-left: 4px;
}

.wbc-table .asterisk {
  color: #dc3545;
  font-weight: bold;
  margin-left: 2px;
}

.wbc-table .no-wbc-data .cell-type {
  color: #6c757d;
  font-style: italic;
}

/* Legacy WBC Inline List Styles (now unused) */
.cell-type-list {
  margin: 20px 0;
}

.cell-type-results {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 15px;
  min-height: 120px;
}

.cell-type-row {
  display: block;
  margin: 3px 0;
  padding: 6px 12px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.4;
}

.cell-type-row .cell-name {
  font-weight: 500;
  color: #333;
}

.cell-type-row .cell-count {
  color: #666;
  margin: 0 4px;
}

.cell-type-row .cell-percentage {
  color: #007bff;
  font-weight: 600;
}

.no-results {
  color: #6c757d;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

.no-wbc-data .cell-type {
  color: #6c757d;
  font-style: italic;
}

/* Professional Refresh Button */
.refresh-btn-professional {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  color: #007bff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 10px;
}

.refresh-btn-professional:hover {
  background: #0056b3;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,123,255,0.3);
}

.refresh-btn-professional:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0,123,255,0.3);
}

.refresh-icon {
  flex-shrink: 0;
}

/* Hide old refresh button styles if they exist */
.refresh-btn {
  display: none;
}

/* Report Tab Styles */
.report-analysis-tab {
  padding: 20px;
  background: #ffffff;
  height: 100%;
  overflow-y: auto;
}

.report-content {
  max-width: 800px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.report-title {
  font-size: 24px;
  font-weight: 300;
  color: #666;
  margin-bottom: 15px;
  text-align: left;
}

.image-info {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 25px;
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
}

.image-details {
  display: flex;
  align-items: center;
  gap: 8px;
}

.image-label {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
}

.image-value {
  color: #6c757d;
  font-size: 14px;
  font-family: 'Courier New', monospace;
  background: #ffffff;
  padding: 2px 6px;
  border-radius: 3px;
  border: 1px solid #e9ecef;
}

.report-section {
  margin-bottom: 40px;
}

.report-table {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 1fr auto;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
}

.cell-type-col {
  padding: 15px 20px;
  text-align: left;
}

.percentage-col {
  padding: 15px 20px;
  text-align: right;
  min-width: 80px;
}

.table-body {
  background: #ffffff;
}

.wbc-row {
  display: grid;
  grid-template-columns: 1fr auto;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
}

.wbc-row:hover {
  background-color: #f8f9fa;
}

.wbc-row.child-row {
  background-color: #fafbfc;
}

.wbc-row.child-row .cell-type {
  padding-left: 40px; /* Indent child rows */
}

.cell-type {
  padding: 12px 20px;
  color: #495057;
  font-size: 16px;
}

.percentage {
  padding: 12px 20px;
  text-align: right;
  color: #495057;
  font-size: 16px;
  font-weight: 500;
  min-width: 80px;
}

.count {
  color: #6c757d;
  font-weight: normal;
  margin-left: 4px;
}

.asterisk {
  color: #dc3545;
  font-weight: bold;
  margin-left: 2px;
}

.report-summary {
  margin-top: 20px;
  padding: 15px 20px;
  background: #f8f9fa;
  border-radius: 6px;
}

.summary-item {
  margin-bottom: 10px;
  color: #495057;
  font-size: 16px;
}

.summary-note {
  color: #6c757d;
  font-size: 14px;
  font-style: italic;
}

.rbc-section {
  padding-top: 20px;
  border-top: 2px solid #dee2e6;
  margin-bottom: 30px;
}

.plt-section {
  padding-top: 20px;
  border-top: 2px solid #dee2e6;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #495057 !important;
  margin-bottom: 15px;
}

.rbc-morphology-results {
  background: #ffffff;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #dee2e6;
}

.rbc-morphology-item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 15px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f4;
}

.rbc-morphology-item:last-child {
  border-bottom: none;
}

.morphology-name {
  font-size: 16px;
  color: #495057;
  font-weight: 500;
}

.morphology-score {
  font-size: 16px;
  font-weight: 600;
  color: #007bff;
  min-width: 40px;
  text-align: center;
}

.morphology-percentage {
  font-size: 14px;
  color: #6c757d;
  min-width: 60px;
  text-align: right;
}

.no-rbc-data {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
}

.no-rbc-data .morphology-name {
  color: #6c757d;
  font-style: italic;
}

.rbc-summary {
  margin-top: 15px;
  padding: 10px 15px;
  background: #f8f9fa;
  border-radius: 6px;
}

.rbc-summary .summary-note {
  color: #6c757d;
  font-size: 13px;
  font-style: italic;
}

.plt-estimate {
  font-size: 16px;
  color: #495057;
  padding: 10px 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .report-content {
    padding: 0 10px;
  }

  .cell-type-col,
  .percentage-col,
  .cell-type,
  .percentage {
    padding: 10px 15px;
    font-size: 14px;
  }

  .wbc-row.child-row .cell-type {
    padding-left: 30px;
  }
}

/* Loading indicators for tabs */
.tab-loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6c757d;
  font-size: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* No data message */
.no-data-message {
  padding: 30px 20px;
  text-align: center;
  color: #6c757d;
  font-style: italic;
}

.no-data-message p {
  margin: 0;
  font-size: 14px;
}

/* RBC Normal cell type styling */
.normal-count {
  color: rgb(74, 74, 74);
  font-weight: normal;
  margin-left: 8px;
  font-size: 0.9em;
}

.normal-info {
  text-align: center !important;
  padding: 8px !important;
}

.normal-status {
  color: rgb(74, 74, 74);
  font-weight: 500;
  font-size: 0.9em;
}

/* Update refresh button states */
.refresh-btn-professional:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-btn-professional.loading {
  position: relative;
}

.refresh-btn-professional.loading::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 16px;
  height: 16px;
  margin-left: -8px;
  margin-top: -8px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Blood Film Annotation Details Playground */
.blood-film-annotation-details-playground {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1500;
}

.blood-film-draggable {
  background: #f2f2f2;
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
  pointer-events: auto;
  z-index: 1500 !important;
  font-weight: normal !important;
}

.blood-film-draggable * {
  font-weight: bold !important;
  color: black !important;
}

.blood-film-draggable .actions {
  padding: 0.35em;
  text-align: right;
  background-color: #e5e5e5;
  border-bottom: 1px solid #b5b5b5;
  border-radius: 5px 5px 0 0;
  display: flex;
  align-items: center;
}

.blood-film-draggable .actions h1 {
  font-size: 0.9rem;
  padding: 0;
  flex: 1;
  text-align: left;
  margin-left: 0.4em;
  font-weight: 600 !important;
}

.blood-film-draggable .actions .button {
  margin-left: 0.25rem;
  width: 1.75rem;
}

.blood-film-annotation-details-container {
  padding: 1rem;
  overflow: auto;
  height: 100%;
  max-height: 400px;
}

.blood-film-draggable .selected-preview {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
}

.blood-film-draggable .selected-thumbnail {
  width: 80px;
  height: 80px;
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  border: 3px solid white;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
  border-radius: 4px;
  flex-shrink: 0;
}

.blood-film-draggable .selected-info {
  flex: 1;
}

.blood-film-draggable .selected-id {
  font-weight: bold !important;
  color: black !important;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
}

.blood-film-draggable .selected-term {
  background: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  color: black !important;
  display: inline-block;
  margin-bottom: 0.25rem;
}

.blood-film-draggable .selected-location {
  font-family: monospace;
  color: black !important;
  font-size: 0.9rem;
}

.blood-film-draggable .terms-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.blood-film-draggable .terms-section h5 {
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  color: black !important;
  font-weight: bold !important;
}

.blood-film-draggable .terms-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.blood-film-draggable .term-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.blood-film-draggable .term-name {
  font-weight: bold !important;
  color: black !important;
}

.blood-film-draggable .term-confidence {
  font-size: 0.8rem;
  color: black !important;
  font-weight: bold !important;
}

/* Term Reclassification Styles */
.blood-film-draggable .current-terms {
  margin-bottom: 1rem;
}

.blood-film-draggable .current-terms-label {
  font-size: 0.9rem;
  color: black !important;
  font-weight: bold !important;
  margin-bottom: 0.5rem;
}

.blood-film-draggable .term-reclassification {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.blood-film-draggable .reclassification-label {
  display: block;
  font-size: 0.9rem;
  color: black !important;
  font-weight: bold !important;
  margin-bottom: 0.5rem;
}

.blood-film-draggable .select-container {
  position: relative;
}

.blood-film-draggable .term-reclassification-select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  color: black !important;
  font-size: 0.9rem;
  font-weight: normal !important;
}

.blood-film-draggable .term-reclassification-select:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.blood-film-draggable .term-reclassification-select:disabled {
  background: #f8f9fa;
  color: #6c757d !important;
  cursor: not-allowed;
}

.blood-film-draggable .term-reclassification-select optgroup {
  font-weight: bold !important;
  color: black !important;
}

.blood-film-draggable .term-reclassification-select option {
  font-weight: normal !important;
  color: black !important;
  padding: 0.25rem;
}

.blood-film-draggable .term-reclassification-select option:disabled {
  color: #6c757d !important;
  font-style: italic;
}

.blood-film-draggable .updating-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #e3f2fd;
  border-radius: 4px;
  border: 1px solid #bbdefb;
  font-size: 0.9rem;
  color: black !important;
  font-weight: bold !important;
}

.blood-film-draggable .updating-indicator .fa-spinner {
  color: #1976d2 !important;
}

/* Prostate Gleason Scoring Specific Styles */
.prostate-gleason-analysis-panel .analysis-header {
  background: #363636;
}

/* Pattern-specific styling */
.pattern-3 {
  background-color: #e8f5e8 !important;
  color: #2e7d32 !important;
}

.pattern-4 {
  background-color: #fff3e0 !important;
  color: #ef6c00 !important;
}

.pattern-5 {
  background-color: #ffebee !important;
  color: #c62828 !important;
}

.pattern-stroma {
  background-color: #f3e5f5 !important;
  color: #7b1fa2 !important;
}

.pattern-benign {
  background-color: #e8eaf6 !important;
  color: #303f9f !important;
}

/* Area Calculation Styles */
.areas-analysis-tab {
  padding: 1.5rem;
}

.areas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.areas-header h3 {
  margin: 0;
  color: #495057;
}

.pixel-config-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.pixel-config-section h4 {
  margin: 0 0 1rem 0;
  color: #495057;
}

.pixel-config {
  display: flex;
  gap: 2rem;
  align-items: flex-end;
}

.config-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.config-input label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.pixel-input {
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  width: 120px;
  font-size: 0.9rem;
}

.config-info {
  color: #6c757d;
  font-size: 0.9rem;
}

.area-summary {
  margin-top: 2rem;
}

.area-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.area-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}

.area-card-header {
  padding: 1rem;
  color: black;
  font-weight: 600;
}

.area-card-header.pattern-3 {
  background: #4CAF50;
}

.area-card-header.pattern-4 {
  background: #FF9800;
}

.area-card-header.pattern-5 {
  background: #F44336;
}

.area-card-header.total-header {
  background: #6c757d;
}

.area-card-content {
  padding: 1.5rem 1rem;
  text-align: center;
}

.area-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #495057;
  margin-bottom: 0.5rem;
}

.area-count {
  font-size: 0.9rem;
  color: #6c757d;
}

.total-value {
  color: #363636;
}

/* Scoring Tab Styles */
.scoring-analysis-tab {
  padding: 1.5rem;
}

.scoring-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.scoring-header h3 {
  margin: 0;
  color: #495057;
}

.pattern-percentages {
  margin-bottom: 2rem;
}

.pattern-percentages h4 {
  margin: 0 0 1rem 0;
  color: #495057;
}

.percentage-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.percentage-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
  transition: all 0.3s ease;
}

.percentage-card.primary-pattern {
  border: 2px solid #4CAF50;
  box-shadow: 0 4px 8px rgba(76, 175, 80, 0.2);
}

.percentage-card.secondary-pattern {
  border: 2px solid #FF9800;
  box-shadow: 0 4px 8px rgba(255, 152, 0, 0.2);
}

.percentage-card.tertiary-pattern {
  border: 2px solid #9C27B0;
  box-shadow: 0 4px 8px rgba(156, 39, 176, 0.2);
}

.percentage-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.percentage-card-header h5 {
  margin: 0;
  color: #495057;
  font-size: 1.1rem;
}

.pattern-role {
  background: #6c757d;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  font-size: 0.8rem;
  font-weight: 600;
}

.percentage-card-content {
  text-align: center;
}

.percentage-value {
  font-size: 2rem;
  font-weight: 700;
  color: #495057;
  margin-bottom: 1rem;
}

.percentage-bar {
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.percentage-fill {
  height: 100%;
  transition: width 0.5s ease;
}

.pattern-3-fill {
  background: #4CAF50;
}

.pattern-4-fill {
  background: #FF9800;
}

.pattern-5-fill {
  background: #F44336;
}

/* Gleason Results Styles */
.gleason-results {
  margin: 2rem 0;
}

.results-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}

.results-header {
  background: #f8f9fa;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.results-header h4 {
  margin: 0;
  color: #495057;
}

.results-content {
  padding: 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.score-display {
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.gleason-score-large {
  font-size: 4rem;
  font-weight: 700;
  color: #363636;
  line-height: 1;
  margin-bottom: 1rem;
}

.grade-group {
  font-size: 1.5rem;
  font-weight: 600;
  color: #495057;
  background: #f8f9fa;
  padding: 0.5rem 1rem;
  border-radius: 4px;
}

.pattern-breakdown {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.breakdown-label {
  font-weight: 600;
  color: #495057;
}

.breakdown-value {
  font-weight: 700;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
}

.breakdown-value.pattern-3 {
  background: #4CAF50;
  color: white;
}

.breakdown-value.pattern-4 {
  background: #FF9800;
  color: white;
}

.breakdown-value.pattern-5 {
  background: #F44336;
  color: white;
}

/* Validation Rules */
.validation-rules {
  margin-top: 2rem;
}

.validation-rules h4 {
  margin: 0 0 1rem 0;
  color: #495057;
}

.rules-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.rule-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.9rem;
}

.rule-item.rule-violation {
  background: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
}

.rule-item i {
  font-size: 1rem;
}

.rule-item.rule-violation i {
  color: #f39c12;
}

.rule-item:not(.rule-violation) i {
  color: #27ae60;
}

/* Prostate Report Styles */
.prostate-report-tab {
  padding: 1.5rem;
}

.gleason-summary-section {
  margin: 2rem 0;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
  color: black;
}

.pattern-distribution-section {
  margin: 2rem 0;
}

.distribution-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.distribution-table .table-header {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
}

.distribution-table .table-cell {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.distribution-table .table-body {
  background: white;
}

.distribution-table .table-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  border-bottom: 1px solid #e9ecef;
}

.distribution-table .table-row:last-child {
  border-bottom: none;
}

.primary-row {
  background: #e8f5e8 !important;
  border-left: 4px solid #4CAF50;
}

.secondary-row {
  background: #fff3e0 !important;
  border-left: 4px solid #FF9800;
}

.clinical-notes-section {
  margin: 2rem 0;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.notes-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.note-item {
  padding: 1rem;
  background: white;
  border-radius: 6px;
  border-left: 4px solid #007bff;
}

/* Responsive Design */
@media (max-width: 768px) {
  .scoring-header,
  .areas-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .results-content {
    grid-template-columns: 1fr;
  }

  .pixel-config {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .distribution-table .table-header,
  .distribution-table .table-row {
    grid-template-columns: 1fr;
  }

  .summary-cards {
    grid-template-columns: 1fr;
  }
}
/* Add to the prostate panel styles */
.prostate-gleason-analysis-panel {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.prostate-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 0; /* Important for flex children to scroll */
}

.analysis-tabs {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Important for flex children to scroll */
}

.tab-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 0; /* Important for flex children to scroll */
}

/* Ensure all tab content areas can scroll */
.areas-analysis-tab,
.scoring-analysis-tab,
.prostate-report-tab {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

/* Pattern-specific styling */
.pattern-3 {
  background-color: #e8f5e8 !important;
  border-left: 4px solid #4CAF50 !important;
}

.pattern-4 {
  background-color: #fff3e0 !important;
  border-left: 4px solid #FF9800 !important;
}

.pattern-5 {
  background-color: #ffebee !important;
  border-left: 4px solid #F44336 !important;
}

.stroma-card {
  background-color: #f3e5f5 !important;
  border-left: 4px solid #9C27B0 !important;
}

.benign-card {
  background-color: #e3f2fd !important;
  border-left: 4px solid #2196F3 !important;
}

.pattern-role {
  background: #666;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  margin-left: 8px;
}

.pattern-role.tertiary {
  background: #9C27B0;
}

/* Area cards */
.area-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin: 20px 0;
}

.area-card {
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.area-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.area-card-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: bold;
}

.area-card-content {
  text-align: center;
}

.area-value {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
}

.area-count {
  font-size: 12px;
  color: #7f8c8d;
  margin: 5px 0;
}

.area-percentage {
  font-size: 14px;
  font-weight: bold;
  color: #e74c3c;
}

/* Distribution chart */
.distribution-chart {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin: 15px 0;
}

.chart-bars {
  display: flex;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
}

.pattern-bar {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width 0.3s ease;
  position: relative;
}

.pattern-bar.primary-pattern {
  border: 2px solid #000;
  box-sizing: border-box;
}

.bar-label {
  font-size: 11px;
  font-weight: bold;
  color: black;
  text-shadow: 1px 1px 1px rgba(0,0,0,0.5);
  text-align: center;
  padding: 0 5px;
}

/* Gleason score display */
.gleason-score-large {
  font-size: 48px;
  font-weight: bold;
  color: #2c3e50;
  text-align: center;
  margin: 10px 0;
}

.grade-group {
  font-size: 20px;
  font-weight: bold;
  color: #e74c3c;
  text-align: center;
  margin-bottom: 15px;
}

/* No data messages */
.no-areas-message,
.no-score-message,
.no-data-message {
  text-align: center;
  padding: 40px 20px;
  color: #7f8c8d;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 20px 0;
}

.no-areas-message p,
.no-score-message p,
.no-data-message p {
  margin: 10px 0;
}

.score-description {
  color: black;
}

/* Editable prostate values styles */
.editable-card {
  position: relative;
  border: 2px solid transparent;
  transition: border-color 0.3s ease;
}

.editable-card:hover {
  border-color: #e0e0e0;
}

.editable-value {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.value-actions {
  display: flex !important;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.editable-value:hover .value-actions {
  opacity: 1;
}

.manual-override-badge {
  color: #ff9800;
  font-size: 10px;
  margin-left: 4px;
}

.section-header-with-actions {
  display: flex !important;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.edit-actions {
  display: flex !important;
  gap: 8px;
}

.btn-edit-small, .btn-edit-tiny, .btn-reset-tiny, .btn-save-tiny, .btn-cancel-tiny {
  background: none;
  border: 1px solid #ddd;
  border-radius: 3px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.3s ease;
}

.btn-edit-small {
  padding: 6px 12px;
}

.btn-edit-tiny, .btn-reset-tiny, .btn-save-tiny, .btn-cancel-tiny {
  padding: 2px 6px;
  font-size: 10px;
}

.btn-edit-small:hover, .btn-edit-tiny:hover {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.btn-reset-tiny:hover {
  background: #ff9800;
  color: white;
  border-color: #ff9800;
}

.btn-save-tiny:hover {
  background: #4CAF50;
  color: white;
  border-color: #4CAF50;
}

.btn-cancel-tiny:hover {
  background: #f44336;
  color: white;
  border-color: #f44336;
}

.summary-edit, .percentage-edit {
  display: flex;
  align-items: center;
  gap: 8px;
}

.edit-input, .percentage-input {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 12px;
  width: 100px;
}

.percentage-input {
  width: 70px;
}

.percentage-suffix {
  font-size: 12px;
  color: #666;
}

.editable-cell {
  position: relative;
}

.percentage-display {
  display: flex;
  align-items: center;
  gap: 4px;
}

.percentage-total-validation {
  margin-top: 10px;
  padding: 8px;
  border-radius: 4px;
  background: #f8f9fa;
  text-align: center;
}

.percentage-total-validation.invalid-total {
  background: #ffeaa7;
  border: 1px solid #fdcb6e;
}

.validation-warning {
  color: #e17055;
  font-size: 11px;
  margin-left: 8px;
}

.pattern-actions {
  display: flex;
  gap: 4px;
  justify-content: center;
}
</style>